import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from 'react';
import './styles.css';
import { requestApi } from './utils/api';
import YunxiangPanel from './YunxiangPanel';
import SharePage from './SharePage';
import { LegalDocumentView } from './LegalDocumentView';

// ==================== 粒子背景组件 ====================
function ParticleBackground() {
  useEffect(() => {
    const container = document.querySelector('.particles-container');
    if (!container) return;
    
    // 创建粒子
    for (let i = 0; i < 30; i++) {
      const particle = document.createElement('div');
      particle.className = 'particle';
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.top = `${Math.random() * 100}%`;
      particle.style.animationDelay = `${Math.random() * 8}s`;
      particle.style.animationDuration = `${6 + Math.random() * 4}s`;
      container.appendChild(particle);
    }
    
    return () => {
      container.innerHTML = '';
    };
  }, []);
  
  return <div className="particles-container" />;
}

function safeLocalStorageGet(key, fallback = '') {
  try {
    if (typeof localStorage === 'undefined') return fallback;
    const v = localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch (_) {
    return fallback;
  }
}

function safeLocalStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (_) {
    /* iOS Safari 无痕 / 跨站跟踪限制等会导致写入失败 */
  }
}

function safeLocalStorageRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch (_) {
    /* ignore */
  }
}

function getOrCreateGuestUserId() {
  try {
    if (typeof localStorage === 'undefined') {
      return 'guest_mem_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
    }
    const storedUserId = localStorage.getItem('neo_user_id');
    if (storedUserId) return storedUserId;
    const newUserId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('neo_user_id', newUserId);
    return newUserId;
  } catch (_) {
    return 'guest_mem_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
  }
}

function getInitialTab() {
  const p = window.location.pathname || '';
  if (p.startsWith('/share/')) return 'share';
  if (p === '/admin-login') return 'adminPortal';
  if (p === '/privacy') return 'privacy';
  if (p === '/terms') return 'terms';
  return 'home';
}

function formatAiChatContent(text = '') {
  if (!text) return [];

  // 将"... 1.xxx 2.xxx"这类连续编号内容拆成独立行，提升可读性
  const normalized = text
    .replace(/\r\n/g, '\n')
    .replace(/([^\n])\s*([1-9]\d*[、.．])/g, '$1\n$2')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return normalized
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
}

/** 在句读标点后切分（不用 (?<=) lookbehind，兼容 iOS Safari 16.4 以下） */
function splitAfterSentencePunct(line) {
  const out = [];
  let buf = '';
  for (let i = 0; i < line.length; i++) {
    buf += line[i];
    if (/[。！？!?；;]/.test(line[i])) {
      let j = i + 1;
      while (j < line.length && /\s/.test(line[j])) j++;
      out.push(buf.trim());
      buf = '';
      i = j - 1;
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

function formatSpiritMessageContent(text = '') {
  if (!text) return [];

  const normalized = text
    .replace(/\r\n/g, '\n')
    // 避免把 ?! / !? 拆成单独一行标点
    .replace(/([?？][!！]+|[!！][?？]+)/g, '？！')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const rawLines = normalized
    .split('\n')
    .flatMap((line) => {
      const trimmed = line.trim();
      if (!trimmed) return [];
      if (/^【.*】/.test(trimmed)) return [trimmed];
      return splitAfterSentencePunct(trimmed)
        .map((item) => item.trim())
        .filter((item) => item && /[\u4e00-\u9fa5A-Za-z0-9]/.test(item));
    });

  const mergedLines = [];
  rawLines.forEach((line) => {
    if (!line) return;

    // 1) 纯标点行直接忽略
    if (/^[\s)\]】」』!！?？,，.。;；:：、]+$/.test(line)) return;

    if (!mergedLines.length) {
      mergedLines.push(line);
      return;
    }

    const prev = mergedLines[mergedLines.length - 1];
    const prevOpenParen = (prev.match(/[（(]/g) || []).length;
    const prevCloseParen = (prev.match(/[）)]/g) || []).length;
    const startsWithClosing = /^[)）】」』]/.test(line);

    // 2) 上一行括号未闭合，或本行以闭合符号开头 -> 拼接到上一行
    if (prevOpenParen > prevCloseParen || startsWithClosing) {
      mergedLines[mergedLines.length - 1] = `${prev}${line}`;
      return;
    }

    mergedLines.push(line);
  });

  // 3) 合并断行碎片：模型常在句中插入 \n，或句读切分后留下「了？」「）。」等单独一行，并非 CSS 限宽
  const compacted = [];
  mergedLines.forEach((line) => {
    if (/^【.*】$/.test(line)) {
      compacted.push(line);
      return;
    }
    if (!compacted.length) {
      compacted.push(line);
      return;
    }
    const prev = compacted[compacted.length - 1];
    const prevEndsSentence = /[。！？…]["」'）\]\s]*$/.test(prev);
    const isShortTail =
      !/^【/.test(line) &&
      (line.length <= 22 ||
        (/^（/.test(line) && line.length <= 48)) &&
      (!prevEndsSentence ||
        /^[）】」』？！…，、]/.test(line) ||
        /^（/.test(line));
    const looksLikeHangover =
      /^[了着呢吧啊呀嘛噢嗯哼呐哇哦诶嘿呗哈…）】」』，、]/.test(line) ||
      /^[？！]+$/.test(line);
    if (isShortTail && looksLikeHangover) {
      compacted[compacted.length - 1] = `${prev}${line}`;
    } else {
      compacted.push(line);
    }
  });

  return compacted;
}

function normalizeRichText(text = '') {
  return String(text || '').replace(/\r\n/g, '\n').trim();
}

function parseRichTextToBlocks(text = '') {
  const t = normalizeRichText(text);
  if (!t) return [];

  const lines = t.split('\n').map((l) => l.trim());
  const sections = [];
  let current = { title: '', lines: [] };

  const pushCurrent = () => {
    if (current.title || current.lines.some(Boolean)) sections.push(current);
    current = { title: '', lines: [] };
  };

  for (const line of lines) {
    if (!line) {
      current.lines.push('');
      continue;
    }

    const mTitle = line.match(/^【\s*(.+?)\s*】$/);
    if (mTitle) {
      pushCurrent();
      current.title = mTitle[1];
      continue;
    }

    current.lines.push(line);
  }
  pushCurrent();

  // Convert lines into blocks: paragraphs or lists
  return sections.map((s) => {
    const blocks = [];
    let listBuf = [];

    const flushList = () => {
      if (listBuf.length) {
        blocks.push({ type: 'list', items: listBuf });
        listBuf = [];
      }
    };

    for (const line of s.lines) {
      if (!line) {
        flushList();
        blocks.push({ type: 'spacer' });
        continue;
      }

      const mNum = line.match(/^(\d+)\s*[.、．)\]]\s*(.+)$/);
      const mDash = line.match(/^[-•·]\s+(.+)$/);
      if (mNum) {
        listBuf.push(mNum[2]);
        continue;
      }
      if (mDash) {
        listBuf.push(mDash[1]);
        continue;
      }

      flushList();
      blocks.push({ type: 'p', text: line });
    }
    flushList();

    // remove leading/trailing spacers
    while (blocks.length && blocks[0].type === 'spacer') blocks.shift();
    while (blocks.length && blocks[blocks.length - 1].type === 'spacer') blocks.pop();

    return { title: s.title, blocks };
  });
}

function ReadableRichText({ text, maxHeight = 360, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showToggle, setShowToggle] = useState(false);
  const contentRef = useRef(null);

  const sections = useMemo(() => parseRichTextToBlocks(text), [text]);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    // show toggle only when content overflows
    const should = el.scrollHeight > maxHeight + 8;
    setShowToggle(should);
    if (!should) setExpanded(true);
    else setExpanded(defaultExpanded);
  }, [text, maxHeight, defaultExpanded]);

  if (!normalizeRichText(text)) return null;

  return (
    <div className="rt">
      <div
        ref={contentRef}
        className={`rt-content${expanded ? ' rt-content--expanded' : ' rt-content--collapsed'}`}
        style={{ '--rt-max': `${maxHeight}px` }}
      >
        {sections.map((sec, si) => (
          <div key={si} className="rt-section">
            {sec.title ? <div className="rt-title">{sec.title}</div> : null}
            <div className="rt-body">
              {sec.blocks.map((b, bi) => {
                if (b.type === 'spacer') return <div key={bi} className="rt-spacer" />;
                if (b.type === 'list') {
                  return (
                    <ul key={bi} className="rt-list">
                      {b.items.map((it, ii) => (
                        <li key={ii}>{it}</li>
                      ))}
                    </ul>
                  );
                }
                return <p key={bi}>{b.text}</p>;
              })}
            </div>
          </div>
        ))}
      </div>

      {showToggle ? (
        <button
          type="button"
          className="rt-toggle"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? '收起内容' : '展开阅读全文'}
        </button>
      ) : null}
    </div>
  );
}

function buildBaziDateInputValue(form = {}) {
  const year = Number(form.year);
  const month = Number(form.month);
  const day = Number(form.day);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return '';
  if (month < 1 || month > 12) return '';
  const maxDay = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDay) return '';
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** 良辰吉日：出生日期不可晚于今日，自动收紧月/日 */
function clampDateFormBirth(prev) {
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth() + 1;
  const cd = now.getDate();
  const { birthYear, birthMonth, birthDay } = prev;
  const maxM = birthYear < cy ? 12 : birthYear === cy ? cm : 12;
  const newM = Math.min(Math.max(1, birthMonth), maxM);
  const dim = new Date(birthYear, newM, 0).getDate();
  let maxD = dim;
  if (birthYear === cy && newM === cm) maxD = Math.min(cd, dim);
  const newD = Math.min(Math.max(1, birthDay), maxD);
  if (newM === birthMonth && newD === birthDay) return prev;
  return { ...prev, birthMonth: newM, birthDay: newD };
}

function isBirthDateAfterToday(y, m, d) {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const b = new Date(y, m - 1, d);
  b.setHours(0, 0, 0, 0);
  return b > t;
}

/** 八字：公历出生须不晚于当前时刻；当日则时辰不超过当前钟点 */
function clampBaziForm(prev) {
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth() + 1;
  const cd = now.getDate();
  const ch = now.getHours();
  let { year, month, day, hour } = prev;
  let newY = Math.min(Math.max(1900, year), cy);
  const maxM = newY < cy ? 12 : newY === cy ? cm : 12;
  let newM = Math.min(Math.max(1, month), maxM);
  const dim = new Date(newY, newM, 0).getDate();
  let maxD = dim;
  if (newY === cy && newM === cm) maxD = Math.min(cd, dim);
  let newD = Math.min(Math.max(1, day), maxD);
  const maxH = newY === cy && newM === cm && newD === cd ? ch : 23;
  let newH = Math.min(Math.max(0, hour), maxH);
  if (newY === year && newM === month && newD === day && newH === hour) return prev;
  return { ...prev, year: newY, month: newM, day: newD, hour: newH };
}

function isBaziDateTimeAfterNow(y, m, d, h) {
  const b = new Date(y, m - 1, d, h, 0, 0, 0);
  return b > new Date();
}

/** 首页功能卡片角标：对应 /api/user 的 freeCalls 字段 */
const HOME_TAB_FREE_KEY = {
  bazi: 'bazi',
  face: 'face',
  fengshui: 'fengshui',
  daily: 'daily',
  mbti: 'mbti',
  aura: 'aura',
  dream: 'dream',
  matchmaking: 'matchmaking',
  name: 'name',
  date: 'date',
  spirit: 'spirit',
  chat: 'chat',
  talisman: 'talisman',
  yunxiang: 'yunxiang'
};

function getHomeFeatureQuota(tabId, userInfo) {
  if (!userInfo) return { text: '…', vip: false };
  if (userInfo.isVip) return { text: '会员畅用', vip: true };
  const key = HOME_TAB_FREE_KEY[tabId];
  if (!key) return { text: '—', vip: false };
  const n = userInfo.freeCalls?.[key];
  if (n === undefined || n === null) return { text: '试用', vip: false };
  if (tabId === 'mbti' && n >= 50) return { text: '免费用', vip: false };
  return { text: n > 0 ? `剩${n}次` : '次数已用完', vip: false };
}

function HomeFeatureQuotaBadge({ tabId, userInfo }) {
  const q = getHomeFeatureQuota(tabId, userInfo);
  return (
    <span className={`home-feature-quota${q.vip ? ' home-feature-quota--vip' : ''}`}>{q.text}</span>
  );
}

// ==================== 主应用组件 ====================
function App() {
  const BRAND_NAME = '云端问道';
  const BRAND_TITLE = '云端问道 | AI心灵导师';
  // 状态管理
  const [activeTab, setActiveTab] = useState(getInitialTab);
  /** 各功能独立 loading，避免付费墙/支付阶段仍盖「推演分析」全屏层 */
  const [faceLoading, setFaceLoading] = useState(false);
  const [baziLoading, setBaziLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [fengShuiLoading, setFengShuiLoading] = useState(false);
  const [auraLoading, setAuraLoading] = useState(false);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dreamLoading, setDreamLoading] = useState(false);
  const [matchmakingLoading, setMatchmakingLoading] = useState(false);
  const [nameLoading, setNameLoading] = useState(false);
  const [dateLoading, setDateLoading] = useState(false);
  const [mbtiLoading, setMbtiLoading] = useState(false);
  const [talismanGenLoading, setTalismanGenLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallInfo, setPaywallInfo] = useState({});
  const [userId, setUserId] = useState(getOrCreateGuestUserId);
  const [authToken, setAuthToken] = useState(() => safeLocalStorageGet('neo_auth_token'));
  const [authPhone, setAuthPhone] = useState(() => safeLocalStorageGet('neo_auth_phone'));
  const [authMethod, setAuthMethod] = useState('password');
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ phone: '', password: '', smsCode: '' });
  const [authLoading, setAuthLoading] = useState(false);
  const [smsInfo, setSmsInfo] = useState(null); // 存储验证码信息
  const [myPageLoginExpanded, setMyPageLoginExpanded] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => safeLocalStorageGet('neo_sidebar_collapsed') === 'true'
  );
  const [vipWechatPay, setVipWechatPay] = useState(null);
  const logoTextRef = useRef(null);

  useEffect(() => {
    // 调试：定位是谁在改标题（仅开发环境）
    const DEBUG_TITLE = String(process.env.REACT_APP_DEBUG_TITLE || '') === '1';
    let origDefine = null;
    let origSetAttr = null;
    let origAppend = null;

    const safeLog = (...args) => {
      if (!DEBUG_TITLE) return;
      try { console.log('[title-debug]', ...args); } catch (_) { /* ignore */ }
    };

    if (DEBUG_TITLE) {
      safeLog('init', { href: window.location.href, title: document.title });

      try {
        // Hook document.title setter via defineProperty proxy
        const desc = Object.getOwnPropertyDescriptor(Document.prototype, 'title');
        if (desc && typeof desc.set === 'function' && typeof desc.get === 'function') {
          origDefine = desc;
          Object.defineProperty(document, 'title', {
            configurable: true,
            enumerable: true,
            get() {
              return origDefine.get.call(document);
            },
            set(v) {
              safeLog('set document.title', { v, stack: new Error().stack });
              return origDefine.set.call(document, v);
            },
          });
        }
      } catch (e) {
        safeLog('hook title failed', String(e?.message || e));
      }

      try {
        const titleEl = document.querySelector('title');
        if (titleEl) {
          origSetAttr = titleEl.setAttribute?.bind(titleEl);
          if (origSetAttr) {
            titleEl.setAttribute = (name, value) => {
              if (String(name).toLowerCase() === 'text' || String(name).toLowerCase() === 'innertext') {
                safeLog('title setAttribute', { name, value, stack: new Error().stack });
              }
              return origSetAttr(name, value);
            };
          }
          origAppend = titleEl.appendChild?.bind(titleEl);
          if (origAppend) {
            titleEl.appendChild = (node) => {
              safeLog('title appendChild', { nodeType: node?.nodeType, text: node?.textContent, stack: new Error().stack });
              return origAppend(node);
            };
          }
        }
      } catch (e) {
        safeLog('hook titleEl failed', String(e?.message || e));
      }
    }

    const enforceTitle = () => {
      try {
        if (document.title !== BRAND_TITLE) document.title = BRAND_TITLE;
      } catch (_) { /* ignore */ }
    };

    enforceTitle();

    const timer = setInterval(enforceTitle, 800);
    const onVis = () => enforceTitle();
    document.addEventListener('visibilitychange', onVis);

    let observer = null;
    try {
      if (typeof MutationObserver !== 'undefined') {
        const titleEl = document.querySelector('title');
        if (titleEl) {
          observer = new MutationObserver(enforceTitle);
          observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
        }
      }
    } catch (_) { /* ignore */ }

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
      if (observer) observer.disconnect();
      // restore hooks (best-effort)
      if (DEBUG_TITLE && origDefine) {
        try { Object.defineProperty(document, 'title', origDefine); } catch (_) { /* ignore */ }
      }
    };
  }, [BRAND_NAME, BRAND_TITLE]);

  useEffect(() => {
    const enforceLogoText = () => {
      const el = logoTextRef.current;
      if (!el) return;
      if (el.textContent !== BRAND_NAME) {
        el.textContent = BRAND_NAME;
      }
    };

    enforceLogoText();
    const timer = setInterval(enforceLogoText, 800);
    let observer = null;
    if (typeof MutationObserver !== 'undefined' && logoTextRef.current) {
      observer = new MutationObserver(enforceLogoText);
      observer.observe(logoTextRef.current, { childList: true, characterData: true, subtree: true });
    }

    return () => {
      clearInterval(timer);
      if (observer) observer.disconnect();
    };
  }, [BRAND_NAME]);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('neo_sidebar_collapsed', next ? 'true' : 'false');
      } catch (_) { /* ignore */ }
      return next;
    });
  };
  const [adminPortalCode, setAdminPortalCode] = useState('');
  const [adminPortalData, setAdminPortalData] = useState(null);
  const [adminPortalPayStats, setAdminPortalPayStats] = useState(null);
  const [adminPortalError, setAdminPortalError] = useState('');
  const [feedbackForm, setFeedbackForm] = useState({ type: 'suggestion', content: '', contact: '' });
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showVipModal, setShowVipModal] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const currentYear = new Date().getFullYear();
  
  // 八字表单
  const [baziForm, setBaziForm] = useState({ 
    year: 1995, 
    month: 3, 
    day: 15, 
    hour: 14, 
    gender: 'male',
    name: '' 
  });
  
  // 聊天
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);
  
  // 赛博灵符
  const [talismanForm, setTalismanForm] = useState({
    name: '',
    year: 1995,
    month: 1,
    day: 1,
    hour: 12,
    wish: '',
    type: 'wealth',
    style: 'cyber',
    tier: 'hd'
  });
  const [talismanPreview, setTalismanPreview] = useState(null);
  const [talismanOrder, setTalismanOrder] = useState(null);
  const [talismanPayMethod, setTalismanPayMethod] = useState('wechat');
  const [talismanWechatPay, setTalismanWechatPay] = useState(null);
  const [talismanTierPickerOpen, setTalismanTierPickerOpen] = useState(false);
  const [talismanPayLoading, setTalismanPayLoading] = useState(false);
  const [membershipPayLoading, setMembershipPayLoading] = useState(false);
  const drawTalismanRef = useRef(null);
  const talismanPreviewRef = useRef(null);
  const talismanPaySectionRef = useRef(null);
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [shareQrCode, setShareQrCode] = useState('');
  
  // 灵兽传音
  const [spiritMessages, setSpiritMessages] = useState([]);
  const [spiritInput, setSpiritInput] = useState('');
  const [spiritLoading, setSpiritLoading] = useState(false);
  const [spiritVoiceEnabled, setSpiritVoiceEnabled] = useState(true);
  const [spiritVoiceType, setSpiritVoiceType] = useState(''); // 由后端返回的音色列表决定默认值
  const [spiritMood, setSpiritMood] = useState('idle'); // idle, happy, angry, shy
  const [spiritTyping, setSpiritTyping] = useState(false);
  const [spiritImage, setSpiritImage] = useState(null); // 上传的图片
  const [spiritImagePreview, setSpiritImagePreview] = useState(null); // 图片预览
  const spiritAudioRef = useRef(null);
  const spiritEndRef = useRef(null);
  const spiritImageInputRef = useRef(null);
  const spiritAudioPrimedRef = useRef(false);
  
  // 风水
  const [fengShuiForm, setFengShuiForm] = useState({
    roomType: '客厅',
    area: '15-20平米',
    direction: '坐北朝南（最佳）',
    purpose: '居住',
    doorDirection: '正南',
    windowDirection: '正南',
    windowCount: '2个',
    floor: '5',
    totalFloors: '33',
    buildingType: '公寓住宅',
    nearbyEnvironment: '无特殊',
    familyMembers: '3人',
    mainDirection: '坐北朝南（最佳）',
    layoutDoorRush: false,
    layoutThroughDraft: false,
    layoutBeamOverSeat: false,
    layoutMirrorToBed: false,
    layoutCluttered: false,
    syncBaziWithBaziTab: false
  });
  
  // 新增功能表单
  const [dreamForm, setDreamForm] = useState({
    content: '',
    emotion: '平静',
    gender: 'male',
    age: '30',
    dreamTime: '前半夜',
    symbolType: '不限'
  });

  // 缘分合盘：双人匹配（非真实牵线，文化体验与自我觉察参考）
  const [matchmakingForm, setMatchmakingForm] = useState({
    goal: '恋爱与相处',
    a: { name: '', gender: 'male', year: 1995, month: 3, day: 15, hour: 14 },
    b: { name: '', gender: 'female', year: 1990, month: 7, day: 2, hour: 10 },
    aMbtiType: '',
    bMbtiType: ''
  });

  // 面相问卷表单
  const [faceForm, setFaceForm] = useState({
    gender: 'male',
    age: '30',
    forehead: '标准', // 高宽/标准/低窄/皱纹
    eyes: '有神', // 有神/温柔/锐利/下垂
    eyebrows: '浓密', // 新增：眉毛
    nose: '端正', // 端正/高挺/圆润/扁平
    mouth: '适中', // 适中/丰厚/薄型/不对称
    cheeks: '饱满', // 新增：脸颊
    jaw: '圆润', // 圆润/方正/尖削/双下巴
    ears: '适中', // 新增：耳朵
    skin: '光泽', // 光泽/暗沉/粗糙
    overall: '和谐',
    sanTingBalance: 'balanced', // upper | middle | lower | balanced
    syncBaziFromBaziTab: false, // 提交时附带八字页生辰
    mode: 'quick' // quick=快速6题, professional=专业12题
  });
  const [faceStep, setFaceStep] = useState(0); // 0=介绍/选择模式, quick=快速6题, 1-12=专业问卷, 13=结果



  // 气场问卷表单 - 专业版10题
  const [auraForm, setAuraForm] = useState({
    gender: 'male',
    age: '30',
    mood: '平静',
    health: '良好',
    career: '稳定',
    relationships: '一般',
    recentEvents: '无特殊',
    favoriteColor: '蓝色',
    sleepQuality: '良好',
    mode: 'quick' // quick=快速5题, professional=专业10题
  });
  const [auraStep, setAuraStep] = useState(0); // 0=介绍, 1-8=问卷, 9=结果
  const [nameForm, setNameForm] = useState({ 
    surname: '', 
    gender: 'male', 
    birthYear: 2026, 
    birthMonth: 1,
    birthDay: 1,
    birthHour: 12,
    style: 'traditional',
    nameLength: '2', // 2字名 or 3字名 or 4字名
    fixedType: 'none', // none, first, middle, last
    fixedChar: '', // 定字
    useBaifen: false, // 是否使用辈分字
    baifenChar: '', // 辈分字
    maSurname: '', // 妈妈姓氏（四字名用）
  });
  const [dateForm, setDateForm] = useState({
    event: '结婚嫁娶',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    days: 30,
    birthYear: 1995,
    birthMonth: 1,
    birthDay: 1,
    gender: 'male',
    preference: 'both' // both=都可以, good=只选好日子, avoid=避开坏日子
  });

  useEffect(() => {
    setDateForm((prev) => clampDateFormBirth(prev));
  }, [dateForm.birthYear, dateForm.birthMonth]);

  const auspiciousBirthLimits = useMemo(() => {
    const nowDt = new Date();
    const cy = nowDt.getFullYear();
    const cm = nowDt.getMonth() + 1;
    const cd = nowDt.getDate();
    const by = dateForm.birthYear;
    const bm = dateForm.birthMonth;
    const maxBirthMonth = by < cy ? 12 : by === cy ? cm : 12;
    const dim = new Date(by, bm, 0).getDate();
    let maxBirthDay = dim;
    if (by === cy && bm === cm) maxBirthDay = Math.min(cd, dim);
    return { maxBirthMonth, maxBirthDay };
  }, [dateForm.birthYear, dateForm.birthMonth]);

  useEffect(() => {
    setBaziForm((prev) => clampBaziForm(prev));
  }, [baziForm.year, baziForm.month, baziForm.day]);

  const baziFormLimits = useMemo(() => {
    const nowDt = new Date();
    const cy = nowDt.getFullYear();
    const cm = nowDt.getMonth() + 1;
    const cd = nowDt.getDate();
    const ch = nowDt.getHours();
    const by = baziForm.year;
    const bm = baziForm.month;
    const bd = baziForm.day;
    const maxMonth = by < cy ? 12 : by === cy ? cm : 12;
    const dim = new Date(by, bm, 0).getDate();
    let maxDay = dim;
    if (by === cy && bm === cm) maxDay = Math.min(cd, dim);
    const maxHour = by === cy && bm === cm && bd === cd ? ch : 23;
    const todayYmd = `${cy}-${String(cm).padStart(2, '0')}-${String(cd).padStart(2, '0')}`;
    return { maxMonth, maxDay, maxHour, todayYmd };
  }, [baziForm.year, baziForm.month, baziForm.day]);

  // MBTI测试状态：mbtiMode choose=选入口 pro=60题 meme=趣味梗型
  const [mbtiMode, setMbtiMode] = useState('choose');
  const [mbtiStep, setMbtiStep] = useState(0); // 0 未用(入口用 mbtiMode)；1-60 题目；61 专业结果
  const [mbtiAnswers, setMbtiAnswers] = useState({});
  const [memeStep, setMemeStep] = useState(0); // 1..MEME_QUESTIONS.length 答题；之后为结果页
  const [memeAnswers, setMemeAnswers] = useState({});
  const [inviteCode] = useState(() => 'NEO' + Math.random().toString(36).substr(2, 6).toUpperCase());
  
  // 历史记录
  const [history, setHistory] = useState([]);

  useEffect(() => { 
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); 
  }, [chatMessages]);

  // 获取用户信息
  useEffect(() => {
    fetchUserInfo();
  }, [userId]);

  useEffect(() => {
    if (authToken) {
      verifyAuthSession(authToken);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'feedback') {
      loadFeedbackHistory();
    }
  }, [activeTab, userId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const path = window.location.pathname || '';
    if (activeTab === 'privacy' && path !== '/privacy') {
      window.history.pushState({ neoTab: 'privacy' }, '', '/privacy');
    } else if (activeTab === 'terms' && path !== '/terms') {
      window.history.pushState({ neoTab: 'terms' }, '', '/terms');
    } else if (!['privacy', 'terms'].includes(activeTab) && (path === '/privacy' || path === '/terms')) {
      window.history.replaceState({ neoTab: activeTab }, '', '/');
    }
    return undefined;
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPop = () => {
      const p = window.location.pathname || '';
      if (p === '/privacy') setActiveTab('privacy');
      else if (p === '/terms') setActiveTab('terms');
      else if (p === '/' || p === '') setActiveTab('home');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // 组件加载时获取公告
  useEffect(() => {
    fetchAnnouncement();
  }, []);

  // 公众号网页授权回跳：换 openid 后清 query，便于 JSAPI 支付
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('wx_oauth_pay') !== '1' || !sp.get('code')) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await requestApi(
          `/wechat/mp-oauth-openid?code=${encodeURIComponent(sp.get('code'))}`
        );
        if (!cancelled && r?.success && r.openid) {
          sessionStorage.setItem('wechatMpOpenId', r.openid);
        } else if (!cancelled && !r?.success) {
          console.warn('[wx oauth]', r?.error);
        }
        const u = new URL(window.location.href);
        u.searchParams.delete('code');
        u.searchParams.delete('state');
        u.searchParams.delete('wx_oauth_pay');
        window.history.replaceState({}, '', u.pathname + u.search + u.hash);
        const pendingTalisman = sessionStorage.getItem('pendingTalismanOrderId');
        const pendingVip = sessionStorage.getItem('pendingVipOrderId');
        if (!cancelled) {
          if (pendingTalisman) {
            alert('微信授权成功。请再次点击所选套餐按钮以继续支付灵符。');
          } else if (pendingVip) {
            alert('微信授权成功。请再次点击「开通」以继续支付。');
          }
        }
      } catch (e) {
        if (!cancelled) console.warn('[wx oauth]', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!vipWechatPay?.orderId) return undefined;
    const oid = vipWechatPay.orderId;
    let cancelled = false;
    const tick = async () => {
      try {
        const st = await requestApi(`/payment/order/${encodeURIComponent(oid)}`);
        if (cancelled) return;
        if (st.success && st.order?.status === 'paid') {
          setVipWechatPay(null);
          setShowPaywall(false);
          setShowVipModal(false);
          sessionStorage.removeItem('pendingVipOrderId');
          await fetchUserInfo();
          alert('会员已开通');
        }
      } catch (_) { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 2500);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [vipWechatPay?.orderId]);

  useEffect(() => {
    talismanPreviewRef.current = talismanPreview;
  }, [talismanPreview]);

  useEffect(() => {
    if (!talismanPreview || talismanPreview.paid) return;
    const t = setTimeout(() => {
      try {
        talismanPaySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_) { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [talismanPreview?.title, talismanPreview?.paid, talismanPreview?.generatedAt]);

  const requestWithUser = (path, options = {}) => {
    const mergedData = options.data !== undefined ? { ...options.data, userId } : options.data;
    return requestApi(path, { ...options, data: mergedData });
  };

  const fetchUserInfo = async () => {
    try {
      const r = await requestApi(`/user/${userId}`);
      setUserInfo(r);
    } catch (e) {
      console.error('获取用户信息失败:', e);
    }
  };

  // 支付宝同步跳转回站后轮询订单，等待异步通知落库
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('vipPayReturn') !== '1') return undefined;
    const oid = sessionStorage.getItem('pendingVipOrderId');
    if (!oid) return undefined;
    let cancelled = false;
    const clearReturnQuery = () => {
      const u = new URL(window.location.href);
      u.searchParams.delete('vipPayReturn');
      window.history.replaceState({}, '', u.pathname + u.search + u.hash);
    };
    const tick = async () => {
      try {
        const st = await requestApi(`/payment/order/${encodeURIComponent(oid)}`);
        if (cancelled) return false;
        if (st.success && st.order && st.order.status === 'paid') {
          sessionStorage.removeItem('pendingVipOrderId');
          clearReturnQuery();
          setShowPaywall(false);
          setShowVipModal(false);
          await fetchUserInfo();
          alert('🎉 会员已开通（若刚付完款请稍等几秒）');
          return true;
        }
      } catch (_) { /* ignore */ }
      return false;
    };
    tick();
    const iv = setInterval(async () => {
      if (await tick()) clearInterval(iv);
    }, 2500);
    const max = setTimeout(() => clearInterval(iv), 120000);
    return () => {
      cancelled = true;
      clearInterval(iv);
      clearTimeout(max);
    };
  }, [userId]);

  const API = async (path, data) => {
    try {
      return await requestWithUser(path, { method: 'POST', data });
    } catch (e) {
      console.error('API异常:', e);
      if (e.body) {
        try {
          const j = JSON.parse(e.body);
          if (j && j.error) return { error: typeof j.error === 'string' ? j.error : JSON.stringify(j.error) };
        } catch (_) {
          /* ignore */
        }
      }
      return { error: '网络错误: ' + e.message };
    }
  };

  const verifyAuthSession = async (token) => {
    try {
      const r = await requestApi('/auth/verify', { token });
      if (r.valid) {
        setUserId(r.userId);
        safeLocalStorageSet('neo_user_id', r.userId);
        return true;
      }
    } catch (e) {
      console.error('验证登录状态失败:', e);
    }
    setAuthToken('');
    setAuthPhone('');
    safeLocalStorageRemove('neo_auth_token');
    safeLocalStorageRemove('neo_auth_phone');
    return false;
  };

  const finishLogin = (loginResult, fallbackPhone) => {
    setMyPageLoginExpanded(false);
    setAuthToken(loginResult.token);
    setAuthPhone(loginResult.phone || fallbackPhone);
    setUserId(loginResult.userId);
    safeLocalStorageSet('neo_auth_token', loginResult.token);
    safeLocalStorageSet('neo_auth_phone', loginResult.phone || fallbackPhone);
    safeLocalStorageSet('neo_user_id', loginResult.userId);
    setAuthForm({ phone: '', password: '', smsCode: '' });
    fetchUserInfo();
  };

  const sendSmsCode = async () => {
    if (!/^1[3-9]\d{9}$/.test(authForm.phone)) {
      alert('请输入正确手机号');
      return;
    }
    setAuthLoading(true);
    try {
      const r = await requestApi('/auth/sms/send', { method: 'POST', data: { phone: authForm.phone } });
      if (!r.success) {
        alert(r.error || '发送失败');
        return;
      }
      // 保存验证码信息（开发环境）
      if (r.devCode) {
        setSmsInfo({
          phone: authForm.phone,
          code: r.devCode,
          sentAt: new Date(),
          expiresAt: new Date(Date.now() + 5 * 60 * 1000) // 5分钟后
        });
        alert(`✅ 验证码已发送：${r.devCode}\n\n📱 手机号：${authForm.phone}\n🔢 验证码：${r.devCode}\n⏰ 有效期：5分钟`);
      } else {
        setSmsInfo(null);
        alert('✅ 验证码已发送，请查收短信');
      }
    } catch (e) {
      console.error('发送验证码失败:', e);
      alert('发送失败，请稍后重试');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthSubmit = async () => {
    if (!/^1[3-9]\d{9}$/.test(authForm.phone)) {
      alert('请输入正确的手机号');
      return;
    }
    setAuthLoading(true);
    try {
      if (authMethod === 'sms') {
        if (!/^\d{6}$/.test(authForm.smsCode || '')) {
          alert('请输入6位验证码');
          return;
        }
        const loginResult = await requestApi('/auth/sms/login', {
          method: 'POST',
          data: { phone: authForm.phone, code: authForm.smsCode, userId }
        });
        if (!loginResult.success) {
          alert(loginResult.error || '登录失败');
          return;
        }
        finishLogin(loginResult, authForm.phone);
        setSmsInfo(null); // 清除验证码信息
        alert('验证码登录成功');
        return;
      }

      if ((authForm.password || '').length < 6) {
        alert('密码至少6位');
        return;
      }
      if (authMode === 'register') {
        const registerResult = await requestApi('/auth/register', {
          method: 'POST',
          data: { ...authForm, userId }
        });
        if (!registerResult.success) {
          alert(registerResult.error || '注册失败');
          return;
        }
      }
      const loginResult = await requestApi('/auth/login', { method: 'POST', data: authForm });
      if (!loginResult.success) {
        alert(loginResult.error || '登录失败');
        return;
      }
      finishLogin(loginResult, authForm.phone);
      alert(authMode === 'register' ? '注册并登录成功' : '登录成功');
    } catch (e) {
      console.error('登录流程失败:', e);
      alert('请求失败，请稍后重试');
    } finally {
      setAuthLoading(false);
    }
  };

  const loginAdminPortal = async () => {
    if (!adminPortalCode) {
      setAdminPortalError('请输入管理员密码');
      return;
    }
    setAdminPortalError('');
    try {
      const [statsResult, payStatsResult] = await Promise.all([
        requestApi('/admin', { method: 'POST', data: { code: adminPortalCode, action: 'stats' } }),
        requestApi('/payment/stats', { method: 'POST', data: { code: adminPortalCode } })
      ]);
      if (statsResult.error) {
        setAdminPortalError(statsResult.error);
        return;
      }
      setAdminPortalData(statsResult);
      setAdminPortalPayStats(payStatsResult.stats || null);
    } catch (e) {
      console.error('后台登录失败:', e);
      setAdminPortalError('请求失败，请稍后重试');
    }
  };

  const handleLogout = async () => {
    try {
      if (authToken) {
        await requestApi('/auth/logout', { method: 'POST', data: {}, token: authToken });
      }
    } catch (e) {
      console.error('退出登录失败:', e);
    }

    // 退出后切换为全新游客身份，避免沿用已登录账号的数据
    safeLocalStorageRemove('neo_user_id');
    const guestUserId = getOrCreateGuestUserId();
    setUserId(guestUserId);
    setAuthToken('');
    setAuthPhone('');
    setActiveTab('home');
    setResult(null);
    setError(null);
    safeLocalStorageRemove('neo_auth_token');
    safeLocalStorageRemove('neo_auth_phone');
    alert('已退出登录');
  };

  const loadFeedbackHistory = async () => {
    setFeedbackLoading(true);
    try {
      const r = await requestApi(`/feedback/history/${userId}`);
      if (r.success) setFeedbackHistory(r.feedbacks || []);
    } catch (e) {
      console.error('加载反馈历史失败:', e);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const submitFeedback = async () => {
    if (!feedbackForm.content.trim()) {
      alert('请输入反馈内容');
      return;
    }
    setFeedbackLoading(true);
    try {
      const r = await requestApi('/feedback/submit', { method: 'POST', data: { ...feedbackForm, userId } });
      if (r.success) {
        alert('反馈已提交，感谢你的建议');
        setFeedbackForm({ type: 'suggestion', content: '', contact: '' });
        await loadFeedbackHistory();
      } else {
        alert(r.error || '提交失败');
      }
    } catch (e) {
      console.error('提交反馈失败:', e);
      alert('网络错误，请稍后重试');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const clearAllAnalysisLoading = () => {
    setFaceLoading(false);
    setBaziLoading(false);
    setChatLoading(false);
    setFengShuiLoading(false);
    setAuraLoading(false);
    setDailyLoading(false);
    setDreamLoading(false);
    setMatchmakingLoading(false);
    setNameLoading(false);
    setDateLoading(false);
    setMbtiLoading(false);
    setTalismanGenLoading(false);
  };

  const analysisOverlayActive =
    faceLoading ||
    baziLoading ||
    chatLoading ||
    fengShuiLoading ||
    auraLoading ||
    dailyLoading ||
    dreamLoading ||
    matchmakingLoading ||
    nameLoading ||
    dateLoading ||
    mbtiLoading ||
    talismanGenLoading;

  const pickAnalysisOverlayText = () => {
    if (faceLoading) return '面相探微分析中…';
    if (baziLoading) return '八字批解分析中…';
    if (chatLoading) return '暖愈心愈回复中…';
    if (fengShuiLoading) return '风水分析中…';
    if (auraLoading) return '气场扫描中…';
    if (dailyLoading) return '每日运势生成中…';
    if (dreamLoading) return '周公解梦中…';
    if (matchmakingLoading) return '缘分合盘分析中…';
    if (nameLoading) return '宝宝取名方案生成中…';
    if (dateLoading) return '良辰吉日推算中…';
    if (mbtiLoading) return '性格测评结果生成中…';
    if (talismanGenLoading) return '赛博灵符生成中…';
    return '推演分析中...';
  };

  const handleError = (err) => {
    if (err.error === 'limit') {
      clearAllAnalysisLoading();
      setPaywallInfo(err);
      setShowPaywall(true);
    } else setError(err.error || '出错了，请重试');
  };

  // 获取公告
  const fetchAnnouncement = async () => {
    try {
      const data = await requestApi('/announcement');
      if (data.success && data.announcement) {
        setAnnouncement(data.announcement);
      } else {
        setAnnouncement(null);
      }
    } catch (error) {
      console.error('获取公告失败:', error);
      setAnnouncement(null);
    }
  };

  // ==================== 核心功能处理 ====================
  
  const handleBazi = async () => {
    if (isBaziDateTimeAfterNow(baziForm.year, baziForm.month, baziForm.day, baziForm.hour)) {
      setError('出生日期与时辰不能晚于当前时间，请调整后再试。');
      return;
    }
    setBaziLoading(true);
    setError(null);
    setResult(null);
    const r = await API('/analyze', baziForm).catch(() => ({ error: '网络错误' }));
    setBaziLoading(false);
    if (r.error) handleError(r);
    else {
      setResult({ type: 'bazi', ...r });
      addToHistory('八字批解', r);
    }
  };

  const handleChat = async () => {
    if (!chatInput.trim()) return;
    const userMsg = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMsg]);
    const msg = chatInput; 
    setChatInput('');
    setChatLoading(true);
    const r = await API('/chat', { message: msg }).catch(() => ({ error: '网络错误' }));
    setChatLoading(false);
    if (r.error) handleError(r);
    else if (r.type === 'crisis' && Array.isArray(r.hotlines) && r.hotlines.length > 0) {
      const extra = r.hotlines.map((h) => `${h.name}：${h.number}`).join(' · ');
      setChatMessages(prev => [...prev, { role: 'neo', content: `${r.message}\n\n📞 ${extra}` }]);
    } else {
      setChatMessages(prev => [...prev, { role: 'neo', content: r.message }]);
    }
  };

  const handleFengShui = async () => {
    setFengShuiLoading(true); setError(null); setResult(null);
    const payload = {
      ...fengShuiForm,
      ...(fengShuiForm.syncBaziWithBaziTab
        ? {
            birthYear: baziForm.year,
            birthMonth: baziForm.month,
            birthDay: baziForm.day,
            birthHour: baziForm.hour
          }
        : {})
    };
    const r = await API('/fengshui', payload).catch(() => ({ error: '网络错误' }));
    setFengShuiLoading(false);
    if (r.error) handleError(r);
    else {
      setResult({ type: 'fengshui', ...r });
      addToHistory('风水分析', r);
    }
  };

  const handleAura = async () => {
    setAuraLoading(true); setError(null); setResult(null);
    const r = await API('/aura', auraForm).catch(() => ({ error: '网络错误' }));
    setAuraLoading(false);
    if (r.error) handleError(r);
    else {
      setResult({ type: 'aura', ...r });
      addToHistory('气场扫描', r);
      setAuraStep(9); // 结果页（与问卷 step 1–8 区分）
    }
  };

  const handleDaily = async () => {
    setDailyLoading(true); setError(null); setResult(null);
    const r = await API('/daily', {}).catch(() => ({ error: '网络错误' }));
    setDailyLoading(false);
    if (r.error) handleError(r); 
    else {
      setResult({ type: 'daily', ...r });
      addToHistory('每日运势', r);
    }
  };

  // 新增功能
  const handleDream = async () => {
    if (!dreamForm.content.trim()) {
      setError('请输入梦境内容');
      return;
    }
    setDreamLoading(true); setError(null); setResult(null);
    const r = await API('/dream', dreamForm).catch(() => ({ error: '网络错误' }));
    setDreamLoading(false);
    if (r.error) handleError(r);
    else {
      setResult({ type: 'dream', ...r });
      addToHistory('周公解梦', r);
      setDreamForm({ content: '', emotion: '平静' });
    }
  };

  // 缘分合盘：双人匹配分析（非真实撮合）
  const handleMatchmaking = async () => {
    const a = matchmakingForm?.a || {};
    const b = matchmakingForm?.b || {};

    const requiredFields = ['year', 'month', 'day', 'hour'];
    const missingA = requiredFields.some((k) => !Number.isInteger(Number(a?.[k])));
    const missingB = requiredFields.some((k) => !Number.isInteger(Number(b?.[k])));

    if (missingA || missingB) {
      setError('请提供双方完整的生辰信息：年/月/日/时');
      return;
    }

    // 生日时辰不能晚于当前时间（与后端校验一致）
    if (
      isBaziDateTimeAfterNow(Number(a.year), Number(a.month), Number(a.day), Number(a.hour)) ||
      isBaziDateTimeAfterNow(Number(b.year), Number(b.month), Number(b.day), Number(b.hour))
    ) {
      setError('出生日期与时辰不能晚于当前时间，请调整后再试。');
      return;
    }

    setMatchmakingLoading(true);
    setError(null);
    setResult(null);

    const payload = {
      goal: matchmakingForm?.goal || '恋爱与相处',
      a: {
        name: a.name || '',
        gender: a.gender || '',
        year: Number(a.year),
        month: Number(a.month),
        day: Number(a.day),
        hour: Number(a.hour)
      },
      b: {
        name: b.name || '',
        gender: b.gender || '',
        year: Number(b.year),
        month: Number(b.month),
        day: Number(b.day),
        hour: Number(b.hour)
      },
      aMbtiType: matchmakingForm?.aMbtiType || '',
      bMbtiType: matchmakingForm?.bMbtiType || ''
    };

    const r = await API('/matchmaking', payload).catch(() => ({ error: '网络错误' }));
    setMatchmakingLoading(false);

    if (r.error) handleError(r);
    else {
      setResult({ type: 'matchmaking', ...r });
      addToHistory('缘分合盘', r);
    }
  };

  const handleName = async () => {
    if (!nameForm.surname.trim()) {
      setError('请输入姓氏');
      return;
    }
    setNameLoading(true); setError(null); setResult(null);
    const r = await API('/name', nameForm).catch(() => ({ error: '网络错误' }));
    setNameLoading(false);
    if (r.error) handleError(r);
    else {
      setResult({ type: 'name', ...r });
      addToHistory('宝宝取名', r);
    }
  };

  const handleDate = async () => {
    if (isBirthDateAfterToday(dateForm.birthYear, dateForm.birthMonth, dateForm.birthDay)) {
      setError('出生日期不能晚于今天，请调整后再试。');
      return;
    }
    setDateLoading(true); setError(null); setResult(null);
    const r = await API('/date', dateForm).catch(() => ({ error: '网络错误' }));
    setDateLoading(false);
    if (r.error) handleError(r);
    else {
      setResult({ type: 'date', ...r });
      addToHistory('良辰吉日', r);
    }
  };

  const handleMBTI = async () => {
    setMbtiLoading(true); setError(null);
    const r = await API('/mbti', mbtiForm).catch(() => ({ error: '网络错误' }));
    setMbtiLoading(false);
    if (r.error) handleError(r);
    else {
      setResult({ type: 'mbti', ...r });
      addToHistory('性格测试', r);
    }
  };

  const handleFace = async () => {
    setFaceLoading(true);
    setError(null);
    setResult(null);
    const payload = {
      ...faceForm,
      ...(faceForm.syncBaziFromBaziTab
        ? {
            birthYear: baziForm.year,
            birthMonth: baziForm.month,
            birthDay: baziForm.day,
            birthHour: baziForm.hour
          }
        : {})
    };
    const r = await API('/face', payload).catch(() => ({ error: '网络错误' }));
    setFaceLoading(false);
    if (r.error) handleError(r);
    else {
      setResult({ type: 'face', ...r });
      addToHistory('面相探微', r);
      setFaceStep(13); // 跳转到结果
    }
  };



  // MBTI答题处理
  const handleMBTIAnswer = (questionNum, answer) => {
    const newAnswers = {...mbtiAnswers, [questionNum]: answer};
    setMbtiAnswers(newAnswers);
    if (questionNum < 60) {
      setTimeout(() => setMbtiStep(questionNum + 1), 300);
    } else {
      // 第60题答完后自动提交
      setTimeout(() => handleMBTIFinish(newAnswers), 300);
    }
  };

  // MBTI完成提交
  const handleMBTIFinish = async (answersToUse = null) => {
    setMbtiLoading(true);
    
    const answers = answersToUse || mbtiAnswers;
    
    // 计算各维度得分
    let E = 0, I = 0, S = 0, N = 0, T = 0, F = 0, J = 0, P = 0;
    
    Object.entries(answers).forEach(([qNum, answer]) => {
      const question = MBTI_QUESTIONS[parseInt(qNum) - 1];
      if (question?.type === 'EI') {
        answer === 'A' ? E++ : I++;
      } else if (question?.type === 'SN') {
        answer === 'A' ? S++ : N++;
      } else if (question?.type === 'TF') {
        answer === 'A' ? T++ : F++;
      } else if (question?.type === 'JP') {
        answer === 'A' ? J++ : P++;
      }
    });
    
    // 确定性格类型
    const type1 = E >= I ? 'E' : 'I';
    const type2 = S >= N ? 'S' : 'N';
    const type3 = T >= F ? 'T' : 'F';
    const type4 = J >= P ? 'J' : 'P';
    const mbtiType = type1 + type2 + type3 + type4;
    
    const r = await API('/mbti', { 
      mbtiType, 
      scores: { E, I, S, N, T, F, J, P },
      answers: answers 
    }).catch(() => ({ error: '网络错误' }));
    
    setMbtiLoading(false);
    if (r.error) handleError(r);
    else {
      setResult({ type: 'mbti', ...r });
      setMbtiStep(61);
    }
  };

  // ==================== 赛博灵符功能 ====================
  
  // 生成灵符
  const generateTalisman = async () => {
    if (!talismanForm.name.trim()) {
      setError('请输入您的姓名');
      return;
    }
    
    setTalismanGenLoading(true);
    setError(null);
    
    try {
      console.log('[赛博灵符] 开始生成，表单数据:', talismanForm);
      
      // 调用后端API生成灵符文案
      const r = await API('/talisman/generate', talismanForm).catch((e) => {
        console.error('[赛博灵符] API调用失败:', e);
        return { error: '网络错误' };
      });
      
      console.log('[赛博灵符] API响应:', r);
      
      if (r.error) {
        console.error('[赛博灵符] API返回错误:', r);
        handleError(r);
        setTalismanGenLoading(false);
        return;
      }
      
      // 绘制灵符到Canvas
      console.log('[赛博灵符] API返回原始数据:', JSON.stringify(r, null, 2));
      
      const talismanData = {
        title: r.title,
        blessing: r.blessing,
        bazi: r.bazi,
        type: talismanForm.type,
        typeName: r.typeName,
        style: talismanForm.style,
        generatedAt: r.generatedAt,
        name: talismanForm.name,
        wish: talismanForm.wish,
        watermark: true,
        paid: false
      };
      
      console.log('[赛博灵符] 合并后数据:', JSON.stringify(talismanData, null, 2));
      await drawTalisman(talismanData);
      console.log('[赛博灵符] Canvas绘制完成');
      
      setTalismanPreview(talismanData);
      setTalismanGenLoading(false);
      console.log('[赛博灵符] 生成完成');
      
    } catch (e) {
      console.error('[赛博灵符] 生成灵符失败:', e);
      setError('生成灵符失败，请重试: ' + e.message);
      setTalismanGenLoading(false);
    }
  };
  
  // Canvas绘制灵符（逻辑尺寸 1080×1920；exportScale 用于高清导出倍率）
  const drawTalisman = async (data, options = {}) => {
    const exportScale = options.exportScale || 1;
    const logicalW = 1080;
    const logicalH = 1920;
    console.log('[赛博灵符] drawTalisman 开始执行', 'scale:', exportScale);
    const canvas = options.targetCanvas || previewCanvasRef.current || canvasRef.current;
    console.log('[赛博灵符] Canvas元素:', canvas ? '已获取' : '未获取', 'previewCanvasRef:', previewCanvasRef.current, 'canvasRef:', canvasRef.current);
    
    if (!canvas) {
      console.error('[赛博灵符] 错误: Canvas元素未找到');
      throw new Error('Canvas元素未找到');
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('[赛博灵符] 错误: 无法获取Canvas 2D上下文');
      throw new Error('无法获取Canvas 2D上下文');
    }
    
    canvas.width = logicalW * exportScale;
    canvas.height = logicalH * exportScale;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(exportScale, exportScale);
    
    console.log('[赛博灵符] Canvas逻辑尺寸:', logicalW, 'x', logicalH, '风格:', data.style, '像素:', canvas.width, 'x', canvas.height);
    
    ctx.clearRect(0, 0, logicalW, logicalH);
    
    await drawTalismanBackground(ctx, data.style, logicalW, logicalH);
    drawTalismanDecorations(ctx, data.style, logicalW, logicalH);
    drawTalismanTitle(ctx, data, logicalW);
    drawTalismanSymbol(ctx, data, logicalW, logicalH);
    drawTalismanText(ctx, data, logicalW, logicalH);
    drawTalismanPersonal(ctx, data, logicalW, logicalH);
    drawTalismanSeal(ctx, data.style, logicalW, logicalH);
    if (data.watermark) {
      drawTalismanWatermark(ctx, logicalW, logicalH);
    }
  };

  drawTalismanRef.current = drawTalisman;

  useEffect(() => {
    if (!talismanWechatPay?.orderId) return undefined;
    const oid = talismanWechatPay.orderId;
    let cancelled = false;
    const tick = async () => {
      try {
        const st = await requestApi(`/payment/order/${encodeURIComponent(oid)}`);
        if (cancelled) return;
        if (st.success && st.order?.status === 'paid') {
          setTalismanWechatPay(null);
          setTalismanOrder(null);
          sessionStorage.removeItem('pendingTalismanOrderId');
          const prev = talismanPreviewRef.current;
          if (prev && drawTalismanRef.current) {
            const u = { ...prev, paid: true, watermark: false };
            await drawTalismanRef.current(u);
            setTalismanPreview(u);
          }
          alert('灵符已解锁，可下载无水印壁纸');
        }
      } catch (_) { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 2500);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [talismanWechatPay?.orderId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('talismanPayReturn') !== '1') return undefined;
    const oid = sessionStorage.getItem('pendingTalismanOrderId');
    if (!oid) return undefined;
    let cancelled = false;
    const clearReturnQuery = () => {
      const u = new URL(window.location.href);
      u.searchParams.delete('talismanPayReturn');
      window.history.replaceState({}, '', u.pathname + u.search + u.hash);
    };
    const tick = async () => {
      try {
        const st = await requestApi(`/payment/order/${encodeURIComponent(oid)}`);
        if (cancelled) return false;
        if (st.success && st.order?.status === 'paid') {
          sessionStorage.removeItem('pendingTalismanOrderId');
          clearReturnQuery();
          const prev = talismanPreviewRef.current;
          if (prev && drawTalismanRef.current) {
            const u = { ...prev, paid: true, watermark: false };
            await drawTalismanRef.current(u);
            setTalismanPreview(u);
          }
          setTalismanOrder(null);
          alert('灵符订单已支付，无水印壁纸已解锁');
          return true;
        }
      } catch (_) { /* ignore */ }
      return false;
    };
    tick();
    const iv = setInterval(async () => {
      if (await tick()) clearInterval(iv);
    }, 2500);
    const max = setTimeout(() => clearInterval(iv), 120000);
    return () => {
      cancelled = true;
      clearInterval(iv);
      clearTimeout(max);
    };
  }, [userId]);
  
  // 绘制背景
  const drawTalismanBackground = async (ctx, style, width, height) => {
    const gradients = {
      cyber: ['#0a0a2e', '#1a1a4e', '#2d1b69'],
      national: ['#1a1a2e', '#16213e', '#0f3460'],
      minimal: ['#f5f5f5', '#fafafa', '#ffffff'],
      gold: ['#1a1a1a', '#2d2d2d', '#1a1a1a'],
      ink: ['#e8e8e8', '#f0f0f0', '#e0e0e0']
    };
    
    const colors = gradients[style] || gradients.cyber;
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, colors[0]);
    gradient.addColorStop(0.5, colors[1]);
    gradient.addColorStop(1, colors[2]);
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    // 添加纹理
    if (style === 'gold') {
      // 金箔纹理
      ctx.save();
      for (let i = 0; i < 50; i++) {
        ctx.beginPath();
        ctx.arc(
          Math.random() * width,
          Math.random() * height,
          Math.random() * 3 + 1,
          0, Math.PI * 2
        );
        ctx.fillStyle = `rgba(212, 175, 55, ${Math.random() * 0.3})`;
        ctx.fill();
      }
      ctx.restore();
    } else if (style === 'ink') {
      // 水墨晕染效果（纸张肌理 + 墨痕留白）
      ctx.save();
      for (let i = 0; i < 10; i++) {
        const x = Math.random() * width;
        const y = Math.random() * height;
        const r = Math.random() * 240 + 90;
        const grd = ctx.createRadialGradient(x, y, 0, x, y, r);
        grd.addColorStop(0, 'rgba(80, 80, 80, 0.11)');
        grd.addColorStop(1, 'rgba(100, 100, 100, 0)');
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // 纸张颗粒感
      for (let i = 0; i < 2800; i++) {
        const px = Math.random() * width;
        const py = Math.random() * height;
        const a = Math.random() * 0.035;
        ctx.fillStyle = `rgba(40,40,40,${a})`;
        ctx.fillRect(px, py, 1, 1);
      }

      // 上方留白过渡
      const topFade = ctx.createLinearGradient(0, 0, 0, 420);
      topFade.addColorStop(0, 'rgba(255,255,255,0.5)');
      topFade.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = topFade;
      ctx.fillRect(0, 0, width, 420);

      ctx.restore();
    }
  };
  
  // 绘制装饰元素
  const drawTalismanDecorations = (ctx, style, width, height) => {
    ctx.save();
    
    if (style === 'cyber') {
      // 赛博朋克：增强电路纹理
      ctx.strokeStyle = 'rgba(0, 255, 255, 0.3)';
      ctx.lineWidth = 3;
      
      // 动态电路线
      for (let i = 0; i < 12; i++) {
        ctx.beginPath();
        const startX = Math.random() * width;
        ctx.moveTo(startX, 0);
        for (let y = 0; y < height; y += 80) {
          const x = startX + Math.sin(y * 0.01) * 50;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      
      // 发光节点
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      for (let i = 0; i < 30; i++) {
        const x = Math.random() * width;
        const y = Math.random() * height;
        const r = Math.random() * 4 + 2;
        
        // 光晕效果
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, r * 4);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.3)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, r * 4, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // 六边形网格增强
      ctx.strokeStyle = 'rgba(255, 0, 255, 0.15)';
      ctx.lineWidth = 2;
      for (let y = 0; y < height; y += 60) {
        for (let x = 0; x < width; x += 52) {
          ctx.beginPath();
          for (let j = 0; j < 6; j++) {
            const angle = (Math.PI / 3) * j;
            const px = x + 25 * Math.cos(angle);
            const py = y + 25 * Math.sin(angle);
            if (j === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.stroke();
        }
      }
      
      // 边缘光带
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(20, 0);
      ctx.lineTo(20, height);
      ctx.moveTo(width - 20, 0);
      ctx.lineTo(width - 20, height);
      ctx.stroke();
      
    } else if (style === 'national') {
      // 国潮：增强祥云纹样
      ctx.strokeStyle = 'rgba(212, 175, 55, 0.4)';
      ctx.lineWidth = 4;
      ctx.shadowColor = 'rgba(212, 175, 55, 0.3)';
      ctx.shadowBlur = 15;
      
      // 主祥云
      for (let i = 0; i < 8; i++) {
        const y = 200 + i * 220;
        ctx.beginPath();
        ctx.moveTo(80, y);
        for (let x = 80; x < width - 80; x += 50) {
          const waveHeight = i % 2 === 0 ? -35 : 35;
          ctx.quadraticCurveTo(x + 25, y + waveHeight, x + 50, y);
        }
        ctx.stroke();
      }
      
      // 小祥云点缀
      ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
      ctx.lineWidth = 2;
      ctx.shadowBlur = 8;
      
      for (let i = 0; i < 4; i++) {
        const y = 150 + i * 250;
        ctx.beginPath();
        ctx.moveTo(120, y);
        for (let x = 120; x < width - 120; x += 40) {
          ctx.quadraticCurveTo(x + 20, y - 15, x + 40, y);
        }
        ctx.stroke();
      }
      
      // 莲花纹样
      ctx.strokeStyle = 'rgba(255, 105, 180, 0.2)';
      ctx.lineWidth = 2;
      ctx.shadowBlur = 0;
      
      // 顶部莲花
      const topY = 100;
      for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 6) {
        const x = width / 2 + Math.cos(angle) * 60;
        const y = topY + Math.sin(angle) * 60;
        
        ctx.beginPath();
        ctx.moveTo(width / 2, topY);
        ctx.lineTo(x, y);
        ctx.stroke();
        
        // 花瓣
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    
    ctx.restore();
  };
  
  // 绘制标题
  const drawTalismanTitle = (ctx, data, width) => {
    ctx.save();
    
    const typeNames = {
      wealth: '财运亨通符',
      love: '姻缘美满符',
      career: '事业有成符',
      health: '身心调和符',
      safety: '平安顺遂符'
    };
    
    const colors = {
      cyber: '#00ffff',
      national: '#d4af37',
      minimal: '#1a1a2e',
      gold: '#d4af37',
      ink: '#1a1a2e'
    };
    
    const color = colors[data.style] || '#00ffff';
    
    const mainTitleColors = {
      cyber: '#ffffff',
      national: '#ffffff',
      minimal: '#1a1a2e',
      gold: '#f5deb3',
      ink: '#222222'
    };

    // ===== 标题 =====
    ctx.font = 'bold 80px "Microsoft YaHei", sans-serif';
    ctx.textAlign = 'center';

    // 主文字
    ctx.fillStyle = mainTitleColors[data.style] || '#ffffff';
    ctx.fillText(typeNames[data.type] || '灵符', width / 2, 200);
    
    ctx.font = '36px "Microsoft YaHei", sans-serif';
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.85;
    ctx.fillText('云端问道 · 赛博灵符', width / 2, 260);
    ctx.globalAlpha = 1;
    
    ctx.restore();
  };
  
  // 绘制核心符文
  const drawTalismanSymbol = (ctx, data, width, height) => {
    console.log('[赛博灵符] drawTalismanSymbol 输入数据:', JSON.stringify({type: data.type, style: data.style, name: data.name}));
    ctx.save();
    
    const centerX = width / 2;
    const centerY = height / 2 - 100;
    const radius = Math.min(width, height) * 0.22; // 相对大小，避免边缘被遮
    
    // 根据灵符类型和风格选择颜色 - 修复：添加所有风格的颜色支持
    const colorSchemes = {
      wealth: { 
        cyber: ['#FFD700', '#FFA500'], 
        national: ['#FFD700', '#FF8C00'],
        minimal: ['#FFD700', '#FFA500'],
        gold: ['#FFD700', '#FFA500'],
        ink: ['#D4AF37', '#B8860B']
      },
      love: { 
        cyber: ['#FF69B4', '#FF1493'], 
        national: ['#FF69B4', '#C71585'],
        minimal: ['#FF69B4', '#FF1493'],
        gold: ['#FF69B4', '#FF1493'],
        ink: ['#C71585', '#8B008B']
      },
      career: { 
        cyber: ['#00CED1', '#20B2AA'], 
        national: ['#00CED1', '#008B8B'],
        minimal: ['#00CED1', '#20B2AA'],
        gold: ['#00CED1', '#20B2AA'],
        ink: ['#008B8B', '#006666']
      },
      health: { 
        cyber: ['#98FB98', '#32CD32'], 
        national: ['#98FB98', '#228B22'],
        minimal: ['#98FB98', '#32CD32'],
        gold: ['#98FB98', '#32CD32'],
        ink: ['#228B22', '#006400']
      },
      safety: { 
        cyber: ['#87CEEB', '#4682B4'], 
        national: ['#87CEEB', '#4169E1'],
        minimal: ['#87CEEB', '#4682B4'],
        gold: ['#87CEEB', '#4682B4'],
        ink: ['#4169E1', '#0000CD']
      }
    };
    
    const defaultScheme = { 
      cyber: ['#00ffff', '#ff00ff'], 
      national: ['#d4af37', '#c41e3a'],
      minimal: ['#333333', '#666666'],
      gold: ['#FFD700', '#FFA500'],
      ink: ['#1a1a2e', '#4a4a6a']
    };
    
    const scheme = colorSchemes[data.type] || defaultScheme;
    const styleColors = scheme[data.style] || scheme.cyber || defaultScheme.cyber;
    let color1 = styleColors[0];
    let color2 = styleColors[1];
    
    // 安全检查：确保颜色是有效的十六进制格式
    const isValidColor = (c) => typeof c === 'string' && /^#[0-9A-Fa-f]{6}$/.test(c);
    if (!isValidColor(color1)) {
      console.error('[赛博灵符] 无效的颜色值:', color1, '使用默认值');
      color1 = '#00CED1';
    }
    if (!isValidColor(color2)) {
      console.error('[赛博灵符] 无效的颜色值:', color2, '使用默认值');
      color2 = '#20B2AA';
    }
    
    console.log('[赛博灵符] drawTalismanSymbol - type:', data.type, 'style:', data.style, 'color1:', color1, 'color2:', color2);
    
    // ===== 水墨风：简化为墨韵印记，避免"科技感过强" =====
    if (data.style === 'ink') {
      // 外圈墨晕
      const inkOuter = ctx.createRadialGradient(centerX, centerY, radius * 0.2, centerX, centerY, radius * 1.15);
      inkOuter.addColorStop(0, 'rgba(30,30,30,0)');
      inkOuter.addColorStop(1, 'rgba(30,30,30,0.18)');
      ctx.fillStyle = inkOuter;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 1.2, 0, Math.PI * 2);
      ctx.fill();

      // 双层圆印
      ctx.strokeStyle = color1;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 1.06, 0, Math.PI * 2);
      ctx.stroke();

      ctx.strokeStyle = color2;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.9, 0, Math.PI * 2);
      ctx.stroke();

      // 内芯宣纸底色
      ctx.fillStyle = 'rgba(255,255,255,0.58)';
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.82, 0, Math.PI * 2);
      ctx.fill();

      // 中心符字（无霓虹）
      const symbols = { wealth: '財', love: '緣', career: '業', health: '康', safety: '安' };
      const text = symbols[data.type] || '運';
      ctx.font = 'bold 130px "STKaiti","KaiTi","Microsoft YaHei",serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#2b2b2b';
      ctx.fillText(text, centerX, centerY + 4);
      ctx.restore();
      return;
    }

    const symChars = { wealth: '財', love: '緣', career: '業', health: '康', safety: '安' };
    const symText = symChars[data.type] || '運';

    // ===== 极简：细线双环 + 无发光/无扫描线 =====
    if (data.style === 'minimal') {
      ctx.shadowBlur = 0;
      ctx.strokeStyle = '#4b5563';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 1.12, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.96, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(0,0,0,0.03)';
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.88, 0, Math.PI * 2);
      ctx.fill();
      ctx.font = 'bold 118px "Microsoft YaHei", "PingFang SC", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#111827';
      ctx.fillText(symText, centerX, centerY);
      ctx.restore();
      return;
    }

    // ===== 国潮：金红双环 + 楷体渐变字（无霓虹/无扫描线/无电路节点）=====
    if (data.style === 'national') {
      const ringGrad = ctx.createLinearGradient(centerX - radius, centerY - radius, centerX + radius, centerY + radius);
      ringGrad.addColorStop(0, color1);
      ringGrad.addColorStop(1, color2);
      ctx.shadowColor = 'rgba(212, 175, 55, 0.35)';
      ctx.shadowBlur = 12;
      ctx.strokeStyle = ringGrad;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 1.14, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(196, 30, 58, 0.7)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.98, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255, 248, 220, 0.2)';
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.9, 0, Math.PI * 2);
      ctx.fill();
      for (let i = 0; i < 4; i++) {
        const ang = (Math.PI / 2) * i;
        ctx.fillStyle = '#d4af37';
        ctx.beginPath();
        ctx.arc(centerX + Math.cos(ang) * radius * 1.02, centerY + Math.sin(ang) * radius * 1.02, 5, 0, Math.PI * 2);
        ctx.fill();
      }
      const txtGrad = ctx.createLinearGradient(centerX, centerY - 72, centerX, centerY + 72);
      txtGrad.addColorStop(0, '#f4e4bc');
      txtGrad.addColorStop(0.5, '#b8860b');
      txtGrad.addColorStop(1, '#7a5c1e');
      ctx.font = 'bold 122px "STKaiti","KaiTi","Microsoft YaHei",serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = txtGrad;
      ctx.shadowColor = 'rgba(0,0,0,0.22)';
      ctx.shadowBlur = 5;
      ctx.fillText(symText, centerX, centerY);
      ctx.shadowBlur = 0;
      ctx.restore();
      return;
    }

    // ===== 金箔：多层金环 + 暗底 + 渐变金字（无扫描线/无旋转光点）=====
    if (data.style === 'gold') {
      ctx.strokeStyle = color1;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 1.15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = color2;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255, 215, 0, 0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.88, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(25, 25, 30, 0.45)';
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.8, 0, Math.PI * 2);
      ctx.fill();
      const gg = ctx.createLinearGradient(centerX - 70, centerY - 80, centerX + 70, centerY + 80);
      gg.addColorStop(0, '#fffef5');
      gg.addColorStop(0.45, color1);
      gg.addColorStop(1, color2);
      ctx.font = 'bold 124px "STKaiti","KaiTi","Microsoft YaHei",serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = gg;
      ctx.shadowColor = 'rgba(255, 215, 0, 0.28)';
      ctx.shadowBlur = 12;
      ctx.fillText(symText, centerX, centerY);
      ctx.shadowBlur = 0;
      ctx.restore();
      return;
    }

    // ===== 赛博朋克：霓虹圆环 + 扫描线 + 旋转光点 + 电路节点（仅 cyber）=====
    ctx.shadowColor = color1;
    ctx.shadowBlur = 15;
    ctx.strokeStyle = color1;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius * 1.15, 0, Math.PI * 2);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = color2;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius * 1.12, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = color1;
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius * 1.08, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.shadowColor = color1;
    ctx.shadowBlur = 20;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = color1 + '15';
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius * 0.92, 0, Math.PI * 2);
    ctx.fill();

    const cornerSize = 35;
    const cornerMargin = 60;
    ctx.strokeStyle = color1;
    ctx.lineWidth = 4;
    const drawCanvasCorner = (x, y, rotation) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.beginPath();
      ctx.moveTo(-cornerSize, 0);
      ctx.lineTo(-cornerSize, -cornerSize);
      ctx.lineTo(0, -cornerSize);
      ctx.stroke();
      ctx.fillStyle = color2;
      ctx.fillRect(-10, -10, 6, 6);
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(-5, -5, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };
    drawCanvasCorner(cornerMargin, cornerMargin, 0);
    drawCanvasCorner(width - cornerMargin, cornerMargin, Math.PI / 2);
    drawCanvasCorner(width - cornerMargin, height - cornerMargin, Math.PI);
    drawCanvasCorner(cornerMargin, height - cornerMargin, -Math.PI / 2);

    const time = Date.now() / 1000;
    const lightAngle = time * 1.5;
    const lightRadius = radius * 1.15;
    const lightX = centerX + Math.cos(lightAngle) * lightRadius;
    const lightY = centerY + Math.sin(lightAngle) * lightRadius;
    const lightGradient = ctx.createRadialGradient(lightX, lightY, 0, lightX, lightY, 15);
    lightGradient.addColorStop(0, '#ffffff');
    lightGradient.addColorStop(0.3, color1);
    lightGradient.addColorStop(1, 'transparent');
    ctx.fillStyle = lightGradient;
    ctx.beginPath();
    ctx.arc(lightX, lightY, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(lightX, lightY, 5, 0, Math.PI * 2);
    ctx.fill();

    ctx.font = 'bold 120px "Microsoft YaHei", sans-serif';
    const textMetrics = ctx.measureText(symText);
    const minMargin = 100;
    const effectiveRadius = Math.min(radius, Math.min(width, height) / 2 - minMargin);
    let fontSize = 120;
    if (textMetrics.width > effectiveRadius * 1.8) {
      fontSize = Math.floor(
        effectiveRadius * 1.8 / (textMetrics.actualBoundingBoxRight + textMetrics.actualBoundingBoxLeft || textMetrics.width)
      );
      ctx.font = `bold ${fontSize}px "Microsoft YaHei", sans-serif`;
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = color1;
    ctx.shadowBlur = 25;
    ctx.strokeStyle = color1;
    ctx.lineWidth = 4;
    ctx.strokeText(symText, centerX, centerY);
    ctx.shadowBlur = 0;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeText(symText, centerX, centerY);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(symText, centerX, centerY);

    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius * 0.85, 0, Math.PI * 2);
    ctx.clip();
    ctx.strokeStyle = color1 + '18';
    ctx.lineWidth = 0.8;
    for (let i = -radius * 0.8; i < radius * 0.8; i += 8) {
      ctx.beginPath();
      ctx.moveTo(centerX - radius * 0.85, centerY + i);
      ctx.lineTo(centerX + radius * 0.85, centerY + i);
      ctx.stroke();
    }
    ctx.restore();

    ctx.save();
    ctx.translate(centerX, centerY);
    const smallRadius = radius * 1.28;
    const nodeSize = 5;
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 / 8) * i;
      const x = Math.cos(angle) * smallRadius;
      const y = Math.sin(angle) * smallRadius;
      ctx.strokeStyle = color1;
      ctx.lineWidth = 2;
      ctx.strokeRect(x - nodeSize, y - nodeSize, nodeSize * 2, nodeSize * 2);
      ctx.fillStyle = i % 2 === 0 ? color1 : color2;
      ctx.fillRect(x - nodeSize + 2, y - nodeSize + 2, (nodeSize - 2) * 2, (nodeSize - 2) * 2);
      ctx.strokeStyle = color2 + '60';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x * 0.7, y * 0.7);
      ctx.lineTo(x * 0.95, y * 0.95);
      ctx.stroke();
    }
    ctx.restore();

    ctx.restore();
  };
  
  // 清理祈福文案中的Markdown代码块标记
  const cleanBlessingText = (text) => {
    if (!text) return '';
    // 移除 ```json、```javascript、``` 等代码块标记
    return text
      .replace(/```json\s*/gi, '')
      .replace(/```javascript\s*/gi, '')
      .replace(/```\s*/g, '')
      .replace(/^\{\s*"title":\s*"([^"]+)"[^}]*"blessing":\s*"([^"]+)"[^}]*\}$/gm, (match, title, blessing) => {
        // 如果是纯JSON格式，提取blessing内容
        return blessing.replace(/\\n/g, '\n');
      })
      .trim();
  };

  // 绘制祈福文案
  const drawTalismanText = (ctx, data, width, height) => {
    ctx.save();
    
    const colors = {
      cyber: '#ffffff',
      national: '#d4af37',
      minimal: '#1a1a2e',
      gold: '#d4af37',
      ink: '#1a1a2e'
    };
    
    const color = colors[data.style] || '#ffffff';
    
    // 祝福语 - 清理Markdown标记
    const cleanBlessing = cleanBlessingText(data.blessing);
    
    const isInk = data.style === 'ink';
    const textStartY = isInk ? (height / 2 + 240) : (height / 2 + 280);
    const textBottomLimit = height - 430;
    const maxWidth = isInk ? (width - 340) : (width - 140);
    const startX = isInk ? 130 : (width / 2);
    ctx.textAlign = isInk ? 'left' : 'center';

    const countWrappedLines = (sections, fontSize) => {
      ctx.font = `${fontSize}px "Microsoft YaHei", sans-serif`;
      let lines = 0;
      sections.forEach((line) => {
        if (!line.trim()) return;
        if (ctx.measureText(line).width <= maxWidth) {
          lines += 1;
          return;
        }
        let cur = '';
        for (let i = 0; i < line.length; i++) {
          const t = cur + line[i];
          if (ctx.measureText(t).width > maxWidth && cur) {
            lines += 1;
            cur = line[i];
          } else {
            cur = t;
          }
        }
        if (cur) lines += 1;
      });
      return lines;
    };

    if (cleanBlessing) {
      const sectionsRaw = cleanBlessing.split('\n').filter(Boolean).slice(0, 6);
      const reserveWish = data.wish ? 76 : 0;
      const maxTextHeight = textBottomLimit - textStartY - reserveWish;
      let fontSize = isInk ? 32 : 40;
      let lineHeight = Math.round(fontSize * (isInk ? 1.45 : 1.48));
      for (let iter = 0; iter < 12; iter++) {
        const lines = countWrappedLines(sectionsRaw, fontSize);
        const need = lines * lineHeight;
        if (need <= maxTextHeight || fontSize <= (isInk ? 22 : 24)) {
          lineHeight = Math.round(fontSize * (isInk ? 1.42 : 1.46));
          break;
        }
        fontSize -= isInk ? 1 : 2;
        lineHeight = Math.round(fontSize * (isInk ? 1.42 : 1.46));
      }
      ctx.font = `${fontSize}px "Microsoft YaHei", sans-serif`;

      let y = textStartY;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;

      sectionsRaw.forEach((line) => {
        if (y > textBottomLimit - reserveWish) return;
        const textMetrics = ctx.measureText(line);
        if (textMetrics.width <= maxWidth) {
          ctx.fillStyle = color;
          ctx.fillText(line, startX, y);
          y += lineHeight;
          return;
        }
        const words = line.split('');
        let currentLine = '';
        for (let i = 0; i < words.length; i++) {
          const testLine = currentLine + words[i];
          const testMetrics = ctx.measureText(testLine);
          if (testMetrics.width > maxWidth && currentLine) {
            ctx.fillStyle = color;
            ctx.fillText(currentLine, startX, y);
            y += lineHeight;
            if (y > textBottomLimit - reserveWish) return;
            currentLine = words[i];
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine && y <= textBottomLimit - reserveWish) {
          ctx.fillStyle = color;
          ctx.fillText(currentLine, startX, y);
          y += lineHeight;
        }
      });

      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }

    if (data.wish) {
      let wSize = Math.min(32, Math.max(22, (isInk ? 28 : 30)));
      ctx.font = `italic ${wSize}px "Microsoft YaHei", sans-serif`;
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.82;
      const wishText = `「${data.wish.substring(0, 36)}${data.wish.length > 36 ? '…' : ''}」`;
      const maxWishWidth = width - 120;
      let wm = ctx.measureText(wishText);
      while (wm.width > maxWishWidth && wSize > 18) {
        wSize -= 1;
        ctx.font = `italic ${wSize}px "Microsoft YaHei", sans-serif`;
        wm = ctx.measureText(wishText);
      }
      const wishY = Math.min(height / 2 + 480, textBottomLimit - 8);
      ctx.fillText(wishText, isInk ? startX : width / 2, wishY);
      ctx.globalAlpha = 1;
    }
    
    ctx.restore();
  };
  
  // 绘制个人信息
  const drawTalismanPersonal = (ctx, data, width, height) => {
    ctx.save();
    
    const colors = {
      cyber: '#00ffff',
      national: '#d4af37',
      minimal: '#1a1a2e',
      gold: '#d4af37',
      ink: '#1a1a2e'
    };
    
    const color = colors[data.style] || '#00ffff';
    const y = height - 320; // 稍微上移，避免与印章重叠
    
    // 姓名
    ctx.font = '32px "Microsoft YaHei", sans-serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    
    const nameText = `专属：${data.name}`;
    const nameMetrics = ctx.measureText(nameText);
    const maxNameWidth = width - 120; // 保留边距
    
    if (nameMetrics.width > maxNameWidth) {
      // 调整姓名文字大小
      const adjustedNameFontSize = Math.floor(32 * maxNameWidth / nameMetrics.width);
      ctx.font = `${adjustedNameFontSize}px "Microsoft YaHei", sans-serif`;
    }
    
    ctx.fillText(nameText, width / 2, y);
    
    // 八字（如果有）
    if (data.bazi) {
      ctx.font = '28px "Microsoft YaHei", sans-serif';
      const baziText = `八字：${data.bazi}`;
      const baziMetrics = ctx.measureText(baziText);
      const maxBaziWidth = width - 120;
      
      if (baziMetrics.width > maxBaziWidth) {
        const adjustedBaziFontSize = Math.floor(28 * maxBaziWidth / baziMetrics.width);
        ctx.font = `${adjustedBaziFontSize}px "Microsoft YaHei", sans-serif`;
      }
      
      ctx.globalAlpha = 0.7;
      ctx.fillText(baziText, width / 2, y + 50);
      ctx.globalAlpha = 1;
    }
    
    // 日期
    ctx.font = '24px "Microsoft YaHei", sans-serif';
    const dateText = `生成于 ${new Date().toLocaleDateString('zh-CN')}`;
    const dateMetrics = ctx.measureText(dateText);
    const maxDateWidth = width - 120;
    
    if (dateMetrics.width > maxDateWidth) {
      const adjustedDateFontSize = Math.floor(24 * maxDateWidth / dateMetrics.width);
      ctx.font = `${adjustedDateFontSize}px "Microsoft YaHei", sans-serif`;
    }
    
    ctx.globalAlpha = 0.5;
    ctx.fillText(dateText, width / 2, y + 90);
    
    ctx.restore();
  };
  
  // 绘制印章
  const drawTalismanSeal = (ctx, style, width, height) => {
    ctx.save();
    
    const isInk = style === 'ink';
    const x = isInk ? (width - 130) : (width - 200);
    const y = isInk ? (height - 120) : (height - 200);
    const size = isInk ? 92 : 120;
    
    ctx.fillStyle = '#c41e3a';
    ctx.globalAlpha = isInk ? 0.9 : 1;
    ctx.beginPath();
    ctx.rect(x - size/2, y - size/2, size, size);
    ctx.fill();
    
    ctx.strokeStyle = '#8b0000';
    ctx.lineWidth = 4;
    ctx.stroke();
    
    ctx.font = isInk ? 'bold 30px "SimSun", serif' : 'bold 40px "SimSun", serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('云端', x, y - 20);
    ctx.fillText('问道', x, y + 20);
    
    ctx.restore();
  };
  
  // 绘制水印
  const drawTalismanWatermark = (ctx, width, height) => {
    ctx.save();
    
    ctx.font = 'bold 60px "Microsoft YaHei", sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    ctx.translate(width / 2, height / 2);
    ctx.rotate(-Math.PI / 6);
    ctx.fillText('云端问道 · 预览版', 0, 0);
    
    ctx.restore();
  };
  
  // 灵符：创建订单后走与会员相同的微信 Native·JSAPI·H5 / 支付宝官方下单，异步通知落库后解锁
  const handleTalismanPayment = async (tier) => {
    if (!talismanPreview) return;

    // 未登录时强制要求先登录，确保购买记录绑定到手机号
    if (!authToken) {
      alert('请先登录后再购买灵符，以便订单安全绑定到您的账号');
      setActiveTab('my');
      setTalismanPayLoading(false);
      return;
    }

    clearAllAnalysisLoading();
    setTalismanPayLoading(true);
    try {
      const orderResult = await requestApi('/talisman/create-order', {
        method: 'POST',
        data: {
          userId,
          tier,
          talismanData: talismanForm,
          paymentMethod: talismanPayMethod
        }
      });

      if (!orderResult.success || !orderResult.orderId) {
        alert(orderResult.error || '创建订单失败');
        return;
      }

      const oid = orderResult.orderId;
      const pm = orderResult.paymentMethod || talismanPayMethod;
      const w = typeof window !== 'undefined' ? window : null;

      // 与会员付费墙一致：不依赖 capabilities，始终请求官方微信下单（Native / JSAPI / H5）
      if (w && pm === 'wechat') {
        const ua = navigator.userAgent || '';
        const isWx = /MicroMessenger/i.test(ua);
        const mobile = /Mobile|Android|iPhone|iPad|iPod|HarmonyOS|webOS/i.test(ua);
        let wechatChannel = 'native';
        if (isWx && mobile) wechatChannel = 'jsapi';
        else if (mobile && !isWx) wechatChannel = 'h5';
        const origin = window.location.origin;
        const pathOnly = window.location.pathname || '/';
        const oauthRedirectUri = `${origin}${pathOnly}?wx_oauth_pay=1`;
        const openid = isWx ? (sessionStorage.getItem('wechatMpOpenId') || '') : '';
        const payReq = await requestApi('/payment/request', {
          method: 'POST',
          data: {
            orderId: oid,
            wechatChannel,
            openid,
            oauthRedirectUri: wechatChannel === 'jsapi' ? oauthRedirectUri : undefined,
            h5WapUrl: wechatChannel === 'h5' ? window.location.href.split('#')[0] : undefined
          }
        });
        if (payReq.needMpOAuth && payReq.oauthUrl) {
          sessionStorage.setItem('pendingTalismanOrderId', oid);
          window.location.href = payReq.oauthUrl;
          return;
        }
        if (payReq.success && wechatChannel === 'h5' && payReq.h5Url) {
          sessionStorage.setItem('pendingTalismanOrderId', oid);
          window.location.href = payReq.h5Url;
          return;
        }
        if (payReq.success && wechatChannel === 'jsapi' && payReq.jsapiParams) {
          sessionStorage.setItem('pendingTalismanOrderId', oid);
          const p = payReq.jsapiParams;
          const invoke = () => {
            const bridge = window.WeixinJSBridge;
            if (!bridge || typeof bridge.invoke !== 'function') {
              alert('请在微信内打开本站以完成支付');
              return;
            }
            bridge.invoke(
              'getBrandWCPayRequest',
              {
                appId: p.appId,
                timeStamp: p.timeStamp,
                nonceStr: p.nonceStr,
                package: p.package,
                signType: p.signType,
                paySign: p.paySign
              },
              (res) => {
                const msg = (res && res.err_msg) || '';
                if (msg.indexOf('get_brand_wcpay_request:ok') === 0) {
                  alert('支付已完成，正在确认订单…');
                } else if (msg.indexOf('get_brand_wcpay_request:cancel') === 0) {
                  alert('已取消支付');
                } else {
                  alert('支付未完成：' + (msg || JSON.stringify(res)));
                }
              }
            );
          };
          if (typeof window.WeixinJSBridge === 'undefined') {
            document.addEventListener('WeixinJSBridgeReady', invoke, false);
          } else {
            invoke();
          }
          setTalismanOrder({ ...orderResult, gateway: 'wechat' });
          return;
        }
        if (payReq.success && payReq.codeUrl) {
          sessionStorage.setItem('pendingTalismanOrderId', oid);
          setTalismanOrder({ ...orderResult, gateway: 'wechat' });
          setTalismanWechatPay({
            orderId: oid,
            qrDataUrl: payReq.qrCodeDataUrl,
            codeUrl: payReq.codeUrl
          });
          return;
        }
        alert(
          `${payReq.error || '微信支付下单失败'}\n\n可打开：当前站点 /api/payment/wechat-diagnose 查看配置自检。`
        );
      }

      // 与会员付费墙一致：不依赖 capabilities 判断是否调官方接口，避免误判导致无法拉起支付宝
      if (w && pm === 'alipay') {
        const mobile = /Mobile|Android|iPhone|iPad|iPod|HarmonyOS|webOS/i.test(navigator.userAgent);
        const ch = mobile ? 'wap' : 'page';
        const path = window.location.pathname || '/';
        const returnUrl = `${window.location.origin}${path}?talismanPayReturn=1`;
        const payReq = await requestApi('/payment/request', {
          method: 'POST',
          data: { orderId: oid, alipayChannel: ch, returnUrl }
        });
        if (payReq.success && payReq.redirectUrl) {
          sessionStorage.setItem('pendingTalismanOrderId', oid);
          window.location.href = payReq.redirectUrl;
          return;
        }
        if (payReq.success && payReq.formHtml) {
          sessionStorage.setItem('pendingTalismanOrderId', oid);
          if (!submitAlipayFormHtml(payReq.formHtml)) {
            alert('无法解析支付表单，请使用 Chrome / Edge 最新版重试。');
          }
          return;
        }
        console.error('[alipay] talisman /payment/request', payReq);
        alert(
          `${payReq.error || '无法拉起支付宝收银台'}\n\n可打开：当前站点 /api/payment/alipay-diagnose 查看配置自检。`
        );
      }

      setTalismanOrder(orderResult);
    } catch (e) {
      console.error('创建灵符订单失败:', e);
      setError('订单创建失败，请重试');
    } finally {
      setTalismanPayLoading(false);
    }
  };

  const handleTalismanConfirmPaid = async () => {
    if (!talismanOrder?.orderId || !talismanPreview) return;
    clearAllAnalysisLoading();
    setTalismanPayLoading(true);
    try {
      const payResult = await requestApi('/talisman/process-payment', {
        method: 'POST',
        data: { orderId: talismanOrder.orderId, userId }
      });
      if (payResult.success) {
        const updatedData = { ...talismanPreview, paid: true, watermark: false };
        await drawTalisman(updatedData);
        setTalismanPreview(updatedData);
        setTalismanOrder(null);
        setTalismanWechatPay(null);
        sessionStorage.removeItem('pendingTalismanOrderId');
      } else {
        alert(payResult.error || '未确认到支付结果，请完成付款后重试');
      }
    } catch (e) {
      console.error('灵符支付确认失败:', e);
      alert('网络错误，请稍后重试');
    } finally {
      setTalismanPayLoading(false);
    }
  };
  
  // 下载灵符（标准版 1× 像素；高清/月卡 2× 导出以提升清晰度）
  const downloadTalisman = async () => {
    if (!talismanPreview) return;
    const tier = talismanForm.tier;
    const exportScale = tier === 'hd' || tier === 'monthly' ? 2 : 1;
    const off = document.createElement('canvas');
    try {
      await drawTalisman(talismanPreview, { exportScale, targetCanvas: off });
      const link = document.createElement('a');
      const suffix = exportScale > 1 ? `@${exportScale}x` : '';
      link.download = `云端问道-灵符-${talismanForm.name}${suffix}-${Date.now()}.png`;
      link.href = off.toDataURL('image/png');
      link.click();
    } finally {
      await drawTalisman(talismanPreview);
    }
  };
  
  // ==================== 灵兽传音功能 ====================
  
  // TTS音色列表
  const [ttsVoices, setTTSVoices] = useState([]);
  const [ttsEnabled, setTTSEnabled] = useState(false);

  const talismanPricingOptions = [
  { id: 'standard', price: '9.9', desc: '标准 1080P · 去水印下载', payLabel: '标准版' },
  { id: 'hd', price: '19.9', desc: '高清 4K · 去水印下载', payLabel: '高清4K版', recommended: true },
  { id: 'monthly', price: '29.9', desc: '月卡 · 当月不限张 · 均去水印', payLabel: '月卡（不限次数）' }
  ];
  const selectedTalismanTier = talismanPricingOptions.find((item) => item.id === talismanForm.tier) || talismanPricingOptions[1];
  const [ttsProvider, setTTSProvider] = useState('');
  const [currentAudio, setCurrentAudio] = useState(null);
  const [spiritLastTtsError, setSpiritLastTtsError] = useState('');
  const [spiritPendingAudio, setSpiritPendingAudio] = useState(null); // { audioUrl, voiceKey }
  
  const fetchTTSVoices = useCallback(async () => {
    try {
      const data = await requestApi('/tts/voices');
      setTTSEnabled(!!data.enabled);
      setTTSProvider(data.provider || '');
      if (Array.isArray(data.voices)) {
        setTTSVoices(data.voices);
        // 默认/兜底选择：未选过，或当前选择不在新列表里（例如从旧音色切到新提供方）
        if (data.voices.length > 0) {
          const selected = spiritVoiceType || '';
          const exists = selected && data.voices.some(v => v.key === selected);
          if (!exists) setSpiritVoiceType(data.voices[0].key);
        }
      } else {
        setTTSVoices([]);
      }
    } catch (e) {
      // 后端短暂重启/断连时，前端不应永久卡在"未启用"
      setTTSEnabled(false);
      setTTSProvider('');
      setTTSVoices([]);
      console.log('TTS服务未启用:', e);
    }
  }, [spiritVoiceType]);

  // 获取TTS音色列表：首次加载 + 进入灵兽传音页时刷新一次（避免后端重启后前端一直无声）
  useEffect(() => {
    fetchTTSVoices();
  }, [fetchTTSVoices]);

  useEffect(() => {
    if (activeTab === 'spirit') fetchTTSVoices();
  }, [activeTab, fetchTTSVoices]);
  
  // 语音合成播放
  // 只播放合成音：宁可等，也不要机械播报；失败就不播（不回退浏览器朗读）
  const speakWithTTS = async (text, voiceType = 'xiaomei') => {
    // 如果有正在播放的音频，先停止
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    
    const primeAudioPlayback = async () => {
      if (spiritAudioPrimedRef.current) return;
      // 用一次极短静音音频"解锁"自动播放策略（需要发生在用户手势链路中）
      const SILENT_WAV_DATA_URI =
        'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';
      try {
        const a = new Audio(SILENT_WAV_DATA_URI);
        a.muted = true;
        await a.play();
        a.pause();
        spiritAudioPrimedRef.current = true;
      } catch {
        // 若浏览器仍拒绝，也不影响后续；真正播放失败会走 fallback
      }
    };
    
    setSpiritLastTtsError('');
    setSpiritPendingAudio(null);
    // 如果服务端TTS未启用：直接不播（避免机械音）
    if (!ttsEnabled) return;
    
    // 根据心情/场景生成更"有情感"的指令（用于角色语音）
    const buildSpiritInstructions = (mood) => {
      // 指令越长端到端越慢：这里刻意做"短指令"以降低语音等待
      const byMood = {
        idle: '嘴硬心软、傲娇、口语化，带一点轻哼与调侃。',
        happy: '开心上头、轻快、有笑意，结尾略上扬。',
        angry: '护主、强势直球，先怼后软，节奏更有力。',
        shy: '害羞嘴硬，小声嘀咕，停顿更明显。'
      };
      return `灵兽传音角色语音。中文口语自然，不要播报腔；有情绪起伏与停顿。心情：${mood || 'idle'}。${byMood[mood] || byMood.idle}`;
    };
    
    try {
      // 尽量在真正播放前再 prime 一次（若此前没解锁过）
      await primeAudioPlayback();

      // 播报文本做"口语播报版"净化与截断：越短越快（体感明显）
      // 1. 去掉括号内内容（动作描述、表情等不朗读）
      // 2. 按换行分割成句子，用句号连接使衔接自然
      // 3. 限制总长度
      const ttsText = String(text || '')
        .replace(/\([^)]*\)/g, '') // 去掉括号内OS
        .split(/\n+/) // 按换行分割
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .join('。') // 用句号连接，相邻句子听起来更自然
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 200);

      const audioBlob = await requestApi('/tts/synthesize', {
        method: 'POST',
        data: {
          text: ttsText,
          voice: voiceType,
          provider: 'qwen3',
          instructions: buildSpiritInstructions(spiritMood),
          // 为了降低等待时间，关闭"指令优化"这一步（可明显减少端到端耗时）
          optimizeInstructions: false,
          // 低延迟优先：服务端会缩短超时窗口，更快返回/更快失败，不会卡太久
          preferLowLatency: true,
          languageType: 'Chinese'
        },
        responseType: 'blob'
      });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      setCurrentAudio(audio);
      // 注意：部分浏览器/场景下，play() 可能被自动播放策略拒绝并以 Promise reject 的形式返回
      await audio.play();
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setCurrentAudio(null);
      };
      
    } catch (e) {
      console.error('TTS播放失败:', e);
      // 不回退到浏览器朗读，避免机械音；但给用户一个"点一下播放"的机会（用户手势更不容易被拦截）
      const msg = e?.message || '语音播放失败';
      setSpiritLastTtsError(msg);
      // 如果是自动播放被拦截，用户点一下可恢复播放；这里提示用户重试/点击播放
    }
  };
  
  // 处理灵兽对话
  const handleSpiritChat = async () => {
    // 检查是否有文字或图片
    const hasText = spiritInput.trim();
    const hasImage = spiritImage !== null;
    
    if (!hasText && !hasImage) return;
    
    // 前端预检：非会员每日3次限制
    if (!userInfo?.isVip) {
      const remaining = userInfo?.freeCalls?.spirit ?? 0;
      if (remaining <= 0) {
        alert('今日灵兽传音次数已用完！升级会员可无限使用~');
        return;
      }
    }
    
    const userMsg = hasText ? spiritInput.trim() : (hasImage ? '📷 [图片]' : '');
    
    // 添加用户消息到聊天记录
    setSpiritMessages(prev => [...prev, { 
      role: 'user', 
      content: userMsg,
      image: spiritImagePreview // 如果有图片，保存预览
    }]);
    
    setSpiritInput('');
    setSpiritLoading(true);
    setSpiritTyping(true);

    // 尝试在"点击发送"的用户手势链路里解锁音频播放（否则异步合成回来后可能被浏览器拦截）
    if (spiritVoiceEnabled) {
      try {
        const SILENT_WAV_DATA_URI =
          'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';
        const a = new Audio(SILENT_WAV_DATA_URI);
        a.muted = true;
        await a.play();
        a.pause();
        spiritAudioPrimedRef.current = true;
      } catch {
        // ignore
      }
    }
    
    try {
      let r;
      
      // 如果有图片，使用图像识别API
      if (hasImage) {
        console.log('🖼️ 正在发送图片给灵兽识别...');
        
        // 获取图片的base64数据（去掉data:image/xxx;base64,前缀）
        const base64Data = spiritImage.split(',')[1];
        const mimeType = spiritImage.match(/data:(.*?);base64/)?.[1] || 'image/jpeg';
        
        r = await requestApi('/spirit/vision', {
          method: 'POST',
          data: {
            imageBase64: base64Data,
            mimeType: mimeType,
            message: hasText ? spiritInput.trim() : '',
            userId,
            history: spiritMessages.slice(-10)
          }
        });
        
        // 清除图片
        setSpiritImage(null);
        setSpiritImagePreview(null);
      } else {
        // 纯文字对话
        r = await requestApi('/spirit/chat', {
          method: 'POST',
          data: {
            message: userMsg,
            userId,
            history: spiritMessages.slice(-10)
          }
        });
      }
      
      setSpiritTyping(false);
      
      if (r?.error === 'limit') {
        // 次数用完的专门提示
        setSpiritMessages(prev => [...prev, { 
          role: 'spirit', 
          content: r.message || '哼...今天本灵兽已经说累了，明天再来找我吧！（或者升级会员，我就不累了~）',
          mood: 'angry'
        }]);
        fetchUserInfo(); // 刷新剩余次数
      } else if (r.error) {
        // 其他错误
        setSpiritMessages(prev => [...prev, { 
          role: 'spirit', 
          content: '哼...本灵兽现在不想说话，你晚点再来吧！（其实是我累了）',
          mood: 'angry'
        }]);
      } else {
        // 文字直接完整输出（用户可读，语音自然等待合成完成后播放）
        setSpiritMessages(prev => [...prev, { 
          role: 'spirit', 
          content: r.message,
          mood: r.mood || 'idle'
        }]);
        
        // 更新灵兽心情
        setSpiritMood(r.mood || 'idle');

        // 语音播放
        if (spiritVoiceEnabled) {
          speakWithTTS(r.message, spiritVoiceType);
        }
      }
    } catch (e) {
      console.error('灵兽对话失败:', e);
      setSpiritTyping(false);
      
      // 处理后端返回的次数限制错误
      if (e?.error === 'limit' || e?.message?.includes('次数已用完')) {
        setSpiritMessages(prev => [...prev, { 
          role: 'spirit', 
          content: e.message || '哼...今天本灵兽已经说累了，明天再来找我吧！（或者升级会员，我就不累了~）',
          mood: 'angry'
        }]);
        fetchUserInfo(); // 刷新剩余次数
      } else {
        setSpiritMessages(prev => [...prev, { 
          role: 'spirit', 
          content: '诶呀，传音法术失灵了...你可别告诉别人我这么丢脸！',
          mood: 'shy'
        }]);
      }
      // 清除图片状态
      setSpiritImage(null);
      setSpiritImagePreview(null);
    }
    
    setSpiritLoading(false);
  };

  // （无打字机定时器，无需额外清理）
  
  // 处理灵兽图片上传
  const handleSpiritImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件！');
      return;
    }
    
    // 检查文件大小（最大5MB）
    if (file.size > 5 * 1024 * 1024) {
      alert('图片太大了！请选小于5MB的图片~');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setSpiritImage(base64);
      setSpiritImagePreview(base64);
      console.log('✅ 图片已选择，大小:', (base64.length / 1024).toFixed(1), 'KB');
    };
    reader.readAsDataURL(file);
  };
  
  // 清除选择的图片
  const clearSpiritImage = () => {
    setSpiritImage(null);
    setSpiritImagePreview(null);
    if (spiritImageInputRef.current) {
      spiritImageInputRef.current.value = '';
    }
  };
  
  // 灵兽快捷回复
  const spiritQuickReplies = [
    { icon: '💕', text: '我想你了', mood: 'happy' },
    { icon: '😢', text: '我今天不开心', mood: 'sad' },
    { icon: '😤', text: '有人欺负我', mood: 'angry' },
    { icon: '✨', text: '今天运势怎么样', mood: 'idle' },
    { icon: '🎮', text: '陪我聊聊天', mood: 'happy' },
    { icon: '🔮', text: '给我点建议', mood: 'idle' }
  ];
  
  // 辅助函数
  const addToHistory = (type, data) => {
    const newItem = { type, date: new Date().toLocaleString('zh-CN'), id: Date.now(), preview: data.analysis?.summary || data.message?.substring(0, 30) || '分析完成' };
    setHistory(prev => [newItem, ...prev].slice(0, 20));
  };

  const handleMemeAnswer = (questionNum, answer) => {
    const newAnswers = { ...memeAnswers, [questionNum]: answer };
    setMemeAnswers(newAnswers);
    if (questionNum < MEME_QUESTIONS.length) {
      setTimeout(() => setMemeStep(questionNum + 1), 300);
    } else {
      setTimeout(() => handleMemeFinish(newAnswers), 300);
    }
  };

  const handleMemeFinish = (answersToUse = null) => {
    const answers = answersToUse || memeAnswers;
    const computed = computeMemeResult(answers);
    const payload = {
      type: 'mbti_meme',
      message: `${computed.code} ${computed.title}`,
      code: computed.code,
      title: computed.title,
      tagline: computed.tagline,
      traits: computed.traits,
      mbtiHint: computed.mbtiHint,
      matchPct: computed.matchPct,
      hitCount: computed.hitCount,
      totalQ: computed.totalQ,
    };
    setResult(payload);
    addToHistory('趣味梗型', { message: payload.message });
    setMemeStep(MEME_QUESTIONS.length + 1);
  };

  const handleSubscribe = async ({
    plan = 'annual',
    paymentMethod = 'wechat',
    useAlipayGateway = false,
    useWechatGateway = false
  } = {}) => {
    // 未登录时强制要求先登录，确保会员绑定到手机号而非游客ID
    if (!authToken) {
      alert('请先登录后再开通会员，以便会员权益安全绑定到您的账号');
      setActiveTab('my');
      setMembershipPayLoading(false);
      return;
    }

    clearAllAnalysisLoading();
    setMembershipPayLoading(true);
    try {
      const orderResult = await requestApi('/payment/create-order', {
        method: 'POST',
        data: { userId, planId: plan, paymentMethod }
      });

      if (!orderResult.success || !orderResult.orderId) {
        alert(orderResult.error || '创建订单失败');
        return;
      }

      if (useWechatGateway && paymentMethod === 'wechat' && typeof window !== 'undefined') {
        const ua = navigator.userAgent || '';
        const isWx = /MicroMessenger/i.test(ua);
        const mobile = /Mobile|Android|iPhone|iPad|iPod|HarmonyOS|webOS/i.test(ua);
        let wechatChannel = 'native';
        if (isWx && mobile) wechatChannel = 'jsapi';
        else if (mobile && !isWx) wechatChannel = 'h5';
        const origin = window.location.origin;
        const pathOnly = window.location.pathname || '/';
        const oauthRedirectUri = `${origin}${pathOnly}?wx_oauth_pay=1`;
        const openid = isWx ? (sessionStorage.getItem('wechatMpOpenId') || '') : '';
        const payReq = await requestApi('/payment/request', {
          method: 'POST',
          data: {
            orderId: orderResult.orderId,
            wechatChannel,
            openid,
            oauthRedirectUri: wechatChannel === 'jsapi' ? oauthRedirectUri : undefined,
            h5WapUrl: wechatChannel === 'h5' ? window.location.href.split('#')[0] : undefined
          }
        });
        if (payReq.needMpOAuth && payReq.oauthUrl) {
          sessionStorage.setItem('pendingVipOrderId', orderResult.orderId);
          window.location.href = payReq.oauthUrl;
          return;
        }
        if (payReq.success && wechatChannel === 'h5' && payReq.h5Url) {
          sessionStorage.setItem('pendingVipOrderId', orderResult.orderId);
          window.location.href = payReq.h5Url;
          return;
        }
        if (payReq.success && wechatChannel === 'jsapi' && payReq.jsapiParams) {
          sessionStorage.setItem('pendingVipOrderId', orderResult.orderId);
          const p = payReq.jsapiParams;
          const invoke = () => {
            const bridge = window.WeixinJSBridge;
            if (!bridge || typeof bridge.invoke !== 'function') {
              alert('请在微信内打开本站以完成支付');
              return;
            }
            bridge.invoke(
              'getBrandWCPayRequest',
              {
                appId: p.appId,
                timeStamp: p.timeStamp,
                nonceStr: p.nonceStr,
                package: p.package,
                signType: p.signType,
                paySign: p.paySign
              },
              (res) => {
                const msg = (res && res.err_msg) || '';
                if (msg.indexOf('get_brand_wcpay_request:ok') === 0) {
                  alert('支付已完成，正在确认订单…');
                } else if (msg.indexOf('get_brand_wcpay_request:cancel') === 0) {
                  alert('已取消支付');
                } else {
                  alert('支付未完成：' + (msg || JSON.stringify(res)));
                }
              }
            );
          };
          if (typeof window.WeixinJSBridge === 'undefined') {
            document.addEventListener('WeixinJSBridgeReady', invoke, false);
          } else {
            invoke();
          }
          return;
        }
        if (payReq.success && payReq.codeUrl) {
          sessionStorage.setItem('pendingVipOrderId', orderResult.orderId);
          setVipWechatPay({
            orderId: orderResult.orderId,
            qrDataUrl: payReq.qrCodeDataUrl,
            codeUrl: payReq.codeUrl
          });
          return;
        }
        const wtip = payReq.error || '微信支付下单失败';
        alert(`${wtip}\n\n可打开：当前站点 /api/payment/wechat-diagnose 查看配置自检。`);
        return;
      }

      if (useAlipayGateway && paymentMethod === 'alipay' && typeof window !== 'undefined') {
        const mobile = /Mobile|Android|iPhone|iPad|iPod|HarmonyOS|webOS/i.test(navigator.userAgent);
        const ch = mobile ? 'wap' : 'page';
        // 支付宝 return_url 有长度上限（约 256）；勿把当前页全部 query 拼进去，否则 INVALID_PARAMETER
        const path = window.location.pathname || '/';
        const returnUrl = `${window.location.origin}${path}?vipPayReturn=1`;
        const payReq = await requestApi('/payment/request', {
          method: 'POST',
          data: { orderId: orderResult.orderId, alipayChannel: ch, returnUrl }
        });
        if (payReq.success && payReq.redirectUrl) {
          sessionStorage.setItem('pendingVipOrderId', orderResult.orderId);
          window.location.href = payReq.redirectUrl;
          return;
        }
        if (payReq.success && payReq.formHtml) {
          sessionStorage.setItem('pendingVipOrderId', orderResult.orderId);
          if (!submitAlipayFormHtml(payReq.formHtml)) {
            alert('无法解析支付表单，请使用 Chrome / Edge 最新版重试，或联系开发者。');
          }
          return;
        }
        const tip = payReq.error || '无法拉起支付宝收银台';
        console.error('[alipay] /payment/request', payReq);
        alert(`${tip}\n\n请打开：当前站点 /api/payment/alipay-diagnose 查看配置自检。`);
        return;
      }

      const payResult = await requestApi('/payment/process', {
        method: 'POST',
        data: { orderId: orderResult.orderId }
      });

      if (payResult.success && payResult.newlyPaid) {
        setShowPaywall(false);
        setShowVipModal(false);
        alert(`🎉 支付成功（${paymentMethod === 'wechat' ? '微信' : paymentMethod === 'alipay' ? '支付宝' : '余额'}）`);
        fetchUserInfo();
      } else if (payResult.success && !payResult.newlyPaid) {
        alert(payResult.message || '订单处理中，请稍后在会员中心查看状态');
      } else {
        alert(payResult.error || '支付失败，请重试');
      }
    } catch (e) {
      console.error('订阅支付失败:', e);
      const detail = e?.body ? String(e.body).slice(0, 500) : (e?.message || '');
      alert(`请求失败\n${detail || '请 F12 打开控制台查看 Network 里 /api/payment/ 的响应'}`);
    } finally {
      setMembershipPayLoading(false);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(`${inviteCode} - 云端问道AI心灵导师邀请您`);
    alert('邀请码已复制！分享给好友双方都能获得奖励');
  };

  const shareResult = (platform) => {
    if (platform === 'wechat') {
      alert('请截图或复制链接分享到微信');
      return;
    }
    if (platform !== 'copy') return;

    if (result?.type === 'mbti_meme') {
      const line = `【云端问道·趣味梗型】${result.code} ${result.title}\n${result.tagline || ''}\n${(result.traits || []).join(' · ')}\n（娱乐向，非专业心理测评）`;
      navigator.clipboard.writeText(line);
      alert('分享文案已复制！');
      return;
    }

    const mapType = (t) => {
      if (t === 'bazi') return 'bazi';
      if (t === 'daily') return 'daily';
      if (t === 'dream') return 'dream';
      if (t === 'matchmaking') return 'matchmaking';
      if (t === 'name') return 'name';
      if (t === 'date') return 'date';
      if (t === 'mbti') return 'mbti';
      if (t === 'aura') return 'aura';
      if (t === 'face') return 'face';
      if (t === 'fengshui') return 'fengshui';
      return '';
    };

    const shareType = mapType(result?.type);
    if (!shareType) {
      const text = `我在云端问道体验了一次内容生成，快来试试吧！`;
      navigator.clipboard.writeText(text);
      alert('分享文案已复制！');
      return;
    }

    requestApi('/share/create', {
      method: 'POST',
      data: { userId, type: shareType, payload: result }
    })
      .then(async (r) => {
        if (!r?.success || !r?.url) throw new Error('share failed');
        await navigator.clipboard.writeText(r.url);
        alert('分享链接已复制！');
      })
      .catch(() => {
        alert('生成分享失败，请稍后再试');
      });
  };

  // ==================== 导航配置（五大分类）====================
  const isAdminPath = window.location.pathname === '/admin-login';
  
  // 1. 命理测算
  const destinyNav = [
    { id: 'bazi', label: '八字批解', icon: '🧬' },
    { id: 'face', label: '面相探微', icon: '👤' },
    { id: 'fengshui', label: '风水顾问', icon: '🏠' },
  ];
  
  // 2. 运势指引
  const fortuneNav = [
    { id: 'daily', label: '每日运势', icon: '📆' },
    { id: 'mbti', label: '性格测试', icon: '🎯' },
    { id: 'aura', label: '气场扫描', icon: '✨' },
    { id: 'dream', label: '周公解梦', icon: '🌙' },
  ];
  
  // 3. 生活定制
  const lifeNav = [
    { id: 'name', label: '宝宝取名', icon: '👶' },
    { id: 'date', label: '良辰吉日', icon: '📅' },
  ];
  
  // 4. 心灵陪伴
  const companionNav = [
    { id: 'spirit', label: '灵兽传音', icon: '🐉' },
    { id: 'chat', label: '暖愈心愈', icon: '🤖' },
    { id: 'matchmaking', label: '缘分合盘', icon: '💞' },
  ];
  
  // 5. 赛博玄学
  const cyberNav = [
    { id: 'talisman', label: '赛博灵符', icon: '🔮' },
    { id: 'yunxiang', label: '云香祈福', icon: '🕯️' },
  ];
  
  // 主导航
  const mainNav = [
    { id: 'home', label: '首页', icon: '🏠' },
    ...(isAdminPath ? [{ id: 'adminPortal', label: '后台登录', icon: '🛡️' }] : []),
  ];

  // ==================== 渲染 ====================
  if (isAdminPath) {
    return (
      <div className="admin-standalone">
        <div className="admin-standalone-card">
          <h1 className="panel-title" style={{ marginBottom: 10 }}>🛡️ NEO 管理后台</h1>
          <p className="panel-subtitle" style={{ marginBottom: 24 }}>仅内部可见，展示运营数据与支付数据</p>
          {!adminPortalData ? (
            <div className="result-card">
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">管理员密码</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="请输入管理员密码"
                  value={adminPortalCode}
                  onChange={(e) => setAdminPortalCode(e.target.value)}
                />
              </div>
              <button className="btn-primary" style={{ marginTop: 0 }} onClick={loginAdminPortal}>
                登录后台
              </button>
              {adminPortalError && <p className="error-text" style={{ marginTop: 12 }}>{adminPortalError}</p>}
            </div>
          ) : (
            <>
              <div className="stats-grid">
                <div className="stat-card"><span className="stat-num">{adminPortalData.totalUsers || 0}</span><span>总用户</span></div>
                <div className="stat-card"><span className="stat-num">{adminPortalData.vipCount || 0}</span><span>VIP用户</span></div>
                <div className="stat-card"><span className="stat-num">{adminPortalData.totalCalls || 0}</span><span>总调用</span></div>
                <div className="stat-card"><span className="stat-num">¥{adminPortalData.platformRevenue || adminPortalData.totalRevenue || 0}</span><span>平台总收入(历史)</span></div>
                <div className="stat-card"><span className="stat-num">¥{adminPortalData.paymentRevenue || 0}</span><span>支付流水收入</span></div>
              </div>
              {adminPortalPayStats && (
                <div className="result-card" style={{ marginTop: 24 }}>
                  <h4 style={{ marginBottom: 12 }}>💰 支付统计</h4>
                  <p>流水总收入：¥{adminPortalPayStats.totalRevenue || 0}</p>
                  <p>订单总数：{adminPortalPayStats.totalOrders || 0}</p>
                  <p>已支付：{adminPortalPayStats.totalPaid || 0}</p>
                  <p>今日收入：¥{adminPortalPayStats.todayRevenue || 0}</p>
                  <p>本月收入：¥{adminPortalPayStats.thisMonthRevenue || 0}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <ParticleBackground />
      
      {/* 简化头部 */}
      <header className="header">
        <div
          className="logo"
          title="按住 Shift 双击可进入后台"
          onDoubleClick={(e) => {
            if (e.shiftKey) setActiveTab('adminPortal');
          }}
        >
          <span className="logo-icon">◈</span>
          <span ref={logoTextRef} className="logo-text">{BRAND_NAME}</span>
        </div>

        {/* 公告跑马灯 - 在 logo 和右侧之间 */}
        <div style={{
          flex: 1,
          overflow: 'hidden',
          padding: '0 20px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'inline-block',
            whiteSpace: 'nowrap',
            animation: 'marqueeScroll 22s linear infinite',
            color: '#a0a0ff',
            fontSize: '13px'
          }}>
            🎊 云端问道正式上线 · AI心灵陪伴 · 文化体验工具集，登录即享免费体验次数 · &nbsp;&nbsp;&nbsp;&nbsp;
            <span>🎊 云端问道正式上线 · AI心灵陪伴 · 文化体验工具集，登录即享免费体验次数 · &nbsp;&nbsp;&nbsp;&nbsp;</span>
          </div>
          <style>{`
            @keyframes marqueeScroll {
              0% { transform: translateX(100%); }
              100% { transform: translateX(-100%); }
            }
          `}</style>
        </div>

        <div className="header-right">
          {userInfo?.isVip ? (
            <div className="vip-status" onClick={() => setShowVipModal(true)}>
              <span className="crown">👑</span>
              <span>VIP</span>
            </div>
          ) : (
            <div className="free-credits">
              <span>剩余:</span>
              <span className="count">{userInfo?.freeCalls?.bazi || 3}次</span>
            </div>
          )}
          {authToken ? (
            <div className="free-credits">
              <span>已登录:</span>
              <span className="count">{authPhone || '用户'}</span>
            </div>
          ) : (
            <div className="free-credits">
              <span>未登录</span>
            </div>
          )}
          {authToken && (
            <button className="admin-btn" onClick={handleLogout}>退出</button>
          )}
          <button className="admin-btn" onClick={() => setActiveTab('my')}>我的</button>
        </div>
      </header>

      <div className="app-layout">
        {/* 左侧边栏导航 - 五大分类（可收起为图标栏，方案 C） */}
        <aside className={`sidebar${sidebarCollapsed ? ' sidebar--collapsed' : ''}`}>
          <div className="sidebar-toolbar">
            <button
              type="button"
              className="sidebar-collapse-btn"
              onClick={toggleSidebarCollapsed}
              title={sidebarCollapsed ? '展开导航' : '收起导航'}
              aria-expanded={!sidebarCollapsed}
            >
              {sidebarCollapsed ? '▶' : '◀'}
            </button>
          </div>
          {/* 首页 */}
          <div className="sidebar-section">
            <div className="sidebar-title">🏠 首页</div>
            {mainNav.map(tab => (
              <button 
                key={tab.id} 
                type="button"
                className={`sidebar-item ${activeTab === tab.id ? 'active' : ''}`} 
                title={sidebarCollapsed ? `${tab.label}` : undefined}
                onClick={() => { setActiveTab(tab.id); setResult(null); setError(null); }}
              >
                <span className="sidebar-icon">{tab.icon}</span>
                <span className="sidebar-label">{tab.label}</span>
              </button>
            ))}
          </div>
          
          {/* 1. 命理测算 */}
          <div className="sidebar-section">
            <div className="sidebar-title">🔮 命理测算</div>
            {destinyNav.map(tab => (
              <button 
                key={tab.id} 
                type="button"
                className={`sidebar-item ${activeTab === tab.id ? 'active' : ''}`} 
                title={sidebarCollapsed ? `命理 · ${tab.label}` : undefined}
                onClick={() => { setActiveTab(tab.id); setResult(null); setError(null); }}
              >
                <span className="sidebar-icon">{tab.icon}</span>
                <span className="sidebar-label">{tab.label}</span>
              </button>
            ))}
          </div>
          
          {/* 2. 心灵陪伴 */}
          <div className="sidebar-section section-highlight">
            <div className="sidebar-title">💕 心灵陪伴</div>
            {companionNav.map(tab => (
              <button 
                key={tab.id} 
                type="button"
                className={`sidebar-item ${activeTab === tab.id ? 'active' : ''}`} 
                title={sidebarCollapsed ? `陪伴 · ${tab.label}` : undefined}
                onClick={() => { setActiveTab(tab.id); setResult(null); setError(null); }}
              >
                <span className="sidebar-icon">{tab.icon}</span>
                <span className="sidebar-label">{tab.label}</span>
              </button>
            ))}
          </div>
          
          {/* 3. 运势指引 */}
          <div className="sidebar-section">
            <div className="sidebar-title">✨ 运势指引</div>
            {fortuneNav.map(tab => (
              <button 
                key={tab.id} 
                type="button"
                className={`sidebar-item ${activeTab === tab.id ? 'active' : ''}`} 
                title={sidebarCollapsed ? `运势 · ${tab.label}` : undefined}
                onClick={() => { setActiveTab(tab.id); setResult(null); setError(null); }}
              >
                <span className="sidebar-icon">{tab.icon}</span>
                <span className="sidebar-label">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* 4. 生活定制 */}
          <div className="sidebar-section">
            <div className="sidebar-title">🎁 生活定制</div>
            {lifeNav.map(tab => (
              <button 
                key={tab.id} 
                type="button"
                className={`sidebar-item ${activeTab === tab.id ? 'active' : ''}`} 
                title={sidebarCollapsed ? `生活 · ${tab.label}` : undefined}
                onClick={() => { setActiveTab(tab.id); setResult(null); setError(null); }}
              >
                <span className="sidebar-icon">{tab.icon}</span>
                <span className="sidebar-label">{tab.label}</span>
              </button>
            ))}
          </div>
          
          {/* 5. 赛博玄学 */}
          <div className="sidebar-section section-cyber">
            <div className="sidebar-title">🌐 赛博玄学</div>
            {cyberNav.map(tab => (
              <button 
                key={tab.id} 
                type="button"
                className={`sidebar-item ${activeTab === tab.id ? 'active' : ''}`} 
                title={sidebarCollapsed ? `赛博 · ${tab.label}` : undefined}
                onClick={() => { setActiveTab(tab.id); setResult(null); setError(null); }}
              >
                <span className="sidebar-icon">{tab.icon}</span>
                <span className="sidebar-label">{tab.label}</span>
              </button>
            ))}
          </div>

          <div
            className="sidebar-vip"
            onClick={() => setShowVipModal(true)}
            title={sidebarCollapsed ? (userInfo?.isVip ? '会员中心' : '开通 VIP') : undefined}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowVipModal(true); } }}
          >
            <div className="sidebar-vip-title">{userInfo?.isVip ? '👑 会员中心' : '🌟 开通VIP'}</div>
            <div className="sidebar-vip-price">{userInfo?.isVip ? '管理套餐' : '¥188/年起'}</div>
            <div className="sidebar-vip-desc">{userInfo?.isVip ? '升级/换套餐/续费' : '解锁全部功能'}</div>
          </div>
        </aside>

        {/* 主内容区 */}
        <main
          className={`main-content${sidebarCollapsed ? ' main-content--sidebar-narrow' : ''}`}
          style={{
            marginTop: showAnnouncement && announcement ? '22px' : '2px'
          }}
        >
        {analysisOverlayActive &&
          !showPaywall &&
          !showVipModal &&
          !membershipPayLoading &&
          !talismanPayLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner" />
            <span>{pickAnalysisOverlayText()}</span>
          </div>
        )}
        
        {showPaywall && (
          <PaywallModal 
            info={paywallInfo} 
            onClose={() => setShowPaywall(false)} 
            onSubscribe={handleSubscribe}
            subscribePayLoading={membershipPayLoading}
          />
        )}
        
        {showVipModal && (
          <VipDetailModal 
            onClose={() => setShowVipModal(false)} 
            onSubscribe={handleSubscribe}
            userInfo={userInfo}
            subscribePayLoading={membershipPayLoading}
          />
        )}

        {vipWechatPay && (
          <div className="modal-overlay" style={{ zIndex: 12000 }}>
            <div className="modal" style={{ maxWidth: 420, textAlign: 'center' }}>
              <div className="modal-header"><span>微信支付</span></div>
              <div className="modal-body">
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>
                  请使用微信扫描下方二维码完成付款；支付成功后一般几秒内自动开通，也可手动刷新。
                </p>
                {vipWechatPay.qrDataUrl ? (
                  <img
                    src={vipWechatPay.qrDataUrl}
                    alt="微信支付"
                    style={{ width: 240, height: 240, objectFit: 'contain', marginBottom: 12 }}
                  />
                ) : (
                  <p style={{ fontSize: 12, color: 'var(--text-tertiary)', wordBreak: 'break-all' }}>
                    {vipWechatPay.codeUrl}
                  </p>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>订单号：{vipWechatPay.orderId}</p>
                <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={async () => {
                      try {
                        const st = await requestApi(`/payment/order/${encodeURIComponent(vipWechatPay.orderId)}`);
                        if (st.success && st.order?.status === 'paid') {
                          setVipWechatPay(null);
                          setShowPaywall(false);
                          setShowVipModal(false);
                          await fetchUserInfo();
                          alert('会员已开通');
                        } else {
                          alert('尚未收到支付成功通知，请确认已付款或稍后再试');
                        }
                      } catch (e) {
                        alert('查询失败');
                      }
                    }}
                  >
                    我已完成支付
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setVipWechatPay(null)}
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {talismanWechatPay && (
          <div className="modal-overlay" style={{ zIndex: 12001 }}>
            <div className="modal" style={{ maxWidth: 420, textAlign: 'center' }}>
              <div className="modal-header"><span>灵符 · 微信支付</span></div>
              <div className="modal-body">
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>
                  请使用微信扫码完成付款；支付成功后无水印壁纸将自动解锁。
                </p>
                {talismanWechatPay.qrDataUrl ? (
                  <img
                    src={talismanWechatPay.qrDataUrl}
                    alt="微信支付"
                    style={{ width: 240, height: 240, objectFit: 'contain', marginBottom: 12 }}
                  />
                ) : (
                  <p style={{ fontSize: 12, color: 'var(--text-tertiary)', wordBreak: 'break-all' }}>
                    {talismanWechatPay.codeUrl}
                  </p>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>订单号：{talismanWechatPay.orderId}</p>
                <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={async () => {
                      try {
                        const st = await requestApi(`/payment/order/${encodeURIComponent(talismanWechatPay.orderId)}`);
                        if (st.success && st.order?.status === 'paid') {
                          const prev = talismanPreviewRef.current;
                          if (prev && drawTalismanRef.current) {
                            const u = { ...prev, paid: true, watermark: false };
                            await drawTalismanRef.current(u);
                            setTalismanPreview(u);
                          }
                          setTalismanWechatPay(null);
                          setTalismanOrder(null);
                          sessionStorage.removeItem('pendingTalismanOrderId');
                          alert('灵符已解锁');
                        } else {
                          alert('尚未收到支付成功通知，请确认已付款或稍后再试');
                        }
                      } catch (e) {
                        alert('查询失败');
                      }
                    }}
                  >
                    我已完成支付
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setTalismanWechatPay(null);
                      setTalismanOrder(null);
                      sessionStorage.removeItem('pendingTalismanOrderId');
                    }}
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {showAdmin && <AdminModal onClose={() => setShowAdmin(false)} />}

        {/* 首页 */}
        {activeTab === 'home' && (
          <div className="home-container">
            {/* 欢迎区域 */}
            <div className="home-welcome">
              <div className="home-welcome-text">
                <h1>{authPhone || authToken ? '欢迎回来' : '开启你的问道之旅'}</h1>
                <p>
                  {`今天是 ${new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}`}
                  {authPhone ? ` · 手机尾号 ${authPhone.slice(-4)}` : !authToken ? ' · 登录可同步云端记录' : ''}
                </p>
              </div>
              <div className="home-quick-action">
                <button type="button" className="btn-primary" onClick={() => setActiveTab('daily')}>
                  📅 查看今日运势
                </button>
              </div>
            </div>

            {/* 今日运势：静态示例，避免误认为已生成个人运势 */}
            <div className="home-today-card" onClick={() => setActiveTab('daily')}>
              <div className="home-today-header">
                <span className="home-today-badge">今日运势</span>
                <span className="home-today-sample-pill">示例预览</span>
                <span className="home-today-date">{new Date().getMonth() + 1}月{new Date().getDate()}日</span>
              </div>
              <div className="home-today-content">
                <div className="home-today-score">
                  <div className="home-today-num">85</div>
                  <div className="home-today-label">综合运势</div>
                </div>
                <div className="home-today-info">
                  <div className="home-today-item">
                    <span className="home-today-icon">🎨</span>
                    <span>幸运色：金色</span>
                  </div>
                  <div className="home-today-item">
                    <span className="home-today-icon">🔢</span>
                    <span>幸运数：3、7</span>
                  </div>
                  <div className="home-today-item">
                    <span className="home-today-icon">🧭</span>
                    <span>吉方：东南</span>
                  </div>
                </div>
              </div>
              <div className="home-today-tip">
                💡 今日宜签约、会友，把握上午9-11点黄金时段
              </div>
              <p className="home-today-disclaimer">
                上图为样式示意。个人化宜忌与评分请在「每日运势」中生成，结果仅供娱乐与文化参考。
              </p>
            </div>

            {/* 1. 命理测算 */}
            <div className="home-section">
              <h3 className="home-section-title">🔮 命理测算</h3>
              <div className="home-features-row">
                <div className="home-feature-item" onClick={() => setActiveTab('bazi')}>
                  <HomeFeatureQuotaBadge tabId="bazi" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #6C5DD3 0%, #8B7FE8 100%)'}}>🧬</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">八字批解</div>
                    <div className="home-feature-desc">精准排盘·五行解读</div>
                  </div>
                </div>
                <div className="home-feature-item" onClick={() => setActiveTab('face')}>
                  <HomeFeatureQuotaBadge tabId="face" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #795548 0%, #A1887F 100%)'}}>👤</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">面相探微</div>
                    <div className="home-feature-desc">探微解读·三停五官</div>
                  </div>
                </div>

                <div className="home-feature-item" onClick={() => setActiveTab('fengshui')}>
                  <HomeFeatureQuotaBadge tabId="fengshui" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #2196F3 0%, #00C9FF 100%)'}}>🏠</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">风水顾问</div>
                    <div className="home-feature-desc">布局分析·环境优化</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. 心灵陪伴 */}
            <div className="home-section section-highlight">
              <h3 className="home-section-title">💕 心灵陪伴</h3>
              <div className="home-features-row">
                <div className="home-feature-item" onClick={() => setActiveTab('spirit')}>
                  <HomeFeatureQuotaBadge tabId="spirit" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #FF69B4 0%, #FF1493 100%)'}}>🐉</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">灵兽传音</div>
                    <div className="home-feature-desc">娱乐向灵兽扮演 · 语音互怼</div>
                  </div>
                </div>
                <div className="home-feature-item" onClick={() => setActiveTab('chat')}>
                  <HomeFeatureQuotaBadge tabId="chat" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #00C9FF 0%, #00D68F 100%)'}}>🤖</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">暖愈心愈</div>
                    <div className="home-feature-desc">温柔倾听·情绪陪伴（非执业咨询）</div>
                  </div>
                </div>
                <div className="home-feature-item" onClick={() => setActiveTab('matchmaking')}>
                  <HomeFeatureQuotaBadge tabId="matchmaking" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #FF69B4 0%, #8B7FE8 100%)'}}>💞</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">缘分合盘</div>
                    <div className="home-feature-desc">双人合盘·沟通建议</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. 运势指引 */}
            <div className="home-section">
              <h3 className="home-section-title">✨ 运势指引</h3>
              <div className="home-features-row">
                <div className="home-feature-item" onClick={() => setActiveTab('daily')}>
                  <HomeFeatureQuotaBadge tabId="daily" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'}}>📆</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">每日运势</div>
                    <div className="home-feature-desc">今日运程·吉凶宜忌</div>
                  </div>
                </div>
                <div className="home-feature-item" onClick={() => setActiveTab('mbti')}>
                  <HomeFeatureQuotaBadge tabId="mbti" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #9B59B6 0%, #E91E63 100%)'}}>🎯</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">性格测试</div>
                    <div className="home-feature-desc">MBTI·性格命理</div>
                  </div>
                </div>
                <div className="home-feature-item" onClick={() => setActiveTab('aura')}>
                  <HomeFeatureQuotaBadge tabId="aura" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #E91E63 0%, #9B59B6 100%)'}}>✨</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">气场扫描</div>
                    <div className="home-feature-desc">能量分析·色彩解读</div>
                  </div>
                </div>
                <div className="home-feature-item" onClick={() => setActiveTab('dream')}>
                  <HomeFeatureQuotaBadge tabId="dream" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #4A3FBF 0%, #6C5DD3 100%)'}}>🌙</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">周公解梦</div>
                    <div className="home-feature-desc">梦境解析·寓意解读</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. 生活定制 */}
            <div className="home-section">
              <h3 className="home-section-title">🎁 生活定制</h3>
              <div className="home-features-row">
                <div className="home-feature-item" onClick={() => setActiveTab('name')}>
                  <HomeFeatureQuotaBadge tabId="name" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)'}}>👶</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">宝宝取名</div>
                    <div className="home-feature-desc">八字起名·五行平衡</div>
                  </div>
                </div>
                <div className="home-feature-item" onClick={() => setActiveTab('date')}>
                  <HomeFeatureQuotaBadge tabId="date" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #4ECDC4 0%, #44A3AA 100%)'}}>📅</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">良辰吉日</div>
                    <div className="home-feature-desc">择吉选日·黄历查询</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 5. 赛博玄学 */}
            <div className="home-section section-cyber">
              <h3 className="home-section-title">🌐 赛博玄学</h3>
              <p style={{ fontSize: 12, opacity: 0.78, margin: '-6px 0 14px', lineHeight: 1.5 }}>
                灵符：可保存的壁纸与心愿仪式感 · 暖愈心愈：情绪倾诉 · 灵兽：娱乐语音互怼
              </p>
              <div className="home-features-row">
                <div className="home-feature-item" onClick={() => setActiveTab('talisman')}>
                  <HomeFeatureQuotaBadge tabId="talisman" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #00FFFF 0%, #0080FF 100%)'}}>🔮</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">赛博灵符</div>
                    <div className="home-feature-desc">AI壁纸·文化寓意·非功效承诺</div>
                  </div>
                </div>
                <div className="home-feature-item" onClick={() => setActiveTab('yunxiang')}>
                  <HomeFeatureQuotaBadge tabId="yunxiang" userInfo={userInfo} />
                  <div className="home-feature-icon" style={{background: 'linear-gradient(135deg, #FFD59A 0%, #C45C5C 100%)'}}>🕯️</div>
                  <div className="home-feature-text">
                    <div className="home-feature-name">云香祈福</div>
                    <div className="home-feature-desc">心香仪式·节令文·正念提醒</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 使用统计（与账号相关） */}
            <div className="home-stats-row">
              <div className="home-stat-item">
                <div className="home-stat-num">{userInfo?.freeCalls?.bazi ?? '—'}</div>
                <div className="home-stat-label">八字等试用剩余</div>
              </div>
              <div className="home-stat-item">
                <div className="home-stat-num home-stat-num--small">
                  {userInfo?.isVip && userInfo?.subscription?.expiresAt
                    ? new Date(userInfo.subscription.expiresAt).toLocaleDateString('zh-CN', { year: 'numeric', month: 'numeric', day: 'numeric' })
                    : userInfo?.isVip ? '已开通' : '未开通'}
                </div>
                <div className="home-stat-label">会员到期</div>
              </div>
              <div className="home-stat-item">
                <div className="home-stat-num">{userInfo?.historyCount ?? 0}</div>
                <div className="home-stat-label">分析记录条数</div>
              </div>
            </div>

            <footer className="home-site-footer">
              <p className="home-site-footer-line">
                云端问道 · AI 心灵与文化工具 · 结果仅供娱乐与文化参考，不构成医疗、投资或法律建议
              </p>
              <p className="home-site-footer-line">
                备案号：
                {' '}
                <a
                  href="https://beian.miit.gov.cn/"
                  target="_blank"
                  rel="noreferrer"
                  className="home-site-footer-link"
                >
                  浙ICP备2026022265号
                </a>
              </p>
              <div className="home-site-footer-links">
                <button type="button" className="home-site-footer-link" onClick={() => setActiveTab('privacy')}>
                  隐私政策
                </button>
                <span className="home-site-footer-sep" aria-hidden="true">|</span>
                <button type="button" className="home-site-footer-link" onClick={() => setActiveTab('terms')}>
                  用户协议
                </button>
              </div>
            </footer>
          </div>
        )}

        {activeTab === 'privacy' && (
          <LegalDocumentView kind="privacy" onBack={() => setActiveTab('home')} />
        )}
        {activeTab === 'terms' && (
          <LegalDocumentView kind="terms" onBack={() => setActiveTab('home')} />
        )}

        {/* 八字批解 */}
        {activeTab === 'bazi' && (
          <div className="panel">
            <h2 className="panel-title">八字命盘批解</h2>
            <p className="panel-subtitle">基于AI大模型的专业八字解读</p>

            <div style={{ marginBottom: 18, padding: 14, borderRadius: 12, border: '1px dashed var(--card-border)', fontSize: 12, lineHeight: 1.65, color: 'var(--text-tertiary)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>说明：</strong>
              四柱排盘依据您填写的公历出生时间；出生日期须不晚于今天，若选「今天」则时辰不可晚于当前钟点。命局强弱、喜忌等为系统内简化规则，长文解读由AI生成。结果仅供娱乐与文化自我觉察参考，不构成医疗、投资、人事或法律决策依据，健康相关请遵医嘱并及时体检。
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">快速选择出生日期</label>
              <input
                type="date"
                className="form-input"
                value={buildBaziDateInputValue(baziForm)}
                max={baziFormLimits.todayYmd}
                onChange={e => {
                  const v = e.target.value;
                  if (!v) return;
                  const [year, month, day] = v.split('-').map(Number);
                  if (!year || !month || !day) return;
                  setBaziForm({ ...baziForm, year, month, day });
                }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>也可在下方直接输入年、月、日数字</span>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">出生年份</label>
                <input type="number" className="form-input" value={baziForm.year} min="1900" max={currentYear}
                  onWheel={e => e.currentTarget.blur()}
                  onChange={e => setBaziForm({...baziForm, year: +e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">出生月份</label>
                <input type="number" className="form-input" value={baziForm.month} min="1" max={baziFormLimits.maxMonth}
                  onWheel={e => e.currentTarget.blur()}
                  onChange={e => setBaziForm({...baziForm, month: +e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">出生日期</label>
                <input type="number" className="form-input" value={baziForm.day} min="1" max={baziFormLimits.maxDay}
                  onWheel={e => e.currentTarget.blur()}
                  onChange={e => setBaziForm({...baziForm, day: +e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">出生时辰</label>
                <select className="form-input" value={baziForm.hour}
                  onChange={e => setBaziForm({...baziForm, hour: +e.target.value})}>
                  {[...Array(baziFormLimits.maxHour + 1)].map((_, i) => (
                    <option key={i} value={i}>{i}:00 ({getShiChen(i)})</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">性别</label>
              <div className="tab-selector">
                <button className={`tab-option ${baziForm.gender === 'male' ? 'active' : ''}`}
                  onClick={() => setBaziForm({...baziForm, gender: 'male'})}>男</button>
                <button className={`tab-option ${baziForm.gender === 'female' ? 'active' : ''}`}
                  onClick={() => setBaziForm({...baziForm, gender: 'female'})}>女</button>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">姓名（可选）</label>
              <input type="text" className="form-input" placeholder="输入姓名便于称呼与个性化解读"
                value={baziForm.name} onChange={e => setBaziForm({...baziForm, name: e.target.value})} />
            </div>
            
            <button className="btn-primary" onClick={handleBazi} disabled={baziLoading}>
              <span>◈</span> {baziLoading ? '八字批解分析中…' : '开始八字批解'}
            </button>
            
            {error && <div className="error-text" style={{ marginTop: 16, textAlign: 'center' }}>{error}</div>}
            
            {result?.type === 'bazi' && <BaziResult data={result} onShare={() => shareResult('copy')} />}
          </div>
        )}

        {/* 暖愈心愈 */}
        {activeTab === 'chat' && (
          <div className="panel chat-panel">
            <h2 className="panel-title">暖愈心愈 · 你的情绪陪伴伙伴</h2>
            <p className="panel-subtitle">倾诉烦恼 · 缓解焦虑 · 支持性倾听 · 深夜树洞</p>
            <p className="panel-subtitle" style={{ fontSize: 11, opacity: 0.78, marginTop: -6, marginBottom: 10, lineHeight: 1.55 }}>
              本功能为AI生成的情绪陪伴与自助疏导，不构成医疗诊断、心理咨询或法律服务，也不能替代持证心理咨询师或医疗机构。若你长期情绪痛苦、有自伤等想法，请立即联系亲友或拨打心理援助热线。
            </p>
            
            <div className="chat-container">
              <div className="chat-messages">
                {chatMessages.length === 0 && (
                  <div className="chat-welcome">
                    <div className="neo-avatar">◈</div>
                    <p style={{ fontSize: 18, marginBottom: 8 }}>你好，我是 NEO，在这陪你聊心情与压力</p>
                    <p style={{ fontSize: 14 }}>我和「灵兽传音」不同：这边走温柔倾听路线；想排盘、看流年、日运，请用首页的八字、每日运势等功能。</p>
                    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>可以试试这样开头：</p>
                      {['最近压力好大，想聊聊', '睡不着，脑子一直转', '心情低落但说不清原因'].map((q, i) => (
                        <button key={i} className="btn-secondary" style={{ fontSize: 13, padding: '10px 16px' }}
                          onClick={() => { setChatInput(q); }}>{q}</button>
                      ))}
                    </div>
                  </div>
                )}
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`chat-message ${msg.role}`}>
                    {msg.role === 'neo' && <span className="msg-avatar">◈</span>}
                    {msg.role === 'neo' ? (
                      <div className="msg-bubble msg-bubble-formatted">
                        {formatAiChatContent(msg.content).map((line, idx) => (
                          <p
                            key={`${i}-${idx}`}
                            className={`msg-line ${/^[1-9]\d*[、.．]/.test(line) ? 'numbered' : ''}`}
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <div className="msg-bubble">{msg.content}</div>
                    )}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              
              <div className="chat-input-area">
                <input 
                  type="text" 
                  className="form-input chat-input" 
                  placeholder="说说此刻的心情或困扰…" 
                  value={chatInput} 
                  onChange={e => setChatInput(e.target.value)} 
                  onKeyPress={e => e.key === 'Enter' && handleChat()} 
                />
                <button className="btn-send" onClick={handleChat}>发送</button>
              </div>
            </div>
            <div className="chat-panel-footer">
              <div className="chat-panel-footer-actions">
                <button
                  className="btn-secondary"
                  style={{ fontSize: 12, padding: '8px 12px' }}
                  onClick={async () => {
                    const lastNeo = [...chatMessages].reverse().find(m => m.role === 'neo');
                    const lastUser = [...chatMessages].reverse().find(m => m.role !== 'neo');
                    if (!lastNeo?.content) {
                      alert('还没有可分享的回复');
                      return;
                    }
                    try {
                      const r = await requestApi('/share/create', {
                        method: 'POST',
                        body: JSON.stringify({
                          userId,
                          type: 'chat',
                          payload: { userText: lastUser?.content || '', reply: lastNeo.content }
                        })
                      });
                      const url = r?.url;
                      if (!url) throw new Error('share url missing');
                      await navigator.clipboard.writeText(url);
                      alert('分享链接已复制');
                    } catch (e) {
                      alert('生成分享链接失败，请稍后重试');
                    }
                  }}
                >
                  生成分享链接
                </button>
              </div>
              <p className="panel-subtitle chat-panel-safety">
                安全提示：若你有自伤、自杀想法或处于紧急危险中，请拨打 400-161-9995（全国心理援助热线）或当地 110/120；本对话仅为 AI 陪伴，请务必向真人专业人士求助。
              </p>
            </div>
          </div>
        )}

        {/* 周公解梦 */}
        {activeTab === 'dream' && (
          <div className="panel">
            <h2 className="panel-title">周公解梦 · 专业版</h2>
            <p className="panel-subtitle">结合东方传统梦境学与现代心理学</p>
            <p className="panel-subtitle" style={{ fontSize: 11, opacity: 0.75, marginTop: -8, marginBottom: 12, lineHeight: 1.5 }}>
              本解读仅供文化兴趣与心理自助参考，不构成医疗、心理咨询或命运断言。若长期失眠、反复噩梦或伴随强烈痛苦，建议咨询专业医生或心理咨询师。
            </p>

            {/* 用户基本信息 */}
            <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>👤 您的基本信息</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">性别</label>
                  <div className="tab-selector">
                    <button className={`tab-option ${dreamForm.gender === 'male' ? 'active' : ''}`}
                      onClick={() => setDreamForm({...dreamForm, gender: 'male'})}>男</button>
                    <button className={`tab-option ${dreamForm.gender === 'female' ? 'active' : ''}`}
                      onClick={() => setDreamForm({...dreamForm, gender: 'female'})}>女</button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">年龄范围</label>
                  <select className="form-input" value={dreamForm.age}
                    onChange={e => setDreamForm({...dreamForm, age: e.target.value})}>
                    <option value="20以下">18-20岁</option>
                    <option value="25">21-25岁</option>
                    <option value="30">26-30岁</option>
                    <option value="35">31-35岁</option>
                    <option value="40">36-40岁</option>
                    <option value="45">41-45岁</option>
                    <option value="50">46-50岁</option>
                    <option value="50以上">50岁以上</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 梦境时间 */}
            <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>⏰ 梦境时间</div>
              <div className="form-group">
                <label className="form-label">做梦时间段</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { id: '前半夜', label: '前半夜(19-23点)' },
                    { id: '子时', label: '子时(23-1点)' },
                    { id: '丑时', label: '丑时(1-3点)' },
                    { id: '寅时', label: '寅时(3-5点)' },
                    { id: '卯时', label: '卯时(5-7点)' },
                    { id: '清晨', label: '清晨(5-7点)' },
                    { id: '午睡', label: '午睡时' },
                  ].map(item => (
                    <button key={item.id} className={`tab-option ${dreamForm.dreamTime === item.id ? 'active' : ''}`}
                      onClick={() => setDreamForm({...dreamForm, dreamTime: item.id})}>{item.label}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* 梦境分类 */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">梦境主题分类</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { id: '不限', label: '不限' },
                  { id: '人物', label: '人物类' },
                  { id: '动物', label: '动物类' },
                  { id: '场景', label: '场景类' },
                  { id: '物品', label: '物品类' },
                  { id: '行为', label: '行为类' },
                  { id: '自然灾害', label: '自然类' },
                  { id: '超自然', label: '超自然' },
                ].map(item => (
                  <button key={item.id} className={`tab-option ${dreamForm.symbolType === item.id ? 'active' : ''}`}
                    onClick={() => setDreamForm({...dreamForm, symbolType: item.id})}>{item.label}</button>
                ))}
              </div>
            </div>

            {/* 梦境内容 */}
            <div className="form-group">
              <label className="form-label">详细描述梦境</label>
              <textarea
                className="form-input"
                style={{ minHeight: 140, resize: 'vertical' }}
                placeholder="请尽可能详细描述您的梦境，包括：&#10;• 梦到了什么场景&#10;• 有哪些人物/动物出现&#10;• 发生了什么重要的事情&#10;• 您的感受如何&#10;• 一些特别的细节..."
                value={dreamForm.content}
                onChange={e => setDreamForm({...dreamForm, content: e.target.value})}
              />
            </div>

            {/* 情绪选择 */}
            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">醒来时的情绪</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { id: '平静', label: '平静', emoji: '😌' },
                  { id: '开心', label: '开心', emoji: '😊' },
                  { id: '焦虑', label: '焦虑', emoji: '😰' },
                  { id: '恐惧', label: '恐惧', emoji: '😱' },
                  { id: '悲伤', label: '悲伤', emoji: '😢' },
                  { id: '困惑', label: '困惑', emoji: '🤔' },
                  { id: '惊讶', label: '惊讶', emoji: '😮' },
                  { id: '兴奋', label: '兴奋', emoji: '🤩' },
                ].map(emotion => (
                  <button key={emotion.id} className={`tab-option ${dreamForm.emotion === emotion.id ? 'active' : ''}`}
                    onClick={() => setDreamForm({...dreamForm, emotion: emotion.id})}>
                    {emotion.emoji} {emotion.label}
                  </button>
                ))}
              </div>
            </div>

            <button className="btn-primary" onClick={handleDream} style={{ marginTop: 20 }}>
              <span>🌙</span> 开始专业解梦
            </button>

            {result?.type === 'dream' && <DreamResult data={result} onShare={() => shareResult('copy')} />}
          </div>
        )}

        {/* 缘分合盘 */}
        {activeTab === 'matchmaking' && (
          <div className="panel">
            <h2 className="panel-title">💞 缘分合盘 · 双人匹配分析</h2>
            <p className="panel-subtitle">输入双方生辰与偏好，获得互补倾向与沟通建议（仅供文化与自我觉察参考）</p>
            <p className="panel-subtitle" style={{ fontSize: 11, opacity: 0.75, marginTop: -8, marginBottom: 12, lineHeight: 1.5 }}>
              本功能不进行真实牵线撮合与联系人引导。报告仅为倾向与建议，不构成婚恋决策依据。
            </p>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">A（你的对象）姓名（可选）</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="例如：张三"
                  value={matchmakingForm?.a?.name || ''}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, a: { ...prev.a, name: e.target.value } }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">A 性别（可选）</label>
                <select
                  className="form-input"
                  value={matchmakingForm?.a?.gender || 'male'}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, a: { ...prev.a, gender: e.target.value } }))}
                >
                  <option value="male">男</option>
                  <option value="female">女</option>
                </select>
              </div>
            </div>

            <div className="form-grid" style={{ marginTop: 14 }}>
              <div className="form-group">
                <label className="form-label">A 出生年份</label>
                <input
                  type="number"
                  className="form-input"
                  min="1900"
                  max={currentYear}
                  value={matchmakingForm?.a?.year || 0}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, a: { ...prev.a, year: +e.target.value } }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">A 出生月份</label>
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  max="12"
                  value={matchmakingForm?.a?.month || 0}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, a: { ...prev.a, month: +e.target.value } }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">A 出生日期</label>
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  max="31"
                  value={matchmakingForm?.a?.day || 0}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, a: { ...prev.a, day: +e.target.value } }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">A 出生时辰（0-23）</label>
                <input
                  type="number"
                  className="form-input"
                  min="0"
                  max="23"
                  value={matchmakingForm?.a?.hour || 0}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, a: { ...prev.a, hour: +e.target.value } }))}
                />
              </div>
            </div>

            <div style={{ marginTop: 18, padding: 14, borderRadius: 12, border: '1px dashed var(--card-border)', color: 'var(--text-tertiary)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>— B（对方）信息 —</strong>
            </div>

            <div className="form-grid" style={{ marginTop: 14 }}>
              <div className="form-group">
                <label className="form-label">B（对方）姓名（可选）</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="例如：李四"
                  value={matchmakingForm?.b?.name || ''}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, b: { ...prev.b, name: e.target.value } }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">B 性别（可选）</label>
                <select
                  className="form-input"
                  value={matchmakingForm?.b?.gender || 'female'}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, b: { ...prev.b, gender: e.target.value } }))}
                >
                  <option value="male">男</option>
                  <option value="female">女</option>
                </select>
              </div>
            </div>

            <div className="form-grid" style={{ marginTop: 14 }}>
              <div className="form-group">
                <label className="form-label">B 出生年份</label>
                <input
                  type="number"
                  className="form-input"
                  min="1900"
                  max={currentYear}
                  value={matchmakingForm?.b?.year || 0}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, b: { ...prev.b, year: +e.target.value } }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">B 出生月份</label>
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  max="12"
                  value={matchmakingForm?.b?.month || 0}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, b: { ...prev.b, month: +e.target.value } }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">B 出生日期</label>
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  max="31"
                  value={matchmakingForm?.b?.day || 0}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, b: { ...prev.b, day: +e.target.value } }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">B 出生时辰（0-23）</label>
                <input
                  type="number"
                  className="form-input"
                  min="0"
                  max="23"
                  value={matchmakingForm?.b?.hour || 0}
                  onWheel={(e) => e.currentTarget.blur()}
                  onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, b: { ...prev.b, hour: +e.target.value } }))}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 18 }}>
              <label className="form-label">目标偏好（可选）</label>
              <select
                className="form-input"
                value={matchmakingForm?.goal || '恋爱与相处'}
                onChange={(e) => setMatchmakingForm((prev) => ({ ...prev, goal: e.target.value }))}
              >
                <option value="恋爱与相处">恋爱与相处</option>
                <option value="求稳定与长久">求稳定与长久</option>
                <option value="更顺畅的沟通">更顺畅的沟通</option>
                <option value="为未来做规划">为未来做规划</option>
              </select>
            </div>

            <button className="btn-primary" onClick={handleMatchmaking} style={{ marginTop: 20 }}>
              <span>💞</span> 开始缘分合盘
            </button>

            {error && <div className="error-text" style={{ marginTop: 16, textAlign: 'center' }}>{error}</div>}

            {result?.type === 'matchmaking' && (
              <div className="result-section matchmaking-result-section">
                <div className="result-card matchmaking-score-card">
                  <div className="result-card-title">缘分合盘指数</div>
                  <div className="result-card-content">
                    {typeof result.compatibility?.score === 'number' ? (
                      <p className="bazi-pro-line matchmaking-score-line">
                        <span className="bazi-pro-label">指数</span>
                        <strong className="matchmaking-score-num">{result.compatibility.score}/100</strong>
                      </p>
                    ) : null}
                    {result.compatibility?.summary ? <p className="matchmaking-summary">{result.compatibility.summary}</p> : null}
                  </div>
                </div>

                {Array.isArray(result.compatibility?.strengths) && result.compatibility.strengths.length > 0 && (
                  <div className="result-card">
                    <div className="result-card-title">互补优势（摘录）</div>
                    <div className="result-card-content">
                      <ul className="bazi-pro-shishen-list">
                        {result.compatibility.strengths.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  </div>
                )}

                {Array.isArray(result.compatibility?.cautions) && result.compatibility.cautions.length > 0 && (
                  <div className="result-card">
                    <div className="result-card-title">潜在摩擦点（摘录）</div>
                    <div className="result-card-content">
                      <ul className="bazi-pro-shishen-list">
                        {result.compatibility.cautions.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                  </div>
                )}

                {result.analysis?.tips?.length > 0 && (
                  <div className="result-card">
                    <div className="result-card-title">破冰沟通锦囊（摘录）</div>
                    <div className="result-card-content">
                      <ul className="bazi-pro-shishen-list">
                        {result.analysis.tips.slice(0, 6).map((t, i) => <li key={i}>{t}</li>)}
                      </ul>
                    </div>
                  </div>
                )}

                {result.analysis?.fullReport ? (
                  <div className="result-card">
                    <div className="result-card-title">完整报告</div>
                    <div className="result-card-content">
                      <div className="yunxiang-result-bless matchmaking-full-report" style={{ whiteSpace: 'pre-wrap' }}>
                        {result.analysis.fullReport}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="share-buttons matchmaking-share-buttons" style={{ marginTop: 18 }}>
                  <button className="btn-share" onClick={() => shareResult('copy')}>
                    <span>🔗</span> 生成分享链接
                  </button>
                </div>

                <div className="usage-info matchmaking-usage">剩余免费次数: {result.remaining > 0 ? result.remaining : 'VIP无限'}</div>
              </div>
            )}
          </div>
        )}

        {/* 宝宝取名 */}
        {activeTab === 'name' && (
          <div className="panel">
            <h2 className="panel-title">宝宝取名大师</h2>
            <p className="panel-subtitle">结合八字五行 · AI智能起名 · 专业排盘</p>
            <p className="panel-subtitle" style={{ fontSize: 11, opacity: 0.75, marginTop: -8, marginBottom: 12, lineHeight: 1.5 }}>
              本推荐仅供文化参考与起名灵感，不构成户籍或命理判决；最终用字请以登记机关与家庭共识为准。下方「喜用对照」为本地简化字库规则提示，非精确评分。
            </p>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">姓氏</label>
                <input type="text" className="form-input" placeholder="如：李"
                  value={nameForm.surname} onChange={e => setNameForm({...nameForm, surname: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">性别</label>
                <select className="form-input" value={nameForm.gender}
                  onChange={e => setNameForm({...nameForm, gender: e.target.value})}>
                  <option value="male">男宝</option>
                  <option value="female">女宝</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: 24, marginBottom: 24 }}>
              <label className="form-label" style={{ marginBottom: 12, display: 'block' }}>生辰八字（精确到时辰）</label>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">出生年份</label>
                  <input type="number" className="form-input" value={nameForm.birthYear} min="1900" max="2030"
                    onChange={e => setNameForm({...nameForm, birthYear: +e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">出生月份</label>
                  <select className="form-input" value={nameForm.birthMonth}
                    onChange={e => setNameForm({...nameForm, birthMonth: +e.target.value})}>
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}月</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">出生日期</label>
                  <select className="form-input" value={nameForm.birthDay}
                    onChange={e => setNameForm({...nameForm, birthDay: +e.target.value})}>
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}日</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">出生时辰</label>
                  <select className="form-input" value={nameForm.birthHour}
                    onChange={e => setNameForm({...nameForm, birthHour: +e.target.value})}>
                    {[...Array(24)].map((_, i) => (
                      <option key={i} value={i}>{i}:00 ({['子','丑','丑','寅','寅','卯','卯','辰','辰','巳','巳','午','午','未','未','申','申','酉','酉','戌','戌','亥','亥','子'][i]}时)</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* 名字长度选择 */}
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">名字长度</label>
              <div className="tab-selector">
                <button 
                  className={`tab-option ${nameForm.nameLength === '2' ? 'active' : ''}`}
                  onClick={() => setNameForm({...nameForm, nameLength: '2'})}
                >
                  双字名（如：李子轩）
                </button>
                <button 
                  className={`tab-option ${nameForm.nameLength === '3' ? 'active' : ''}`}
                  onClick={() => setNameForm({...nameForm, nameLength: '3'})}
                >
                  三字名（如：李梓涵悦）
                </button>
                <button 
                  className={`tab-option ${nameForm.nameLength === '4' ? 'active' : ''}`}
                  onClick={() => setNameForm({...nameForm, nameLength: '4', maSurname: nameForm.maSurname || ''})}
                >
                  四字名（如：李王梓涵）
                </button>
              </div>
            </div>

            {/* 四字名时显示妈妈姓氏 */}
            {nameForm.nameLength === '4' && (
              <div className="form-group" style={{ marginBottom: 24 }}>
                <label className="form-label">妈妈姓氏（四字名用）</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="如：王"
                  value={nameForm.maSurname}
                  onChange={e => setNameForm({...nameForm, maSurname: e.target.value})}
                />
                <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>
                  四字名格式：爸爸姓 + 妈妈姓 + 双字名（如：李王子涵）
                </p>
              </div>
            )}

            {/* 辈分字选项 */}
            <div className="form-group" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <input 
                  type="checkbox" 
                  id="useBaifen"
                  checked={nameForm.useBaifen}
                  onChange={e => setNameForm({...nameForm, useBaifen: e.target.checked})}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor="useBaifen" style={{ cursor: 'pointer', fontSize: 14 }}>
                  使用辈分字（家族传承）
                </label>
              </div>
              {nameForm.useBaifen && (
                <div style={{ marginLeft: 30 }}>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder="输入辈分字，如：文、德、志"
                    value={nameForm.baifenChar}
                    onChange={e => setNameForm({...nameForm, baifenChar: e.target.value})}
                    style={{ maxWidth: 200 }}
                  />
                  <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>
                    辈分字将自动安排在名字中间位置
                  </p>
                </div>
              )}
            </div>

            {/* 定字选项 */}
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">定字取名（可选）</label>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <select 
                  className="form-input" 
                  value={nameForm.fixedType}
                  onChange={e => setNameForm({...nameForm, fixedType: e.target.value})}
                  style={{ maxWidth: 140 }}
                >
                  <option value="none">无定字</option>
                  <option value="first">首字固定</option>
                  <option value="middle">中间字固定</option>
                  <option value="last">末字固定</option>
                </select>
                {nameForm.fixedType !== 'none' && (
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={`输入固定字`}
                    value={nameForm.fixedChar}
                    onChange={e => setNameForm({...nameForm, fixedChar: e.target.value})}
                    style={{ maxWidth: 120 }}
                  />
                )}
              </div>
              {nameForm.fixedType !== 'none' && (
                <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>
                  {nameForm.fixedType === 'first' && '首字固定，AI推荐后几个字'}
                  {nameForm.fixedType === 'middle' && '中间字固定，AI推荐其他位置'}
                  {nameForm.fixedType === 'last' && '末字固定，AI推荐前几个字'}
                </p>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">风格偏好</label>
              <div className="tab-selector">
                {[
                  { id: 'traditional', label: '传统经典' },
                  { id: 'modern', label: '现代简约' },
                  { id: 'poetic', label: '诗意文雅' },
                  { id: 'unique', label: '独特稀有' }
                ].map(style => (
                  <button 
                    key={style.id} 
                    className={`tab-option ${nameForm.style === style.id ? 'active' : ''}`}
                    onClick={() => setNameForm({...nameForm, style: style.id})}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ 
              padding: 16, 
              background: 'rgba(108, 93, 211, 0.1)', 
              borderRadius: 12, 
              marginBottom: 24,
              fontSize: 13,
              color: 'var(--text-secondary)'
            }}>
              <strong style={{ color: 'var(--primary-light)' }}>📋 取名依据：</strong><br/>
              1. 八字五行分析：根据生辰八字计算五行喜忌<br/>
              2. 音韵美学：注重声调搭配，朗朗上口<br/>
              3. 寓意内涵：取自诗词典故，寓意美好<br/>
              4. 书写美观：笔画适中，易于书写<br/>
              {nameForm.useBaifen && '5. 辈分传承：尊重家族传统，延续字辈'}
              {nameForm.fixedType !== 'none' && ' • 定字定制：满足个性化需求'}
            </div>
            
            <button className="btn-primary" onClick={handleName}>
              <span>✨</span> AI智能起名
            </button>
            
            {result?.type === 'name' && <NameResult data={result} onShare={() => shareResult('copy')} />}
          </div>
        )}

        {/* 良辰吉日 */}
        {activeTab === 'date' && (
          <div className="panel">
            <h2 className="panel-title">良辰吉日 · 专业择日</h2>
            <p className="panel-subtitle">通书日宜忌与建除 · 结合生日地支冲合参考</p>
            <p className="panel-subtitle" style={{ fontSize: 11, opacity: 0.75, marginBottom: 4, lineHeight: 1.5 }}>
              吉日参考分由通书「日宜忌、忌项、建除」与事项关键词综合计算，确定性、可复算；冲合仅按您出生日地支简化比对。非法律文书，请以实际安排为准。
            </p>
            <p className="panel-subtitle" style={{ fontSize: 11, opacity: 0.72, marginBottom: 14, lineHeight: 1.5 }}>
              注：本模块仅做通书/民俗参考，不提供宗教仪式、法事或任何"灵验"承诺；涉及医疗、法律与重大决策请以专业意见为准。
            </p>

            {/* 择日事项 - 扩展版 */}
            <div className="form-group">
              <label className="form-label">择日事项</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { id: '结婚嫁娶', label: '结婚嫁娶' },
                  { id: '订婚求子', label: '订婚求子' },
                  { id: '搬家入宅', label: '搬家入宅' },
                  { id: '开业开市', label: '开业开市' },
                  { id: '动土奠基', label: '动土奠基' },
                  { id: '装修装饰', label: '装修装饰' },
                  { id: '民俗祈愿', label: '民俗祈愿' },
                  { id: '出行远行', label: '出行远行' },
                  { id: '签约合作', label: '签约合作' },
                  { id: '求职上任', label: '求职上任' },
                  { id: '看病就医', label: '看病就医' },
                  { id: '其他大事', label: '其他大事' },
                ].map(item => (
                  <button key={item.id} className={`tab-option ${dateForm.event === item.id ? 'active' : ''}`}
                    onClick={() => setDateForm({...dateForm, event: item.id})}>{item.label}</button>
                ))}
              </div>
            </div>

            {/* 年份和月份 */}
            <div className="form-grid" style={{ marginTop: 16 }}>
              <div className="form-group">
                <label className="form-label">选择年份</label>
                <select className="form-input" value={dateForm.year}
                  onChange={e => setDateForm({...dateForm, year: +e.target.value})}>
                  {[...Array(5)].map((_, i) => {
                    const y = new Date().getFullYear() + i;
                    return <option key={y} value={y}>{y}年</option>;
                  })}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">起始月份</label>
                <select className="form-input" value={dateForm.month}
                  onChange={e => setDateForm({...dateForm, month: +e.target.value})}>
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}月</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 查询范围 */}
            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">查询范围</label>
              <select className="form-input" value={dateForm.days}
                onChange={e => setDateForm({...dateForm, days: +e.target.value})}>
                <option value={15}>近15天</option>
                <option value={30}>近30天</option>
                <option value={60}>近60天</option>
                <option value={90}>近90天</option>
              </select>
            </div>

            {/* 用户出生信息 - 用于计算冲煞 */}
            <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, marginTop: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                <span style={{ marginRight: 8 }}>📌</span>出生日期须不晚于今天；选择后，列表会标注与您出生日地支「六冲」的择日日支（民俗简化参考）
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">出生年份</label>
                  <select className="form-input" value={dateForm.birthYear}
                    onChange={e => setDateForm({...dateForm, birthYear: +e.target.value})}>
                    {[...Array(100)].map((_, i) => {
                      const y = new Date().getFullYear() - i;
                      return <option key={y} value={y}>{y}年</option>;
                    })}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">出生月份</label>
                  <select className="form-input" value={dateForm.birthMonth}
                    onChange={e => setDateForm({...dateForm, birthMonth: +e.target.value})}>
                    {[...Array(auspiciousBirthLimits.maxBirthMonth)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}月</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">出生日期</label>
                  <select className="form-input" value={dateForm.birthDay}
                    onChange={e => setDateForm({...dateForm, birthDay: +e.target.value})}>
                    {[...Array(auspiciousBirthLimits.maxBirthDay)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}日</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">性别</label>
                  <div className="tab-selector">
                    <button className={`tab-option ${dateForm.gender === 'male' ? 'active' : ''}`}
                      onClick={() => setDateForm({...dateForm, gender: 'male'})}>男</button>
                    <button className={`tab-option ${dateForm.gender === 'female' ? 'active' : ''}`}
                      onClick={() => setDateForm({...dateForm, gender: 'female'})}>女</button>
                  </div>
                </div>
              </div>
            </div>

            {/* 择日偏好 */}
            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">筛选偏好</label>
              <div className="tab-selector">
                <button className={`tab-option ${dateForm.preference === 'both' ? 'active' : ''}`}
                  onClick={() => setDateForm({...dateForm, preference: 'both'})}>推荐所有</button>
                <button className={`tab-option ${dateForm.preference === 'good' ? 'active' : ''}`}
                  onClick={() => setDateForm({...dateForm, preference: 'good'})}>仅好日子</button>
                <button className={`tab-option ${dateForm.preference === 'avoid' ? 'active' : ''}`}
                  onClick={() => setDateForm({...dateForm, preference: 'avoid'})}>避开坏日子</button>
              </div>
            </div>

            <button className="btn-primary" onClick={handleDate} style={{ marginTop: 20 }}>
              <span>📅</span> 开始专业择日
            </button>

            {result?.type === 'date' && <DateResult data={result} />}
          </div>
        )}

        {/* 风水顾问 */}
        {activeTab === 'fengshui' && (
          <div className="panel">
            <h2 className="panel-title">风水布局顾问 · 专业版</h2>
            <p className="panel-subtitle">居住舒适 · 动线采光 · 文化参考（非实地罗盘）</p>

            <div style={{ marginBottom: 16, padding: 14, borderRadius: 12, border: '1px dashed var(--card-border)', fontSize: 12, lineHeight: 1.65, color: 'var(--text-tertiary)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>说明：</strong>
              结果依据您自填信息与系统规则分，非实地勘察；未实现玄空、八宅等完整理气排盘。长文为AI生成，仅供居住与文化参考，不构成建筑安全、医疗、投资或人事依据。复杂格局请咨询持证设计师或其他更专业人士。
            </div>

            {/* 基本信息 */}
            <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>🏠 基本信息</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">房间类型</label>
                  <select className="form-input" value={fengShuiForm.roomType}
                    onChange={e => setFengShuiForm({...fengShuiForm, roomType: e.target.value})}>
                    <option>客厅</option>
                    <option>卧室</option>
                    <option>主卧</option>
                    <option>次卧</option>
                    <option>书房</option>
                    <option>厨房</option>
                    <option>餐厅</option>
                    <option>办公室</option>
                    <option>商铺</option>
                    <option>整套住宅</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">面积范围</label>
                  <select className="form-input" value={fengShuiForm.area}
                    onChange={e => setFengShuiForm({...fengShuiForm, area: e.target.value})}>
                    <option>10平米以下</option>
                    <option>10-15平米</option>
                    <option>15-20平米</option>
                    <option>20-30平米</option>
                    <option>30-50平米</option>
                    <option>50-80平米</option>
                    <option>80-120平米</option>
                    <option>120平米以上</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 朝向信息 */}
            <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>🧭 朝向信息</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">房屋朝向</label>
                  <select className="form-input" value={fengShuiForm.direction}
                    onChange={e => setFengShuiForm({...fengShuiForm, direction: e.target.value})}>
                    <option>坐北朝南（最佳）</option>
                    <option>坐南朝北</option>
                    <option>坐东朝西</option>
                    <option>坐西朝东</option>
                    <option>坐东北朝西南</option>
                    <option>坐西南朝东北</option>
                    <option>坐西北朝东南</option>
                    <option>坐东南朝西北</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">入户门朝向</label>
                  <select className="form-input" value={fengShuiForm.doorDirection}
                    onChange={e => setFengShuiForm({...fengShuiForm, doorDirection: e.target.value})}>
                    <option>正南</option>
                    <option>正北</option>
                    <option>正东</option>
                    <option>正西</option>
                    <option>东南</option>
                    <option>东北</option>
                    <option>西南</option>
                    <option>西北</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">窗户朝向</label>
                  <select className="form-input" value={fengShuiForm.windowDirection}
                    onChange={e => setFengShuiForm({...fengShuiForm, windowDirection: e.target.value})}>
                    <option>正南</option>
                    <option>正北</option>
                    <option>正东</option>
                    <option>正西</option>
                    <option>东南</option>
                    <option>东北</option>
                    <option>西南</option>
                    <option>西北</option>
                    <option>多面采光</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">窗户数量</label>
                  <select className="form-input" value={fengShuiForm.windowCount}
                    onChange={e => setFengShuiForm({...fengShuiForm, windowCount: e.target.value})}>
                    <option>无窗户</option>
                    <option>1个</option>
                    <option>2个</option>
                    <option>3个</option>
                    <option>4个及以上</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 楼层与环境 */}
            <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>🏢 楼层与环境</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">所在楼层</label>
                  <select className="form-input" value={fengShuiForm.floor}
                    onChange={e => setFengShuiForm({...fengShuiForm, floor: e.target.value})}>
                    {[...Array(50)].map((_, i) => (
                      <option key={i + 1} value={String(i + 1)}>{i + 1}楼</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">总楼层</label>
                  <select className="form-input" value={fengShuiForm.totalFloors}
                    onChange={e => setFengShuiForm({...fengShuiForm, totalFloors: e.target.value})}>
                    {[...Array(60)].map((_, i) => (
                      <option key={i + 1} value={String(i + 1)}>{i + 1}层</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">建筑类型</label>
                  <select className="form-input" value={fengShuiForm.buildingType}
                    onChange={e => setFengShuiForm({...fengShuiForm, buildingType: e.target.value})}>
                    <option>公寓住宅</option>
                    <option>别墅</option>
                    <option>商铺店面</option>
                    <option>写字楼</option>
                    <option>农村自建房</option>
                    <option>其他</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">周边环境</label>
                  <select className="form-input" value={fengShuiForm.nearbyEnvironment}
                    onChange={e => setFengShuiForm({...fengShuiForm, nearbyEnvironment: e.target.value})}>
                    <option>无特殊</option>
                    <option>医院附近</option>
                    <option>学校附近</option>
                    <option>寺庙教堂附近</option>
                    <option>殡仪馆附近</option>
                    <option>加油站附近</option>
                    <option>菜市场附近</option>
                    <option>KTV/酒吧附近</option>
                    <option>十字路口旁</option>
                    <option>铁路旁</option>
                    <option>高压电塔附近</option>
                    <option>垃圾处理厂附近</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 用途与家庭 */}
            <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>👨‍👩‍👧‍👦 用途与成员</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">主要用途</label>
                  <select className="form-input" value={fengShuiForm.purpose}
                    onChange={e => setFengShuiForm({...fengShuiForm, purpose: e.target.value})}>
                    <option value="居住">居住休息</option>
                    <option value="工作">工作办公</option>
                    <option value="学习">学习读书</option>
                    <option value="经商">经商营业</option>
                    <option value="求财">投资求财</option>
                    <option value="健康">健康养生</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">家庭成员数</label>
                  <select className="form-input" value={fengShuiForm.familyMembers}
                    onChange={e => setFengShuiForm({...fengShuiForm, familyMembers: e.target.value})}>
                    <option>独居</option>
                    <option>2人</option>
                    <option>3人</option>
                    <option>4人</option>
                    <option>5人及以上</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>📐 常见布局关注点（可选，用于规则提示）</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { key: 'layoutDoorRush', label: '门外动线或长廊直冲入户（开门见底）' },
                  { key: 'layoutThroughDraft', label: '入户门与阳台/窗近似一条直线（对流明显）' },
                  { key: 'layoutBeamOverSeat', label: '沙发或床上方有显眼横梁' },
                  { key: 'layoutMirrorToBed', label: '镜子对床（多见于卧室）' },
                  { key: 'layoutCluttered', label: '门口或通道杂物多、动线不畅' }
                ].map((item) => (
                  <label key={item.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
                    <input
                      type="checkbox"
                      checked={!!fengShuiForm[item.key]}
                      onChange={(e) => setFengShuiForm({ ...fengShuiForm, [item.key]: e.target.checked })}
                      style={{ marginTop: 3 }}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={fengShuiForm.syncBaziWithBaziTab}
                onChange={(e) => setFengShuiForm({ ...fengShuiForm, syncBaziWithBaziTab: e.target.checked })}
                style={{ marginTop: 3 }}
              />
              <span>附带「八字批解」页当前生辰，供色彩与倾向描述参照（可选；未勾选则仅谈通用布局）。</span>
            </label>
            {fengShuiForm.syncBaziWithBaziTab && (
              <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '-8px 0 16px', paddingLeft: 28 }}>
                将使用：{baziForm.year}年{baziForm.month}月{baziForm.day}日 · {baziForm.hour}:00
              </p>
            )}

            <button className="btn-primary" onClick={handleFengShui}>
              <span>◐</span> 开始深度风水分析
            </button>

            {result?.type === 'fengshui' && <FengShuiResult data={result} onShare={() => shareResult('copy')} />}
          </div>
        )}

        {/* 面相探微 */}
        {activeTab === 'face' && (
          <div className="panel">
            {(faceStep === 0 || faceStep === 'intro') ? (
              // 模式选择页
              <>
                <h2 className="panel-title">👤 面相探微</h2>
                <p className="panel-subtitle">通过问答分析您的面向特征</p>

                <div style={{ padding: 24, background: 'rgba(255,255,255,0.03)', borderRadius: 16, margin: '20px 0' }}>
                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
                    <h3>什么是面相探微？</h3>
                  </div>
                  <ul style={{ fontSize: 14, lineHeight: 2, color: 'var(--text-secondary)', paddingLeft: 20 }}>
                    <li>面相探微借鉴传统相学观察，结合问卷做气质与倾向梳理（文化娱乐向）</li>
                    <li>通过观察五官形状、位置、气色判断运势</li>
                    <li>涵盖额头、眉眼、鼻相、口相、下颌、耳相、气色等</li>
                    <li>结合三停比例、五岳六府、十二宫位综合分析</li>
                  </ul>
                  <div style={{ marginTop: 20, padding: 14, borderRadius: 12, border: '1px dashed var(--card-border)', fontSize: 12, lineHeight: 1.65, color: 'var(--text-tertiary)' }}>
                    <strong style={{ color: 'var(--text-secondary)' }}>说明：</strong>
                    本功能基于您自填问卷与规则化倾向分，非照片实测；长文为AI生成，仅供娱乐与文化参考，不构成医疗、投资或人事决策依据。健康相关请遵医嘱与体检。
                  </div>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
                    <input
                      type="checkbox"
                      checked={faceForm.syncBaziFromBaziTab}
                      onChange={(e) => setFaceForm({ ...faceForm, syncBaziFromBaziTab: e.target.checked })}
                      style={{ marginTop: 3 }}
                    />
                    <span>提交时附带「八字批解」页当前生辰，与命局参照联动（可选；若与面向叙述冲突，以八字扶抑参考为主）。</span>
                  </label>
                  {faceForm.syncBaziFromBaziTab && (
                    <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8, paddingLeft: 28 }}>
                      将使用：{baziForm.year}年{baziForm.month}月{baziForm.day}日 · {baziForm.hour}:00（请在八字页确认无误）
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                  <button className="btn-primary" style={{ width: '100%', padding: '20px 16px' }}
                    onClick={() => setFaceStep(1)}>
                    <div style={{ fontSize: 20, marginBottom: 8 }}>🔬</div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>开始面相探微</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>12题 · 深度解读面相探微</div>
                  </button>
                </div>
              </>
            ) : typeof faceStep === 'number' && faceStep >= 1 && faceStep <= 12 ? (
              // 专业版 12题
              <>
                <h2 className="panel-title">👤 面相探微 · 第{faceStep}/12题</h2>
                <div style={{ margin: '20px 0', padding: '12px 0' }}>
                  <div style={{ display: 'flex', gap: 2 }}>
                    {[...Array(12)].map((_, i) => (
                      <div key={i} style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: i < faceStep ? 'var(--primary)' : 'rgba(255,255,255,0.1)'
                      }} />
                    ))}
                  </div>
                </div>

                {faceStep === 1 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>您的性别是？</label>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button className={`tab-option ${faceForm.gender === 'male' ? 'active' : ''}`}
                        style={{ flex: 1, padding: '16px 0', fontSize: 15 }}
                        onClick={() => { setFaceForm({...faceForm, gender: 'male'}); setFaceStep(2); }}>👨 男性</button>
                      <button className={`tab-option ${faceForm.gender === 'female' ? 'active' : ''}`}
                        style={{ flex: 1, padding: '16px 0', fontSize: 15 }}
                        onClick={() => { setFaceForm({...faceForm, gender: 'female'}); setFaceStep(2); }}>👩 女性</button>
                    </div>
                  </div>
                )}

                {faceStep === 2 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>👁️ 额头（上停）状态？</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { id: '高宽', label: '高宽饱满 - 天庭饱满，早年发达' },
                        { id: '标准', label: '标准适中 - 智慧通达，运势平稳' },
                        { id: '低窄', label: '低窄偏小 - 早年艰辛，需努力' },
                        { id: '皱纹', label: '皱纹较多 - 思虑过多，操劳' }
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${faceForm.forehead === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px', textAlign: 'left' }}
                          onClick={() => { setFaceForm({...faceForm, forehead: item.id}); setFaceStep(3); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 3 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>👁️ 眉毛状态？</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {[
                        { id: '浓密', label: '浓密有形' },
                        { id: '稀疏', label: '稀疏单薄' },
                        { id: '上扬', label: '眉尾上扬' },
                        { id: '下垂', label: '眉尾下垂' }
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${faceForm.eyebrows === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px' }}
                          onClick={() => { setFaceForm({...faceForm, eyebrows: item.id}); setFaceStep(4); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 4 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>👁️ 眼睛状态？</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { id: '有神', label: '有神明亮 - 精力充沛' },
                        { id: '温柔', label: '温柔细长 - 感情丰富' },
                        { id: '锐利', label: '锐利有神 - 事业心强' },
                        { id: '下垂', label: '眼皮下垂 - 易忧思' }
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${faceForm.eyes === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px', textAlign: 'left' }}
                          onClick={() => { setFaceForm({...faceForm, eyes: item.id}); setFaceStep(5); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 5 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>👃 鼻相状态？</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { id: '端正', label: '端正匀称 - 财运稳定' },
                        { id: '高挺', label: '高挺有势 - 事业心强' },
                        { id: '圆润', label: '圆润饱满 - 财运亨通' },
                        { id: '扁平', label: '扁平塌陷 - 财运一般' }
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${faceForm.nose === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px', textAlign: 'left' }}
                          onClick={() => { setFaceForm({...faceForm, nose: item.id}); setFaceStep(6); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 6 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>👄 嘴唇状态？</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { id: '适中', label: '厚薄适中 - 表达能力好' },
                        { id: '丰厚', label: '丰厚饱满 - 财运旺盛' },
                        { id: '薄型', label: '薄型单薄 - 表达力佳但口福较差' },
                        { id: '不对称', label: '嘴唇不对称 - 口福一般' }
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${faceForm.mouth === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px', textAlign: 'left' }}
                          onClick={() => { setFaceForm({...faceForm, mouth: item.id}); setFaceStep(7); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 7 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>🔶 脸颊状态？</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {[
                        { id: '饱满', label: '圆润饱满' },
                        { id: '平坦', label: '平坦适中' },
                        { id: '凹陷', label: '略显消瘦' },
                        { id: '有肉', label: '有肉感' }
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${faceForm.cheeks === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px' }}
                          onClick={() => { setFaceForm({...faceForm, cheeks: item.id}); setFaceStep(8); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 8 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>👄 下颌状态？</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { id: '圆润', label: '圆润饱满 - 晚年福气' },
                        { id: '方正', label: '方正有力 - 意志坚定' },
                        { id: '尖削', label: '尖削纤细 - 晚年奔波' },
                        { id: '双下巴', label: '双下巴 - 人缘不错' }
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${faceForm.jaw === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px', textAlign: 'left' }}
                          onClick={() => { setFaceForm({...faceForm, jaw: item.id}); setFaceStep(9); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 9 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>👂 耳朵形态？</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {[
                        { id: '大耳', label: '较大且明显 - 耳聪目明' },
                        { id: '适中', label: '大小适中 - 聪明适中' },
                        { id: '小耳', label: '较小偏薄 - 需努力' },
                        { id: '招风耳', label: '外招较大 - 接受能力强' }
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${faceForm.ears === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px', fontSize: 13 }}
                          onClick={() => { setFaceForm({...faceForm, ears: item.id}); setFaceStep(10); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 10 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>🎨 皮肤气色？</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {[
                        { id: '光泽', label: '红润有光泽 - 气血旺盛' },
                        { id: '细腻', label: '白皙细腻 - 身体健康' },
                        { id: '暗沉', label: '略带暗沉 - 需注意健康' },
                        { id: '蜡黄', label: '蜡黄无光 - 运势平平' }
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${faceForm.skin === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px', fontSize: 13 }}
                          onClick={() => { setFaceForm({...faceForm, skin: item.id}); setFaceStep(11); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 11 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>📐 三停主观感受（上庭额 / 中庭眼鼻 / 下庭口颌）？</label>
                    <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>选一項最贴近的自我观察，用于规则层加权三停解读（非精密测量）。</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { id: 'balanced', label: '三停较均衡' },
                        { id: 'upper', label: '上停（额头一带）相对更饱满/突出' },
                        { id: 'middle', label: '中停（眉眼鼻一带）相对更饱满/突出' },
                        { id: 'lower', label: '下停（口颌一带）相对更饱满/突出' }
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`tab-option ${faceForm.sanTingBalance === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px', textAlign: 'left' }}
                          onClick={() => {
                            setFaceForm({ ...faceForm, sanTingBalance: item.id });
                            setFaceStep(12);
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {faceStep === 12 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>😊 整体面部感觉？</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {['和谐对称 - 福相', '略有不均 - 正常', '有明显差异 - 需注意', '特殊气质 - 独特魅力', '普通人相 - 普通人', '俊美/美丽 - 颜值高'].map((item) => (
                        <button
                          key={item}
                          type="button"
                          className={`tab-option ${faceForm.overall === item ? 'active' : ''}`}
                          style={{ padding: '14px 16px' }}
                          onClick={() => {
                            const updatedForm = { ...faceForm, overall: item };
                            setFaceForm(updatedForm);
                            setTimeout(() => handleFace(), 50);
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              // 结果页
              <>
                <h2 className="panel-title">面相探微结果</h2>
                {result?.type === 'face' && <FaceResult data={result} onShare={() => shareResult('copy')} />}
                <button className="btn-secondary" style={{ marginTop: 20 }} onClick={() => { setFaceStep(0); setResult(null); }}>
                  重新测试
                </button>
              </>
            )}
          </div>
        )}



        {/* MBTI性格测试 - 专业版60题 + 趣味梗型 */}
        {activeTab === 'mbti' && (
          <div className="panel mbti-panel">
            {mbtiMode === 'choose' ? (
              // 介绍页：双入口（方案 B）
              <>
                <h2 className="panel-title">🎯 性格测试</h2>
                <p className="panel-subtitle">专业测评与趣味梗型 · 请任选一种开始</p>

                <div className="mbti-intro">
                  <div className="mbti-intro-card">
                    <h4>📋 专业 60 题（MBTI）</h4>
                    <ul>
                      <li>共 <strong>60 道</strong>二选一，约 8–12 分钟</li>
                      <li>基于 MBTI 四维度计分，并结合东方命理语境解读</li>
                      <li>生成类型码、维度倾向、AI 分析与职业参考</li>
                    </ul>
                  </div>
                  <div className="mbti-intro-card">
                    <h4>🎪 趣味梗型（娱乐）</h4>
                    <ul>
                      <li>共 <strong>{MEME_QUESTIONS.length} 道</strong>轻松题，约 2 分钟</li>
                      <li>梗型代号与吐槽向标签，适合截图分享</li>
                      <li>
                        <strong>纯娱乐</strong>，不构成心理诊断，亦非 MBTI 正式测评
                      </li>
                    </ul>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn-primary mbti-start-btn"
                    onClick={() => {
                      setMbtiMode('pro');
                      setMbtiStep(1);
                      setMbtiAnswers({});
                      setResult(null);
                    }}
                  >
                    <span>🎯</span> 专业 60 题（MBTI）
                  </button>
                  <button
                    type="button"
                    className="btn-secondary mbti-start-btn"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => {
                      setMbtiMode('meme');
                      setMemeStep(1);
                      setMemeAnswers({});
                      setResult(null);
                    }}
                  >
                    <span>🎪</span> 趣味梗型（娱乐）
                  </button>
                </div>
              </>
            ) : mbtiMode === 'pro' && mbtiStep <= 60 ? (
              // 答题页
              <>
                <div className="mbti-header">
                  <h2 className="panel-title">性格测试</h2>
                  <div className="mbti-progress">
                    <div className="mbti-progress-bar">
                      <div className="mbti-progress-fill" style={{width: `${(mbtiStep / 60) * 100}%`}} />
                    </div>
                    <span className="mbti-progress-text">{mbtiStep} / 60</span>
                  </div>
                </div>
                
                <div className="mbti-question">
                  <div className="mbti-question-num">第 {mbtiStep} 题</div>
                  <div className="mbti-question-text">{MBTI_QUESTIONS[mbtiStep - 1]?.question}</div>
                  
                  <div className="mbti-options">
                    <button 
                      className={`mbti-option ${mbtiAnswers[mbtiStep] === 'A' ? 'selected' : ''}`}
                      onClick={() => handleMBTIAnswer(mbtiStep, 'A')}
                    >
                      <span className="mbti-option-label">A</span>
                      <span className="mbti-option-text">{MBTI_QUESTIONS[mbtiStep - 1]?.optionA}</span>
                    </button>
                    <button 
                      className={`mbti-option ${mbtiAnswers[mbtiStep] === 'B' ? 'selected' : ''}`}
                      onClick={() => handleMBTIAnswer(mbtiStep, 'B')}
                    >
                      <span className="mbti-option-label">B</span>
                      <span className="mbti-option-text">{MBTI_QUESTIONS[mbtiStep - 1]?.optionB}</span>
                    </button>
                  </div>
                </div>
                
                <div className="mbti-nav">
                  {mbtiStep >= 1 && (
                    <button 
                      className="btn-secondary" 
                      onClick={() => {
                        if (mbtiStep <= 1) {
                          setMbtiMode('choose');
                          setMbtiStep(0);
                          setMbtiAnswers({});
                        } else {
                          setMbtiStep(mbtiStep - 1);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        color: 'var(--text-secondary)',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.15)';
                        e.target.style.color = 'var(--text-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.08)';
                        e.target.style.color = 'var(--text-secondary)';
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                      </svg>
                      {mbtiStep <= 1 ? '← 返回选题' : '上一题'}
                    </button>
                  )}
                  {mbtiStep < 60 ? (
                    <button 
                      className={`btn-primary ${!mbtiAnswers[mbtiStep] ? 'disabled' : ''}`}
                      onClick={() => {
                        if (mbtiAnswers[mbtiStep]) {
                          setMbtiStep(mbtiStep + 1);
                        }
                      }}
                      style={{marginLeft: 'auto', opacity: mbtiAnswers[mbtiStep] ? 1 : 0.5, cursor: mbtiAnswers[mbtiStep] ? 'pointer' : 'not-allowed'}}
                    >
                      {mbtiAnswers[mbtiStep] ? '下一题 →' : '请先选择答案'}
                    </button>
                  ) : (
                    <button 
                      className={`btn-primary ${!mbtiAnswers[mbtiStep] ? 'disabled' : ''}`}
                      onClick={() => {
                        if (mbtiAnswers[mbtiStep]) {
                          handleMBTIFinish();
                        }
                      }}
                      style={{marginLeft: 'auto', opacity: mbtiAnswers[mbtiStep] ? 1 : 0.5, cursor: mbtiAnswers[mbtiStep] ? 'pointer' : 'not-allowed'}}
                    >
                      {mbtiAnswers[mbtiStep] ? '查看结果 ✨' : '请先选择答案'}
                    </button>
                  )}
                </div>
              </>
            ) : mbtiMode === 'pro' && mbtiStep === 61 ? (
              // 专业版结果页
              <>
                <h2 className="panel-title">测试结果</h2>
                <p className="panel-subtitle">基于您的60道题目回答生成的专业性格分析</p>
                {result?.type === 'mbti' ? (
                  <MBTIResult data={result} onShare={() => shareResult('copy')} />
                ) : result ? (
                  <div className="result-section">
                    <div className="result-card">
                      <h3>分析完成</h3>
                      <p>您的性格测试已完成，正在生成详细报告...</p>
                      <pre style={{fontSize: 12, marginTop: 16, opacity: 0.7}}>{JSON.stringify(result, null, 2)}</pre>
                    </div>
                  </div>
                ) : (
                  <div className="result-section">
                    <div className="result-card">
                      <h3>正在加载结果...</h3>
                      <p>请稍候，正在为您生成性格分析报告</p>
                    </div>
                  </div>
                )}
                <button 
                  className="btn-secondary" 
                  style={{marginTop: 24, width: '100%'}}
                  onClick={() => {
                    setMbtiMode('choose');
                    setMbtiStep(0);
                    setMbtiAnswers({});
                    setResult(null);
                  }}
                >
                  重新测试
                </button>
              </>
            ) : mbtiMode === 'meme' && memeStep >= 1 && memeStep <= MEME_QUESTIONS.length ? (
              // 趣味梗型答题
              <>
                <div className="mbti-header">
                  <h2 className="panel-title">趣味梗型</h2>
                  <p className="panel-subtitle" style={{ marginTop: 4, opacity: 0.85 }}>娱乐向 · 非专业心理测评</p>
                  <div className="mbti-progress">
                    <div className="mbti-progress-bar">
                      <div
                        className="mbti-progress-fill"
                        style={{ width: `${(memeStep / MEME_QUESTIONS.length) * 100}%` }}
                      />
                    </div>
                    <span className="mbti-progress-text">
                      {memeStep} / {MEME_QUESTIONS.length}
                    </span>
                  </div>
                </div>

                <div className="mbti-question">
                  <div className="mbti-question-num">第 {memeStep} 题</div>
                  <div className="mbti-question-text">{MEME_QUESTIONS[memeStep - 1]?.question}</div>

                  <div className="mbti-options">
                    <button
                      type="button"
                      className={`mbti-option ${memeAnswers[memeStep] === 'A' ? 'selected' : ''}`}
                      onClick={() => handleMemeAnswer(memeStep, 'A')}
                    >
                      <span className="mbti-option-label">A</span>
                      <span className="mbti-option-text">{MEME_QUESTIONS[memeStep - 1]?.optionA}</span>
                    </button>
                    <button
                      type="button"
                      className={`mbti-option ${memeAnswers[memeStep] === 'B' ? 'selected' : ''}`}
                      onClick={() => handleMemeAnswer(memeStep, 'B')}
                    >
                      <span className="mbti-option-label">B</span>
                      <span className="mbti-option-text">{MEME_QUESTIONS[memeStep - 1]?.optionB}</span>
                    </button>
                  </div>
                </div>

                <div className="mbti-nav">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      if (memeStep <= 1) {
                        setMbtiMode('choose');
                        setMemeStep(0);
                        setMemeAnswers({});
                      } else {
                        setMemeStep(memeStep - 1);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {memeStep <= 1 ? '← 返回选题' : '上一题'}
                  </button>
                  {memeStep < MEME_QUESTIONS.length ? (
                    <button
                      type="button"
                      className={`btn-primary ${!memeAnswers[memeStep] ? 'disabled' : ''}`}
                      onClick={() => {
                        if (memeAnswers[memeStep]) setMemeStep(memeStep + 1);
                      }}
                      style={{
                        marginLeft: 'auto',
                        opacity: memeAnswers[memeStep] ? 1 : 0.5,
                        cursor: memeAnswers[memeStep] ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {memeAnswers[memeStep] ? '下一题 →' : '请先选择答案'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`btn-primary ${!memeAnswers[memeStep] ? 'disabled' : ''}`}
                      onClick={() => {
                        if (memeAnswers[memeStep]) handleMemeFinish();
                      }}
                      style={{
                        marginLeft: 'auto',
                        opacity: memeAnswers[memeStep] ? 1 : 0.5,
                        cursor: memeAnswers[memeStep] ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {memeAnswers[memeStep] ? '查看梗型结果 ✨' : '请先选择答案'}
                    </button>
                  )}
                </div>
              </>
            ) : mbtiMode === 'meme' && memeStep > MEME_QUESTIONS.length ? (
              // 趣味梗型结果
              <>
                <h2 className="panel-title">趣味梗型结果</h2>
                <p className="panel-subtitle">娱乐分享向 · 不构成任何心理诊断或正式 MBTI 结论</p>
                {result?.type === 'mbti_meme' ? (
                  <MemeResult data={result} onShare={() => shareResult('copy')} />
                ) : (
                  <div className="result-section">
                    <div className="result-card">
                      <p>未能加载梗型结果，请返回重试。</p>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ marginTop: 24, width: '100%' }}
                  onClick={() => {
                    setMbtiMode('choose');
                    setMemeStep(0);
                    setMemeAnswers({});
                    setResult(null);
                  }}
                >
                  重新测试
                </button>
              </>
            ) : null}
          </div>
        )}

        {/* 气场扫描 */}
        {activeTab === 'aura' && (
          <div className="panel">
            {auraStep === 0 ? (
              // 介绍页
              <>
                <h2 className="panel-title">气场能量扫描 · 专业版</h2>
                <p className="panel-subtitle">情绪自评 · 象征色 · 身心节律（非仪器实测）</p>

                <div style={{ padding: 24, background: 'rgba(255,255,255,0.03)', borderRadius: 16, margin: '20px 0' }}>
                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>✨</div>
                    <h3>什么是气场能量扫描？</h3>
                  </div>
                  <ul style={{ fontSize: 14, lineHeight: 2, color: 'var(--text-secondary)', paddingLeft: 20 }}>
                    <li>用颜色象征帮助理解当前情绪节奏与自我觉察</li>
                    <li>主导色由问卷规则加权得出，长文解读由 AI生成</li>
                    <li>身心节律指数、压力倾向为规则化自评指标，非医疗诊断</li>
                  </ul>
                  <div style={{ marginTop: 16, padding: 14, borderRadius: 12, border: '1px dashed var(--card-border)', fontSize: 12, lineHeight: 1.65, color: 'var(--text-tertiary)' }}>
                    <strong style={{ color: 'var(--text-secondary)' }}>说明：</strong>
                    非生物或能量场仪器检测；不构成心理或医疗诊断。持续低落、焦虑或自伤念头请寻求专业援助。
                  </div>
                </div>

                <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, marginBottom: 20 }}>
                  <h4 style={{ marginBottom: 12, fontSize: 14 }}>🌈 气场颜色含义</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, fontSize: 12 }}>
                    <span style={{ color: '#9B59B6' }}>● 紫色：灵性智慧</span>
                    <span style={{ color: '#3498DB' }}>● 蓝色：平和冷静</span>
                    <span style={{ color: '#2ECC71' }}>● 绿色：治愈成长</span>
                    <span style={{ color: '#F1C40F' }}>● 金色：财富成功</span>
                    <span style={{ color: '#E91E63' }}>● 粉色：爱情人缘</span>
                    <span style={{ color: '#ECF0F1' }}>● 白色：纯净净化</span>
                    <span style={{ color: '#E67E22' }}>● 橙色：活力创造</span>
                    <span style={{ color: '#1ABC9C' }}>● 青色：平衡和谐</span>
                  </div>
                </div>

                <button className="btn-primary" onClick={() => setAuraStep(1)}>
                  <span>◑</span> 开始气场测试
                </button>
              </>
            ) : auraStep >= 1 && auraStep <= 8 ? (
              // 问卷页（8 题）
              <>
                <h2 className="panel-title">气场扫描 · 第{auraStep}/8题</h2>
                <div style={{ margin: '20px 0', padding: '12px 0' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <div key={i} style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: i <= auraStep ? 'var(--primary)' : 'rgba(255,255,255,0.1)'
                      }} />
                    ))}
                  </div>
                </div>

                {auraStep === 1 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>您的性别是？</label>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button className={`tab-option ${auraForm.gender === 'male' ? 'active' : ''}`}
                        style={{ flex: 1, padding: '16px 0', fontSize: 15 }}
                        onClick={() => { setAuraForm({...auraForm, gender: 'male'}); setAuraStep(2); }}>👨 男性</button>
                      <button className={`tab-option ${auraForm.gender === 'female' ? 'active' : ''}`}
                        style={{ flex: 1, padding: '16px 0', fontSize: 15 }}
                        onClick={() => { setAuraForm({...auraForm, gender: 'female'}); setAuraStep(2); }}>👩 女性</button>
                    </div>
                  </div>
                )}

                {auraStep === 2 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>您目前整体的心情状态是？</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {[
                        { id: '平静', label: '😌 平静祥和', color: '#3498DB' },
                        { id: '愉悦', label: '😊 开心愉快', color: '#F1C40F' },
                        { id: '焦虑', label: '😰 焦虑不安', color: '#E67E22' },
                        { id: '低落', label: '😢 情绪低落', color: '#9B59B6' },
                        { id: '充实', label: '🤩 充实忙碌', color: '#2ECC71' },
                        { id: '迷茫', label: '🤔 迷茫困惑', color: '#95A5A6' },
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${auraForm.mood === item.id ? 'active' : ''}`}
                          style={{ padding: '12px 16px' }}
                          onClick={() => { setAuraForm({...auraForm, mood: item.id}); setAuraStep(3); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {auraStep === 3 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>您最近的身体状况如何？</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {[
                        { id: '良好', label: '💪 精力充沛' },
                        { id: '还好', label: '🙂 状态不错' },
                        { id: '一般', label: '😐 偶尔疲惫' },
                        { id: '疲劳', label: '😓 容易疲劳' },
                        { id: '不适', label: '🤒 身体不适' },
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${auraForm.health === item.id ? 'active' : ''}`}
                          style={{ padding: '12px 16px' }}
                          onClick={() => { setAuraForm({...auraForm, health: item.id}); setAuraStep(4); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {auraStep === 4 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>您目前的事业/学业状态？</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {[
                        { id: '上升', label: '🚀 上升期' },
                        { id: '稳定', label: '📊 稳定发展' },
                        { id: '瓶颈', label: '🔒 遇到瓶颈' },
                        { id: '转型', label: '🔄 转型探索' },
                        { id: '待业', label: '🔍 寻找机会' },
                      ].map(item => (
                        <button key={item.id} className={`tab-option ${auraForm.career === item.id ? 'active' : ''}`}
                          style={{ padding: '12px 16px' }}
                          onClick={() => { setAuraForm({...auraForm, career: item.id}); setAuraStep(5); }}>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {auraStep === 5 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>最近睡眠质量？</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {[
                        { id: '良好', label: '😴 入睡快、醒后清爽' },
                        { id: '一般', label: '😐 偶尔失眠或浅眠' },
                        { id: '多梦易醒', label: '💭 多梦、易醒' },
                        { id: '失眠', label: '😣 常失眠或早醒' }
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`tab-option ${auraForm.sleepQuality === item.id ? 'active' : ''}`}
                          style={{ padding: '12px 16px' }}
                          onClick={() => { setAuraForm({ ...auraForm, sleepQuality: item.id }); setAuraStep(6); }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {auraStep === 6 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>人际关系主观感受？</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {[
                        { id: '融洽', label: '🤝 融洽支持' },
                        { id: '一般', label: '😐 平淡尚可' },
                        { id: '紧张', label: '😣 易冲突压力' },
                        { id: '孤独', label: '🙁 孤独少支持' },
                        { id: '疏离', label: '🫥 疏离难亲近' }
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`tab-option ${auraForm.relationships === item.id ? 'active' : ''}`}
                          style={{ padding: '12px 16px' }}
                          onClick={() => { setAuraForm({ ...auraForm, relationships: item.id }); setAuraStep(7); }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {auraStep === 7 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>近期生活事件（选最接近的一项）</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { id: '无特殊', label: '无特殊变化' },
                        { id: '工作压力', label: '工作/学业压力加大' },
                        { id: '感情变化', label: '恋爱、分手或家庭关系变化' },
                        { id: '亲友离别', label: '亲友离别或重大失落' },
                        { id: '搬迁装修', label: '搬迁、装修或环境大变' },
                        { id: '健康担忧', label: '自己或家人健康担忧' }
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`tab-option ${auraForm.recentEvents === item.id ? 'active' : ''}`}
                          style={{ padding: '14px 16px', textAlign: 'left' }}
                          onClick={() => { setAuraForm({ ...auraForm, recentEvents: item.id }); setAuraStep(8); }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {auraStep === 8 && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 16, marginBottom: 16 }}>您最喜欢哪种颜色？（直觉选择）</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                      {[
                        { id: '紫色', label: '紫色', color: '#9B59B6' },
                        { id: '蓝色', label: '蓝色', color: '#3498DB' },
                        { id: '绿色', label: '绿色', color: '#2ECC71' },
                        { id: '金色', label: '金色', color: '#F1C40F' },
                        { id: '粉色', label: '粉色', color: '#E91E63' },
                        { id: '白色', label: '白色', color: '#ECF0F1' },
                        { id: '橙色', label: '橙色', color: '#E67E22' },
                        { id: '红色', label: '红色', color: '#E74C3C' },
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`tab-option ${auraForm.favoriteColor === item.id ? 'active' : ''}`}
                          style={{ padding: '16px 8px', textAlign: 'center' }}
                          onClick={() => { setAuraForm({ ...auraForm, favoriteColor: item.id }); handleAura(); }}
                        >
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: item.color, margin: '0 auto 8px', border: '2px solid ' + (auraForm.favoriteColor === item.id ? '#fff' : 'transparent') }} />
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              // 结果页
              <>
                <h2 className="panel-title">气场扫描结果</h2>
                {result?.type === 'aura' && <AuraResult data={result} onShare={() => shareResult('copy')} />}
                <button className="btn-secondary" style={{ marginTop: 20 }} onClick={() => { setAuraStep(0); setResult(null); }}>
                  重新测试
                </button>
              </>
            )}
          </div>
        )}

        {/* 每日运势 */}
        {activeTab === 'daily' && (
          <div className="panel daily-panel">
            {result?.type === 'daily' ? (
              // 有结果时展示详细运势
              <DailyResult data={result} />
            ) : (
              // 无结果时展示今日预览卡片
              <>
                <h2 className="panel-title">📅 今日运势</h2>
                <p className="panel-subtitle">{new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}</p>
                
                {/* 今日信息预览卡片 */}
                <div className="daily-preview">
                  <div className="daily-preview-header">
                    <div className="daily-preview-date">
                      <span className="daily-preview-month">{new Date().getMonth() + 1}月</span>
                      <span className="daily-preview-day">{new Date().getDate()}</span>
                    </div>
                    <div className="daily-preview-info">
                      <div className="daily-preview-lunar">农历{['正','二','三','四','五','六','七','八','九','十','冬','腊'][new Date().getMonth()]}月</div>
                      <div className="daily-preview-weekday">星期{['日','一','二','三','四','五','六'][new Date().getDay()]}</div>
                    </div>
                  </div>
                  
                  <div className="daily-preview-divider" />
                  
                  <div className="daily-preview-content">
                    <div className="daily-preview-item">
                      <span className="daily-preview-icon">🌅</span>
                      <div className="daily-preview-text">
                        <div className="daily-preview-label">日出东方</div>
                        <div className="daily-preview-desc">新的一天，新的开始</div>
                      </div>
                    </div>
                    <div className="daily-preview-item">
                      <span className="daily-preview-icon">🌙</span>
                      <div className="daily-preview-text">
                        <div className="daily-preview-label">月相盈亏</div>
                        <div className="daily-preview-desc">顺应天时，把握机遇</div>
                      </div>
                    </div>
                    <div className="daily-preview-item">
                      <span className="daily-preview-icon">⭐</span>
                      <div className="daily-preview-text">
                        <div className="daily-preview-label">星辰指引</div>
                        <div className="daily-preview-desc">点击获取专属运势分析</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 功能特色 */}
                <div className="daily-features">
                  <div className="daily-feature">
                    <span className="daily-feature-icon">📊</span>
                    <span className="daily-feature-text">事业财富健康情感全维度分析</span>
                  </div>
                  <div className="daily-feature">
                    <span className="daily-feature-icon">🎯</span>
                    <span className="daily-feature-text">幸运色幸运数吉时方位早知道</span>
                  </div>
                  <div className="daily-feature">
                    <span className="daily-feature-icon">💡</span>
                    <span className="daily-feature-text">宜忌提示助你趋吉避凶</span>
                  </div>
                </div>
                
                <button className="btn-primary daily-btn" onClick={handleDaily}>
                  <span>✨</span> 解锁今日完整运势
                </button>
                
                <p className="daily-hint">
                  💫 剩余免费次数：{userInfo?.freeCalls?.daily || 3}次
                  {!userInfo?.isVip && ' · 开通VIP每日自动推送'}
                </p>
              </>
            )}
          </div>
        )}

        {/* 我的页面 */}
        {activeTab === 'my' && (
          <div className="panel my-page">
            {(() => {
              const levelKey = userInfo?.memberLevel || 'normal';
              const levelNames = { normal: '普通用户', wendao: '🥉 问道者', xiuxing: '🥈 修行者', tianji: '🥇 天机阁' };
              const expireStr = userInfo?.subscription?.expiresAt
                ? new Date(userInfo.subscription.expiresAt).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })
                : null;
              return (
                <div className="my-profile-hero result-card">
                  <div className="neo-avatar my-profile-avatar">👤</div>
                  <h3 className="my-profile-name">用户 {userId.substr(-6)}</h3>
                  <div className="my-profile-badges">
                    {userInfo?.isVip ? (
                      <span className="my-badge my-badge-vip">👑 会员 · {levelNames[levelKey] || levelNames.normal}</span>
                    ) : (
                      <span className="my-badge">{levelNames[levelKey] || '普通用户'}</span>
                    )}
                    {authPhone ? (
                      <span className="my-badge my-badge-muted">{authPhone}</span>
                    ) : (
                      <span className="my-badge my-badge-muted">未绑定手机</span>
                    )}
                  </div>
                  {userInfo?.isVip && expireStr && (
                    <p className="my-profile-expire">有效期至 {expireStr}</p>
                  )}
                  {!authPhone && (
                    <p className="my-profile-hint">当前为本地游客身份，登录后可同步数据、换机不丢记录</p>
                  )}
                </div>
              );
            })()}

            <div className="result-card my-auth-card" style={{ marginBottom: 20 }}>
              <div className="my-auth-card-head">
                <h4 className="my-section-title" style={{ marginBottom: 0 }}>🔐 账号与安全</h4>
                {!authToken && myPageLoginExpanded && (
                  <button type="button" className="my-link-btn" onClick={() => setMyPageLoginExpanded(false)}>
                    收起
                  </button>
                )}
              </div>
              {!authToken ? (
                !myPageLoginExpanded ? (
                  <div className="my-auth-collapsed">
                    <p>登录后可跨设备同步记录、绑定手机号更安全。</p>
                    <button type="button" className="btn-primary" onClick={() => setMyPageLoginExpanded(true)}>
                      登录 / 注册
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ flex: 1, borderColor: authMethod === 'password' ? 'var(--primary)' : 'var(--card-border)' }}
                        onClick={() => setAuthMethod('password')}
                      >
                        密码登录
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ flex: 1, borderColor: authMethod === 'sms' ? 'var(--primary)' : 'var(--card-border)' }}
                        onClick={() => setAuthMethod('sms')}
                      >
                        验证码登录
                      </button>
                    </div>
                    <div className="form-grid" style={{ marginBottom: 0 }}>
                      <div className="form-group">
                        <label className="form-label">手机号</label>
                        <input
                          className="form-input"
                          placeholder="请输入手机号"
                          value={authForm.phone}
                          onChange={(e) => setAuthForm(prev => ({ ...prev, phone: e.target.value.trim() }))}
                        />
                      </div>
                      {authMethod === 'password' ? (
                        <div className="form-group">
                          <label className="form-label">密码</label>
                          <input
                            type="password"
                            className="form-input"
                            placeholder="至少6位密码"
                            value={authForm.password}
                            onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="form-group">
                            <label className="form-label">验证码</label>
                            <div style={{ display: 'flex', gap: 10 }}>
                              <input
                                className="form-input"
                                placeholder="6位验证码"
                                value={authForm.smsCode}
                                onChange={(e) => setAuthForm(prev => ({ ...prev, smsCode: e.target.value.trim() }))}
                              />
                              <button type="button" className="btn-secondary" onClick={sendSmsCode} disabled={authLoading}>
                                发送
                              </button>
                            </div>
                          </div>
                          {smsInfo && (
                            <div style={{
                              marginTop: 10,
                              padding: 10,
                              background: 'rgba(0, 255, 255, 0.1)',
                              border: '1px solid rgba(0, 255, 255, 0.3)',
                              borderRadius: 8,
                              fontSize: 14
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                                <span style={{ color: '#00ffff', marginRight: 5 }}>🔢</span>
                                <strong style={{ color: '#fff' }}>开发环境验证码</strong>
                              </div>
                              <div style={{ color: '#ccc', fontSize: 13 }}>
                                <div>📱 手机号: {smsInfo.phone}</div>
                                <div>🔢 验证码: <strong style={{ color: '#00ffff' }}>{smsInfo.code}</strong></div>
                                <div>⏰ 有效期: 5分钟</div>
                                <div style={{ marginTop: 5, fontSize: 12, color: '#aaa' }}>
                                  提示: 此验证码仅用于开发联调，正式环境请接入真实短信服务
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {authMethod === 'password' && (
                      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ flex: 1, borderColor: authMode === 'login' ? 'var(--primary)' : 'var(--card-border)' }}
                          onClick={() => setAuthMode('login')}
                        >
                          登录
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ flex: 1, borderColor: authMode === 'register' ? 'var(--primary)' : 'var(--card-border)' }}
                          onClick={() => setAuthMode('register')}
                        >
                          注册
                        </button>
                      </div>
                    )}
                    <button type="button" className="btn-primary" style={{ marginTop: 12, width: '100%' }} onClick={handleAuthSubmit} disabled={authLoading}>
                      {authLoading ? '处理中...' : authMethod === 'sms' ? '验证码登录' : authMode === 'register' ? '注册并登录' : '立即登录'}
                    </button>
                  </>
                )
              ) : (
                <>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>已登录：{authPhone || '未知'}</p>
                  <button type="button" className="btn-secondary" style={{ width: '100%' }} onClick={handleLogout}>
                    退出登录
                  </button>
                </>
              )}
            </div>

            <div style={{ marginBottom: 20 }}>
              <h4 className="my-section-title">💬 意见反馈</h4>
              <button type="button" className="btn-secondary" style={{ width: '100%', padding: '12px 16px', fontSize: 14 }} onClick={() => setActiveTab('feedback')}>
                💬 意见反馈
              </button>
            </div>

            <div style={{ marginBottom: 20 }}>
              <h4 className="my-section-title">📧 联系我们</h4>
              <div style={{ padding: '14px 16px', background: 'var(--card-bg)', borderRadius: 12, border: '1px solid var(--card-border)', textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 10 }}>遇到问题请邮件联系我们</p>
                <a href="mailto:3224288945@qq.com" style={{ color: 'var(--primary)', fontSize: 14, textDecoration: 'none', fontWeight: 500 }}>
                  3224288945@qq.com
                </a>
              </div>
            </div>

            {!userInfo?.isVip && (
              <div className="result-card" style={{ marginBottom: 20 }}>
                <h4 className="my-section-title">剩余免费次数</h4>
                {userInfo?.freeCalls && Object.entries(userInfo.freeCalls)
                  .filter(([type]) => ['bazi', 'chat', 'fengshui', 'aura', 'daily', 'dream', 'name', 'date', 'face', 'mbti', 'spirit', 'talisman', 'yunxiang', 'matchmaking'].includes(type))
                  .map(([type, count]) => (
                  <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--card-border)' }}>
                    <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{getTypeName(type)}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: count > 0 ? 'var(--success)' : 'var(--error)' }}>
                      {count > 0 ? `${count}次` : '已用完'}
                    </span>
                  </div>
                  ))}
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <h4 className="my-section-title">📜 分析历史</h4>
              {history.length > 0 ? (
                <div className="history-list">
                  {history.map((item, i) => (
                    <div key={i} className="history-item" onClick={() => alert(item.preview || '暂无详情')}>
                      <div className="history-info">
                        <span className="history-type">{item.type}</span>
                        <span className="history-date">{item.date}</span>
                      </div>
                      <span className="history-arrow">›</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="my-empty-hint">暂无历史记录，快去体验吧！</p>
              )}
            </div>

            <div className="invite-box" style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>🎁</span> 邀请好友赚次数
              </h4>
              <div className="invite-code">
                <div className="invite-code-display">{inviteCode}</div>
                <button type="button" className="btn-send" onClick={copyInviteCode}>复制</button>
              </div>
            </div>

            {/* 分享二维码 */}
            <div className="result-card" style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>📱</span> 分享给好友
              </h4>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                扫码即可体验云端问道，分享给朋友一起探索~
              </p>
              {shareQrCode ? (
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={`data:image/png;base64,${shareQrCode}`}
                    alt="扫码体验"
                    style={{ width: 200, height: 200, borderRadius: 12, background: '#fff', padding: 10 }}
                  />
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn-primary"
                      style={{ fontSize: 13, padding: '8px 20px' }}
                      onClick={() => {
                        const link = document.createElement('a');
                        link.download = `云端问道-二维码-${Date.now()}.png`;
                        link.href = `data:image/png;base64,${shareQrCode}`;
                        link.click();
                      }}
                    >
                      💾 保存图片
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={async () => {
                    try {
                      const url = window.location.origin || (window.location.protocol + '//' + window.location.host);
                      const r = await requestApi(`/qrcode?text=${encodeURIComponent(url)}&size=400`);
                      if (r.success && r.imageBase64) {
                        setShareQrCode(r.imageBase64);
                      } else {
                        alert('生成失败，请稍后重试');
                      }
                    } catch (e) {
                      alert('网络异常，请稍后重试');
                    }
                  }}
                >
                  📲 生成二维码
                </button>
              )}
            </div>

          </div>
        )}

        {/* 赛博灵符 */}
        {activeTab === 'talisman' && (
          <div className="panel talisman-panel">
            <h2 className="panel-title">🔮 赛博灵符</h2>
            <p className="talisman-panel-lead">生成可保存壁纸 · 文化意象与心愿仪式感</p>
            <details className="talisman-disclaimer-fold">
              <summary>说明与免责</summary>
              <p>
                仅供装饰与数字壁纸，不具「灵验」、医疗、投资或安全效力。生辰与八字仅作文案轻度个性化；健康相关为自我关怀提示，不替代诊疗。
              </p>
            </details>

            {!talismanPreview ? (
              <div className="talisman-form">
                {/* 灵符类型选择 */}
                <div className="form-group">
                  <label className="form-label">选择灵符类型</label>
                  <div className="talisman-types">
                    {[
                      { id: 'wealth', icon: '💰', name: '财运符', desc: '财源寓意 · 积极期许' },
                      { id: 'love', icon: '💕', name: '桃花符', desc: '情缘寓意 · 和美期许' },
                      { id: 'career', icon: '💼', name: '事业符', desc: '进取寓意 · 步步向前' },
                      { id: 'health', icon: '🏥', name: '健康符', desc: '身心调护 · 非医疗' },
                      { id: 'safety', icon: '🛡️', name: '平安符', desc: '出入审慎 · 心安寓意' }
                    ].map(type => (
                      <button
                        key={type.id}
                        className={`talisman-type-btn ${talismanForm.type === type.id ? 'active' : ''}`}
                        onClick={() => setTalismanForm({...talismanForm, type: type.id})}
                      >
                        <span className="type-icon">{type.icon}</span>
                        <span className="type-name">{type.name}</span>
                        <span className="type-desc">{type.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 风格选择 */}
                <div className="form-group">
                  <label className="form-label">选择视觉风格</label>
                  <div className="talisman-styles">
                    {[
                      { id: 'cyber', name: '赛博朋克', preview: '🌐' },
                      { id: 'national', name: '国潮风', preview: '🏮' },
                      { id: 'minimal', name: '极简白', preview: '⚪' },
                      { id: 'gold', name: '金箔款', preview: '🏆' },
                      { id: 'ink', name: '水墨风', preview: '🌊' }
                    ].map(style => (
                      <button
                        key={style.id}
                        className={`talisman-style-btn ${talismanForm.style === style.id ? 'active' : ''}`}
                        onClick={() => setTalismanForm({...talismanForm, style: style.id})}
                      >
                        <span className="style-preview">{style.preview}</span>
                        <span className="style-name">{style.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 个人信息 */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">您的姓名</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="输入姓名（用于生成专属灵符）"
                      value={talismanForm.name}
                      onChange={(e) => setTalismanForm({...talismanForm, name: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">出生年份</label>
                    <input
                      type="number"
                      className="form-input"
                      value={talismanForm.year}
                      onChange={(e) => setTalismanForm({...talismanForm, year: +e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">月份</label>
                    <input
                      type="number"
                      className="form-input"
                      min="1"
                      max="12"
                      value={talismanForm.month}
                      onChange={(e) => setTalismanForm({...talismanForm, month: +e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">日期</label>
                    <input
                      type="number"
                      className="form-input"
                      min="1"
                      max="31"
                      value={talismanForm.day}
                      onChange={(e) => setTalismanForm({...talismanForm, day: +e.target.value})}
                    />
                  </div>
                </div>

                {/* 心愿 */}
                <div className="form-group">
                  <label className="form-label">您的心愿（可选）</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    placeholder="可选：一句心愿，用于文案点缀"
                    value={talismanForm.wish}
                    onChange={(e) => setTalismanForm({...talismanForm, wish: e.target.value})}
                  />
                </div>

                {/* 价格说明 */}
                <div className="talisman-pricing">
                  {talismanPricingOptions.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`pricing-option ${item.recommended ? 'recommended' : ''} ${talismanForm.tier === item.id ? 'active' : ''}`}
                      onClick={() => setTalismanForm({ ...talismanForm, tier: item.id })}
                    >
                      <span className="price-tag">¥{item.price}</span>
                      <span className="price-desc">{item.desc}</span>
                      {item.recommended && <span className="recommend-badge">推荐</span>}
                      {talismanForm.tier === item.id && <span className="selected-badge">已选</span>}
                    </button>
                  ))}
                </div>

                <p className="talisman-form-hint">
                  预览含水印；支付所选档位后可下载无水印图（1080P / 4K / 月卡见档位说明）。
                </p>

                <button
                  className="btn-primary btn-large talisman-generate-btn"
                  onClick={generateTalisman}
                  disabled={talismanGenLoading || !talismanForm.name}
                >
                  {talismanGenLoading ? '生成中…' : `生成预览 · ${selectedTalismanTier.payLabel} ¥${selectedTalismanTier.price}`}
                </button>
              </div>
            ) : (
              <div className="talisman-preview">
                {!talismanPreview.paid ? (
                  <div ref={talismanPaySectionRef} className="talisman-pay-section">
                    <div className="talisman-pay-card">
                      <div className="talisman-pay-card-head">
                        <span className="talisman-pay-badge">预览 · 含水印</span>
                        <h3 className="talisman-pay-title">解锁无水印下载</h3>
                        <p className="talisman-pay-lead">支付成功后自动去水印，可保存高清图。</p>
                      </div>
                      <div className="talisman-payment">
                        {talismanWechatPay ? (
                          <div className="talisman-pay-state">
                            <p className="talisman-pay-state-text">请扫描弹层二维码完成支付。</p>
                            <button
                              type="button"
                              className="btn-secondary talisman-pay-full"
                              onClick={() => {
                                setTalismanWechatPay(null);
                                setTalismanOrder(null);
                                sessionStorage.removeItem('pendingTalismanOrderId');
                              }}
                            >
                              取消
                            </button>
                          </div>
                        ) : talismanOrder ? (
                          <div className="talisman-pay-state talisman-pay-state--left">
                            <p className="talisman-order-line">
                              <span className="talisman-order-amount">¥{talismanOrder.amount}</span>
                              <span className="talisman-order-name">{talismanOrder.name || '灵符'}</span>
                            </p>
                            <p className="talisman-pay-state-muted">
                              {talismanOrder.gateway === 'wechat'
                                ? '在微信内完成支付，或稍后点击刷新同步状态。'
                                : '若已付款仍未解锁，请稍等或点击下方刷新。'}
                            </p>
                            <button
                              type="button"
                              className="btn-primary talisman-pay-full"
                              disabled={talismanPayLoading}
                              onClick={handleTalismanConfirmPaid}
                            >
                              {talismanPayLoading ? '同步中…' : '刷新支付状态'}
                            </button>
                            <button
                              type="button"
                              className="btn-secondary talisman-pay-full"
                              disabled={talismanPayLoading}
                              onClick={() => {
                                setTalismanOrder(null);
                                setTalismanWechatPay(null);
                                sessionStorage.removeItem('pendingTalismanOrderId');
                              }}
                            >
                              返回
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="talisman-tier-chip"
                              onClick={() => setTalismanTierPickerOpen((v) => !v)}
                              aria-expanded={talismanTierPickerOpen}
                            >
                              <span className="talisman-tier-chip-price">¥{selectedTalismanTier.price}</span>
                              <span className="talisman-tier-chip-label">{selectedTalismanTier.payLabel}</span>
                              <span className="talisman-tier-chip-action">{talismanTierPickerOpen ? '收起' : '更换档位'}</span>
                            </button>
                            {talismanTierPickerOpen && (
                              <div className="talisman-pricing talisman-pricing--compact">
                                {talismanPricingOptions.map((item) => (
                                  <button
                                    key={item.id}
                                    type="button"
                                    className={`pricing-option ${item.recommended ? 'recommended' : ''} ${talismanForm.tier === item.id ? 'active' : ''}`}
                                    onClick={() => setTalismanForm({ ...talismanForm, tier: item.id })}
                                  >
                                    <span className="price-tag">¥{item.price}</span>
                                    <span className="price-desc">{item.desc}</span>
                                    {item.recommended && <span className="recommend-badge">推荐</span>}
                                    {talismanForm.tier === item.id && <span className="selected-badge">已选</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                            <button
                              type="button"
                              className={`talisman-pay-method talisman-pay-method--active ${talismanForm.tier === 'hd' ? 'btn-gold' : ''}`}
                              disabled={talismanPayLoading}
                              onClick={() => handleTalismanPayment(talismanForm.tier)}
                            >
                              {talismanPayLoading ? '处理中…' : `支付 ¥${selectedTalismanTier.price} · 去水印`}
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className="talisman-link-muted"
                          onClick={() => {
                            setTalismanPreview(null);
                            setTalismanOrder(null);
                            setTalismanWechatPay(null);
                            setTalismanTierPickerOpen(false);
                            sessionStorage.removeItem('pendingTalismanOrderId');
                          }}
                        >
                          重新生成
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="talisman-download">
                    <p className="success-message">已解锁 · 可下载无水印图</p>
                    <button className="btn-primary btn-large" onClick={downloadTalisman}>
                      下载壁纸
                    </button>
                    <button className="btn-secondary" onClick={() => setTalismanPreview(null)}>
                      再生成一张
                    </button>
                  </div>
                )}

                <div className="preview-container talisman-preview-canvas-wrap">
                  <canvas 
                    ref={previewCanvasRef}
                    width={1080}
                    height={1920}
                    className="talisman-canvas"
                  />
                  {talismanPreview.watermark && (
                    <div className="watermark">云端问道 · 预览版</div>
                  )}
                </div>
                
                <div className="talisman-info">
                  <h3>✨ {talismanPreview.title}</h3>
                  <p className="talisman-blessing">{talismanPreview.blessing}</p>
                  <div className="talisman-meta">
                    <span>{talismanPreview.name}</span>
                    <span>{new Date().toLocaleDateString('zh-CN')}</span>
                    {talismanPreview.bazi && <span>八字 {talismanPreview.bazi}</span>}
                  </div>
                  <details className="talisman-disclaimer-fold talisman-disclaimer-fold--inline">
                    <summary>解读说明</summary>
                    <p>
                      文案为文化向解读，仅供娱乐与自我提醒；重要决策与身体不适请咨询专业人士。八字仅作个性化点缀，不具效力依据。
                    </p>
                  </details>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 12, padding: '8px 12px' }}
                      onClick={async () => {
                        if (!talismanPreview?.title || !talismanPreview?.blessing) {
                          alert('还没有可分享的灵符文案');
                          return;
                        }
                        try {
                          const r = await requestApi('/share/create', {
                            method: 'POST',
                            body: JSON.stringify({
                              userId,
                              type: 'talisman',
                              payload: {
                                title: talismanPreview.title,
                                blessing: talismanPreview.blessing,
                                type: talismanPreview.type,
                                style: talismanPreview.style,
                                name: talismanPreview.name
                              }
                            })
                          });
                          const url = r?.url;
                          if (!url) throw new Error('share url missing');
                          await navigator.clipboard.writeText(url);
                          alert('分享链接已复制');
                        } catch (e) {
                          alert('生成分享链接失败，请稍后重试');
                        }
                      }}
                    >
                      生成分享链接
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 隐藏的画布用于生成 */}
            <canvas ref={canvasRef} style={{display: 'none'}} />
          </div>
        )}

        {activeTab === 'yunxiang' && (
          <YunxiangPanel
            userId={userId}
            requestApi={requestApi}
            fetchUserInfo={fetchUserInfo}
            userInfo={userInfo}
          />
        )}

        {activeTab === 'share' && (
          <SharePage requestApi={requestApi} />
        )}

        {/* 灵兽传音 */}
        {activeTab === 'spirit' && (
          <div className="panel spirit-panel">
            <h2 className="panel-title">🐉 灵兽传音</h2>
            <p className="panel-subtitle">AI傲娇灵兽在线陪聊 · 打开声音就能听我念给你听~</p>
            <p className="panel-subtitle spirit-panel-hint" style={{ fontSize: '0.86em', opacity: 0.9, marginTop: 8, lineHeight: 1.6 }}>
              📌 次数小本本：每点一次「发送」、只要我成功回你一整条，就算 1 次（带图也算一队哦）~ 每天默认 3 通，修行者及以上就不限啦，跨自然日清零。
            </p>
            <p className="panel-subtitle spirit-panel-hint" style={{ fontSize: '0.86em', opacity: 0.85, marginTop: 6, lineHeight: 1.6 }}>
              🛡️ 人家是 AI 角色扮演 + 语音合成，陪你吐槽哄你开心可以，不能代替医生、律师，也不作确定性算命或人生担保啦~(´･ω･`)
            </p>
            
            <div className="spirit-container">
              {/* 灵兽形象区域 */}
              <div className={`spirit-avatar ${spiritMood} ${spiritTyping ? 'typing' : ''}`}>
                <div className="spirit-character">
                  {spiritMood === 'happy' && '😸'}
                  {spiritMood === 'angry' && '😾'}
                  {spiritMood === 'shy' && '😺'}
                  {spiritMood === 'sad' && '😿'}
                  {spiritMood === 'idle' && '😼'}
                </div>
                <div className="spirit-aura"></div>
                {spiritTyping && <div className="typing-indicator">✨</div>}
              </div>
              
              {/* 语音控制 */}
              <div className="spirit-voice-control">
                <button 
                  className={`voice-toggle ${spiritVoiceEnabled ? 'on' : 'off'}`}
                  onClick={() => setSpiritVoiceEnabled(!spiritVoiceEnabled)}
                >
                  {spiritVoiceEnabled ? '🔊 声音开启' : '🔇 声音关闭'}
                </button>
                <select 
                  value={spiritVoiceType} 
                  onChange={(e) => setSpiritVoiceType(e.target.value)}
                  className="voice-type-select"
                  title={ttsVoices.find(v => v.key === spiritVoiceType)?.description || ''}
                >
                  {ttsEnabled && ttsVoices.length > 0 ? (
                    ttsVoices.map(voice => (
                      <option key={voice.key} value={voice.key}>
                        {voice.displayName}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="cute">🎀 甜妹音</option>
                      <option value="cool">😎 酷姐音</option>
                      <option value="warm">🌸 温柔音</option>
                    </>
                  )}
                </select>
                {spiritLastTtsError ? (
                  <div style={{ marginLeft: 10, fontSize: 12, opacity: 0.85 }}>
                    语音播放失败，请重试
                  </div>
                ) : null}
              </div>
              
              {/* 聊天记录 */}
              <div className="spirit-chat">
                {spiritMessages.length === 0 ? (
                  <div className="spirit-welcome">
                    <div className="welcome-bubble">
                      <p>哼！终于想起来召唤本灵兽了？</p>
                      <p>我可不是随叫随到的！不过...既然你都来了，勉强陪你聊两句吧~</p>
                      <p style={{fontSize: '0.88em', opacity: 0.82, marginTop: 6}}>（小声）其实是 AI 灵兽啦，开声音就是念给你听，别当真会法术哦~</p>
                      <p style={{fontSize: '0.9em', opacity: 0.8, marginTop: 8}}>💫 点下面的快捷语，或者自己打字发我~</p>
                    </div>
                  </div>
                ) : (
                  <div className="spirit-messages">
                    {spiritMessages.map((msg, i) => (
                      <div key={i} className={`spirit-msg ${msg.role}`}>
                        {msg.role === 'spirit' && (
                          <div className="spirit-avatar-small">
                            {msg.mood === 'happy' && '😸'}
                            {msg.mood === 'angry' && '😾'}
                            {msg.mood === 'shy' && '😺'}
                            {msg.mood === 'sad' && '😿'}
                            {(!msg.mood || msg.mood === 'idle') && '😼'}
                          </div>
                        )}
                        <div className={`msg-bubble ${msg.role}`}>
                          {msg.role === 'spirit' ? (
                            <div className="spirit-bubble-formatted">
                              {formatSpiritMessageContent(msg.content).map((line, idx) => (
                                <p
                                  key={`${i}-${idx}`}
                                  className={`spirit-bubble-line ${/^【.*】/.test(line) ? 'title' : ''}`}
                                >
                                  {line}
                                </p>
                              ))}
                            </div>
                          ) : (
                            msg.content
                          )}
                          {msg.image && (
                            <div className="msg-image">
                              <img src={msg.image} alt="用户发送的图片" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={spiritEndRef} />
                  </div>
                )}
              </div>
              
              {/* 快捷回复 */}
              <div className="spirit-quick-replies">
                {spiritQuickReplies.map((reply, i) => (
                  <button
                    key={i}
                    className="quick-reply-btn"
                    onClick={() => {
                      setSpiritInput(reply.text);
                      setSpiritMood(reply.mood);
                    }}
                  >
                    <span>{reply.icon}</span>
                    <span>{reply.text}</span>
                  </button>
                ))}
              </div>
              
              {/* 图片预览 */}
              {spiritImagePreview && (
                <div className="spirit-image-preview">
                  <img src={spiritImagePreview} alt="预览" />
                  <button 
                    className="clear-image-btn" 
                    onClick={clearSpiritImage}
                    title="取消发送"
                  >
                    ✕
                  </button>
                </div>
              )}
              
              {/* 输入区域 */}
              <div className="spirit-input-area">
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={spiritImageInputRef}
                  onChange={handleSpiritImageSelect}
                />
                <button
                  className="spirit-image-btn"
                  onClick={() => spiritImageInputRef.current?.click()}
                  disabled={spiritLoading}
                  title="发送图片"
                >
                  📷
                </button>
                <input
                  type="text"
                  className="spirit-input"
                  placeholder={spiritImagePreview ? "给图片配句话吧（可选）..." : "想对灵兽说什么...（它可能会怼你哦）"}
                  value={spiritInput}
                  onChange={(e) => setSpiritInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSpiritChat()}
                  disabled={spiritLoading}
                />
                <button
                  className="spirit-send-btn"
                  onClick={handleSpiritChat}
                  disabled={(!spiritInput.trim() && !spiritImagePreview) || spiritLoading}
                >
                  {spiritLoading ? '✨' : '发送'}
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <button
                  className="btn-secondary"
                  style={{ fontSize: 12, padding: '8px 12px' }}
                  onClick={async () => {
                    const lastSpirit = [...spiritMessages].reverse().find(m => m.role === 'spirit');
                    const lastUser = [...spiritMessages].reverse().find(m => m.role !== 'spirit');
                    if (!lastSpirit?.content) {
                      alert('还没有可分享的回复');
                      return;
                    }
                    try {
                      const r = await requestApi('/share/create', {
                        method: 'POST',
                        body: JSON.stringify({
                          userId,
                          type: 'spirit',
                          payload: { userText: lastUser?.content || '', reply: lastSpirit.content }
                        })
                      });
                      const url = r?.url;
                      if (!url) throw new Error('share url missing');
                      await navigator.clipboard.writeText(url);
                      alert('分享链接已复制');
                    } catch (e) {
                      alert('生成分享链接失败，请稍后重试');
                    }
                  }}
                >
                  生成分享链接
                </button>
              </div>
              
              {/* 功能说明 */}
              <div className="spirit-tips">
                <p>💡 灵兽特点：嘴硬心软 · 贫嘴傲娇 · 关键时刻超护主</p>
                <p>🎙️ {ttsEnabled ? '✨ 已开启语音播放：我的回复会用合成语音念给你听（支持多种音色）~' : '打开声音：把我的回复用语音念出来（AI 合成音）~'}</p>
                <p>📷 点相机，我能看图吐槽；发图也会占用 1 次传音额度哦~</p>
                <p>🧾 再强调下：每天 3 通 = 3 次成功「发送→回复」，修行者及以上不限~</p>
              </div>
            </div>
          </div>
        )}

        {/* 反馈建议 */}
        {activeTab === 'feedback' && (
          <div className="panel">
            <h2 className="panel-title">💬 反馈建议</h2>
            <p className="panel-subtitle">把 Bug、建议和需求告诉我们，我们会尽快处理</p>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">反馈类型</label>
              <select
                className="form-input"
                value={feedbackForm.type}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="bug">Bug 问题</option>
                <option value="feature">新功能需求</option>
                <option value="suggestion">产品建议</option>
                <option value="complaint">投诉建议</option>
                <option value="other">其他</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">反馈内容</label>
              <textarea
                className="form-input"
                rows={5}
                placeholder="请详细描述你的问题或建议"
                value={feedbackForm.content}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">联系方式（可选）</label>
              <input
                className="form-input"
                placeholder="手机号 / 微信 / 邮箱"
                value={feedbackForm.contact}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, contact: e.target.value }))}
              />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn-primary" style={{ marginTop: 0, flex: 1 }} onClick={submitFeedback} disabled={feedbackLoading}>
                {feedbackLoading ? '提交中...' : '提交反馈'}
              </button>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={loadFeedbackHistory} disabled={feedbackLoading}>
                刷新我的反馈
              </button>
            </div>
            <div className="result-card" style={{ marginTop: 24 }}>
              <h4 style={{ marginBottom: 12 }}>我的反馈记录</h4>
              {feedbackHistory.length > 0 ? (
                feedbackHistory.map(item => (
                  <div key={item.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--card-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <strong>{item.type}</strong>
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{new Date(item.createdAt).toLocaleString('zh-CN')}</span>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>{item.content}</p>
                    <p style={{ fontSize: 12 }}>
                      状态：<span style={{ color: item.status === 'resolved' ? 'var(--success)' : 'var(--warning)' }}>{item.status}</span>
                    </p>
                    {item.reply?.content && (
                      <p style={{ fontSize: 13, color: 'var(--primary-light)', marginTop: 6 }}>官方回复：{item.reply.content}</p>
                    )}
                  </div>
                ))
              ) : (
                <p style={{ color: 'var(--text-tertiary)' }}>暂无反馈记录</p>
              )}
            </div>
          </div>
        )}

        {/* 管理员登录页（内部使用） */}
        {activeTab === 'adminPortal' && (
          <div className="panel">
            <h2 className="panel-title">🛡️ 管理后台登录</h2>
            <p className="panel-subtitle">仅内部运营可见，访问路径：/admin-login</p>
            {!adminPortalData ? (
              <div className="result-card">
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label className="form-label">管理员密码</label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder="请输入管理员密码"
                    value={adminPortalCode}
                    onChange={(e) => setAdminPortalCode(e.target.value)}
                  />
                </div>
                <button className="btn-primary" style={{ marginTop: 0 }} onClick={loginAdminPortal}>
                  登录后台
                </button>
                {adminPortalError && <p className="error-text" style={{ marginTop: 12 }}>{adminPortalError}</p>}
              </div>
            ) : (
              <>
                <div className="stats-grid">
                  <div className="stat-card"><span className="stat-num">{adminPortalData.totalUsers || 0}</span><span>总用户</span></div>
                  <div className="stat-card"><span className="stat-num">{adminPortalData.vipCount || 0}</span><span>VIP用户</span></div>
                  <div className="stat-card"><span className="stat-num">{adminPortalData.totalCalls || 0}</span><span>总调用</span></div>
                  <div className="stat-card"><span className="stat-num">¥{adminPortalData.platformRevenue || adminPortalData.totalRevenue || 0}</span><span>平台总收入(历史)</span></div>
                  <div className="stat-card"><span className="stat-num">¥{adminPortalData.paymentRevenue || 0}</span><span>支付流水收入</span></div>
                </div>
                {adminPortalPayStats && (
                  <div className="result-card" style={{ marginTop: 24 }}>
                    <h4 style={{ marginBottom: 12 }}>💰 支付统计</h4>
                    <p>流水总收入：¥{adminPortalPayStats.totalRevenue || 0}</p>
                    <p>订单总数：{adminPortalPayStats.totalOrders || 0}</p>
                    <p>已支付：{adminPortalPayStats.totalPaid || 0}</p>
                    <p>今日收入：¥{adminPortalPayStats.todayRevenue || 0}</p>
                    <p>本月收入：¥{adminPortalPayStats.thisMonthRevenue || 0}</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        </main>
      </div>
    </div>
  );
}

// ==================== 结果展示组件 ====================

function BaziResult({ data, onShare }) {
  const [viewMode, setViewMode] = useState('simple');
  const WUXING_COLOR = { '金': '#C9B037', '木': '#4CAF50', '水': '#2196F3', '火': '#FF5722', '土': '#795548' };
  const w = { '甲': '木', '乙': '木', '丙': '火', '丁': '火', '戊': '土', '己': '土', '庚': '金', '辛': '金', '壬': '水', '癸': '水' };
  const xi = data.dayMaster?.xiJi;
  const shiShenSummary = data.dayMaster?.shiShenSummary;
  const provenance = data.analysis?.provenance;
  const hasProData = Boolean(xi && shiShenSummary);

  return (
    <div className="result-section">
      {hasProData && (
        <div className="bazi-view-toggle" role="tablist" aria-label="八字结果展示模式">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'simple'}
            className={`bazi-view-toggle-btn ${viewMode === 'simple' ? 'active' : ''}`}
            onClick={() => setViewMode('simple')}
          >
            简版
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'pro'}
            className={`bazi-view-toggle-btn ${viewMode === 'pro' ? 'active' : ''}`}
            onClick={() => setViewMode('pro')}
          >
            专业版
          </button>
        </div>
      )}
      <div className="daymaster-card">
        <div className="daymaster-header">
          <span>日主能量分析</span>
          <span className="score">{data.dayMaster?.score || 75}%</span>
        </div>
        <div className="daymaster-stats">
          <div><span>天干</span><strong style={{color: WUXING_COLOR[data.dayMaster?.wuxing]}}>{data.dayMaster?.gan}</strong></div>
          <div><span>地支</span><strong>{data.dayMaster?.zhi}</strong></div>
          <div><span>五行</span><strong style={{color: WUXING_COLOR[data.dayMaster?.wuxing]}}>{data.dayMaster?.wuxing}</strong></div>
          <div><span>强弱</span><strong>{data.dayMaster?.strength}</strong></div>
        </div>
      </div>
      
      <div className="bazi-grid">
        {data.bazi && Object.entries(data.bazi).map(([k, v]) => (
          <div key={k} className="bazi-card">
            <div className="bazi-label">{k === 'year' ? '年柱' : k === 'month' ? '月柱' : k === 'day' ? '日柱' : '时柱'}</div>
            <div className="bazi-chars" style={{color: WUXING_COLOR[w[v?.[0]]]}}>{v}</div>
            <span className="bazi-wuxing-tag" style={{background: WUXING_COLOR[w[v?.[0]]] + '20', color: WUXING_COLOR[w[v?.[0]]]}}>{w[v?.[0]]}</span>
          </div>
        ))}
      </div>
      
      {viewMode === 'pro' && hasProData ? (
        <>
          <div className="bazi-pro-card">
            <div className="bazi-pro-card-kicker">结论</div>
            <div className="bazi-pro-card-body">{data.analysis?.summary}</div>
          </div>
          <div className="bazi-pro-card">
            <div className="bazi-pro-card-kicker">依据（规则引擎 · 扶抑简化）</div>
            <div className="bazi-pro-card-body">
              <p className="bazi-pro-line"><span className="bazi-pro-label">说明</span>{xi.rationale}</p>
              <p className="bazi-pro-line"><span className="bazi-pro-label">主喜五行</span>{xi.primaryFavorable.join('、')}</p>
              <p className="bazi-pro-line"><span className="bazi-pro-label">宜避免过重</span>{xi.avoid.join('、')}</p>
              <p className="bazi-pro-line"><span className="bazi-pro-label">十神组</span>天干与地支藏干约计</p>
              <ul className="bazi-pro-shishen-list">
                {Object.entries(shiShenSummary).map(([k, v]) => (
                  <li key={k}><strong>{k}</strong> {v} 处</li>
                ))}
              </ul>
            </div>
          </div>
          {data.analysis?.suggestions?.length > 0 && (
            <div className="bazi-pro-card">
              <div className="bazi-pro-card-kicker">建议（规则生成 · 非 AI）</div>
              <div className="bazi-pro-card-body suggestions-row">
                {data.analysis.suggestions.map((s, i) => (
                  <span key={i} className="suggestion-tag">{s}</span>
                ))}
              </div>
            </div>
          )}
          {data.analysis?.fullReport && (
            <div className="ai-report">
              <div className="ai-report-header">
                <span>🤖 AI 解读（生成内容）</span>
                <span className="model-tag">智能引擎</span>
              </div>
              <div className="ai-report-content">
                <ReadableRichText text={data.analysis.fullReport} maxHeight={420} />
              </div>
            </div>
          )}
          {provenance?.disclaimer && (
            <div className="bazi-pro-card bazi-provenance-card">
              <div className="bazi-pro-card-kicker">可信度说明</div>
              <p className="bazi-pro-card-body subtle">{provenance.disclaimer}</p>
              <div className="bazi-pro-meta">
                规则版本 {provenance.ruleEngineVersion || '—'} · 开运提示：{provenance.suggestionsSource === 'rule_engine' ? '规则引擎' : '—'} · 正文：{provenance.fullReportSource === 'ai_generated' ? 'AI生成' : '—'}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {data.analysis?.fullReport && (
            <div className="ai-report">
              <div className="ai-report-header">
                <span>🤖 AI命理大师解读</span>
                <span className="model-tag">智能引擎</span>
              </div>
              <div className="ai-report-content">
                <ReadableRichText text={data.analysis.fullReport} maxHeight={420} />
              </div>
            </div>
          )}
          {data.analysis?.suggestions && (
            <div className="suggestions-row">
              {data.analysis.suggestions.map((s, i) => (
                <span key={i} className="suggestion-tag">{s}</span>
              ))}
            </div>
          )}
        </>
      )}
      
      <div className="share-buttons">
        <button className="btn-share" onClick={onShare}>
          <span>📋</span> 复制分享
        </button>
      </div>
      
      <div className="usage-info">剩余免费次数: {data.remaining > 0 ? data.remaining : 'VIP无限'}</div>
    </div>
  );
}

function FengShuiResult({ data, onShare }) {
  const wuxingColors = { '木': '#2ECC71', '火': '#E74C3C', '土': '#F39C12', '金': '#ECF0F1', '水': '#3498DB' };
  const prov = data.provenance;

  return (
    <div className="result-section">
      <div className="fengshui-score">
        <div className="score-circle">
          <span className="score-num">{data.score ?? '—'}</span>
          <span className="score-label">规则参考分</span>
        </div>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 8, lineHeight: 1.55 }}>
        分数由问卷与勾选项确定性计算，与正文 AI 解读一致；非精密勘测结论。
      </p>

      {data.baziLink && (
        <div className="result-card" style={{ marginTop: 16 }}>
          <div className="result-card-title">🔗 八字联动（参照）</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.65, margin: 0 }}>{data.baziLink.summary}</p>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '8px 0 0' }}>
            {data.baziLink.pillars.year} {data.baziLink.pillars.month} {data.baziLink.pillars.day} {data.baziLink.pillars.hour}
          </p>
        </div>
      )}

      {data.wuxing && (
        <div className="result-card" style={{ marginTop: 16 }}>
          <div className="result-card-title">🔮 环境与朝向参考</div>
          {data.wuxing.floorTailNote && (
            <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 10 }}>{data.wuxing.floorTailNote}</p>
          )}
          <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
            <div style={{ flex: 1, textAlign: 'center', padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: wuxingColors[data.wuxing.floor] || '#3498DB' }}>
                {data.wuxing.floor}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>楼层尾数五行</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{data.wuxing.mainDirection}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>房屋朝向</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{data.wuxing.doorDirection}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>入户门朝向</div>
            </div>
          </div>
        </div>
      )}

      {data.layoutRuleLines && data.layoutRuleLines.length > 0 && (
        <div className="result-card" style={{ marginTop: 16 }}>
          <div className="result-card-title">📌 布局规则提示（非 AI）</div>
          <div className="result-card-content" style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--text-secondary)' }}>
            {data.layoutRuleLines.map((line, i) => (
              <p key={i} style={{ marginBottom: 8 }}>{line}</p>
            ))}
          </div>
        </div>
      )}

      {data.report && (
        <div className="ai-report">
          <div className="ai-report-header"><span>🏠 AI 居住与环境解读</span></div>
          <div className="ai-report-content">
            <ReadableRichText text={data.report} maxHeight={420} />
          </div>
        </div>
      )}

      {data.tips && (
        <div className="result-card">
          <div className="result-card-title">💡 结合问卷的建议</div>
          <div className="result-card-content">
            {data.tips.map((tip, i) => (
              <p key={i}>{i + 1}. {tip}</p>
            ))}
          </div>
        </div>
      )}

      {prov?.disclaimer && (
        <div className="bazi-pro-card bazi-provenance-card" style={{ marginTop: 16 }}>
          <div className="bazi-pro-card-kicker">可信度说明</div>
          <p className="bazi-pro-card-body subtle" style={{ margin: 0 }}>{prov.disclaimer}</p>
          <div className="bazi-pro-meta">
            规则 {prov.ruleEngineVersion || '—'} · 分数：{prov.scoreSource === 'rule_engine_deterministic' ? '确定性规则' : '—'} · 八字联动：{prov.baziLinked ? '是' : '否'}
          </div>
        </div>
      )}

      {typeof onShare === 'function' && (
        <div className="share-buttons">
          <button className="btn-share" onClick={onShare}>
            <span>📋</span> 生成分享链接
          </button>
        </div>
      )}

      <div className="usage-info">剩余免费次数: {data.remaining > 0 ? data.remaining : 'VIP无限'}</div>
    </div>
  );
}

function AuraResult({ data, onShare }) {
  const auraColors = {
    '紫色': '#9B59B6', '蓝色': '#3498DB', '绿色': '#2ECC71', '金色': '#F1C40F',
    '粉色': '#E91E63', '白色': '#ECF0F1', '橙色': '#E67E22', '青色': '#1ABC9C',
    '灰色': '#95A5A6', '褐色': '#8B4513', '红色': '#E74C3C', '黄色': '#F1C40F',
    '黑色': '#2C3E50', '多彩': 'linear-gradient(135deg, #9B59B6, #3498DB, #2ECC71)'
  };
  const prov = data.provenance;
  const tally = data.ruleVote?.tally;
  const dc = data.dominantColor || '紫色';
  const ringColor = (() => {
    const c = auraColors[dc];
    if (typeof c === 'string' && !c.includes('gradient')) return c;
    return '#9B59B6';
  })();

  return (
    <div className="result-section">
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6, marginBottom: 12 }}>
        主导色与指数为问卷规则计算；以下为象征与自我觉察参考，非仪器或临床测量。
      </p>
      <div className="aura-display">
        <div className="aura-ring" style={{
          borderColor: ringColor,
          background: `radial-gradient(circle, ${ringColor}33 0%, transparent 70%)`,
          color: ringColor
        }}>
          <span className="aura-color">{data.dominantColor || '—'}</span>
          <span className="aura-level">{data.energyLevel ?? '—'}%</span>
        </div>
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 8 }}>身心节律指数（规则 · 越高越稳）</p>

      {data.stressTendency != null && (
        <div className="result-card" style={{ marginTop: 16 }}>
          <div className="result-card-title">📊 压力倾向（自评）</div>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>0–100，越高表示主观压力越大，非医学诊断。</p>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${data.stressTendency}%`,
                background: data.stressTendency > 60 ? '#E74C3C' : data.stressTendency > 35 ? 'var(--primary)' : 'var(--success)'
              }}
            />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8, color: 'var(--text-secondary)' }}>{data.stressTendency}</div>
        </div>
      )}

      {tally && (
        <div className="result-card" style={{ marginTop: 16 }}>
          <div className="result-card-title">🎨 颜色投票（规则层）</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            {Object.entries(tally)
              .sort((a, b) => b[1] - a[1])
              .map(([c, n]) => (
                <span key={c} style={{ marginRight: 12 }}>{c} <strong>{n}</strong></span>
              ))}
          </div>
        </div>
      )}

      {data.colorMeaning && (
        <div className="result-card" style={{ marginTop: 16, textAlign: 'left' }}>
          <div className="result-card-title">象征含义（规则参考）</div>
          <div style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--text-secondary)' }}>{data.colorMeaning}</div>
        </div>
      )}

      {data.advice && (
        <div className="result-card">
          <div className="result-card-title">💡 可行小贴士（非医疗）</div>
          <div className="result-card-content">
            <p style={{ lineHeight: 1.65 }}>{data.advice}</p>
          </div>
        </div>
      )}

      {data.analysis && (
        <div className="ai-report">
          <div className="ai-report-header"><span>✨ AI 解读（生成内容）</span></div>
          <div className="ai-report-content">
            {data.analysis.split('\n').map((line, i) => (line.trim() ? <p key={i}>{line}</p> : <br key={i} />))}
          </div>
        </div>
      )}

      {prov?.disclaimer && (
        <div className="bazi-pro-card bazi-provenance-card" style={{ marginTop: 16 }}>
          <div className="bazi-pro-card-kicker">可信度说明</div>
          <p className="bazi-pro-card-body subtle" style={{ margin: 0 }}>{prov.disclaimer}</p>
          <div className="bazi-pro-meta">规则 {prov.ruleEngineVersion || '—'} · 主导色：{prov.dominantColorSource === 'rule_engine_weighted_vote' ? '加权投票' : '—'}</div>
        </div>
      )}

      {typeof onShare === 'function' && (
        <div className="share-buttons">
          <button className="btn-share" onClick={onShare}>
            <span>📋</span> 生成分享链接
          </button>
        </div>
      )}

      <div className="usage-info">剩余免费次数: {data.remaining > 0 ? data.remaining : 'VIP无限'}</div>
    </div>
  );
}

function DailyResult({ data }) {
  return (
    <div className="result-section daily-result">
      {/* 紧凑的运势头部 - 减少顶部空白 */}
      <div className="daily-result-header">
        <div className="daily-result-score">
          <span className="daily-result-num">{data.overall || 85}%</span>
          <span className="daily-result-label">今日运势</span>
        </div>
        <div className="daily-result-quick">
          <div className="daily-result-item">
            <span className="daily-result-icon">🎨</span>
            <span className="daily-result-text">{data.luckyColor || '金色'}</span>
          </div>
          <div className="daily-result-item">
            <span className="daily-result-icon">🔢</span>
            <span className="daily-result-text">{data.luckyNumbers?.join(', ') || '3, 7'}</span>
          </div>
          <div className="daily-result-item">
            <span className="daily-result-icon">🧭</span>
            <span className="daily-result-text">{data.luckyDirection || '东南'}</span>
          </div>
        </div>
      </div>
      
      {/* 宜忌卡片 */}
      <div className="daily-yiji-row">
        <div className="daily-yiji-card yi">
          <div className="daily-yiji-title">✅ 宜</div>
          <div className="daily-yiji-content">
            {(data.yi || ['签约', '出行', '学习', '社交']).map((item, i) => (
              <span key={i} className="daily-yiji-tag">{item}</span>
            ))}
          </div>
        </div>
        <div className="daily-yiji-card ji">
          <div className="daily-yiji-title">❌ 忌</div>
          <div className="daily-yiji-content">
            {(data.ji || ['冲动决策', '大额投资', '争吵']).map((item, i) => (
              <span key={i} className="daily-yiji-tag">{item}</span>
            ))}
          </div>
        </div>
      </div>
      
      {/* 详细运势 */}
      {data.horoscope && (
        <div className="ai-report daily-horoscope">
          <div className="ai-report-header"><span>📅 今日运势详解</span></div>
          <div className="ai-report-content">
            {data.horoscope.split('\n').map((line, i) => line.trim() ? <p key={i}>{line}</p> : <br key={i} />)}
          </div>
        </div>
      )}
      
      {/* 温馨提示 */}
      <div className="result-card daily-tip">
        <div className="result-card-title">💡 开运建议</div>
        <div className="result-card-content">
          <p>• 今日幸运色为{data.luckyColor || '金色'}，可穿戴相应颜色衣物增强运势</p>
          <p>• 重要决策可选择{data.luckyNumbers?.[0] || '3'}:{data.luckyNumbers?.[1] || '7'}等幸运时刻</p>
          <p>• 往{data.luckyDirection || '东南'}方向出行更易遇到贵人</p>
        </div>
      </div>
      
      <div className="usage-info">剩余免费次数: {data.remaining > 0 ? data.remaining : 'VIP无限'}</div>
    </div>
  );
}

function DreamResult({ data, onShare }) {
  const dreamDisclaimer =
    data.disclaimer ||
    '本解读仅供文化兴趣与心理自助参考，不构成医疗、心理咨询或命运断言。若长期失眠、反复噩梦或伴随强烈痛苦，建议咨询专业医生或心理咨询师。';
  return (
    <div className="result-section">
      {data.dream && (
        <div className="result-card" style={{ marginBottom: 16, background: 'rgba(108,93,211,0.08)' }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>🌙 您描述的梦境</div>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>{data.dream}</div>
        </div>
      )}

      <div className="ai-report">
        <div className="ai-report-header"><span>🌙 专业梦境解析</span></div>
        <div className="ai-report-content">
          <ReadableRichText text={data.analysis} maxHeight={420} />
        </div>
      </div>

      <div className="result-card" style={{ marginTop: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>⚕️ {dreamDisclaimer}</div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        {data.emotion && (
          <span style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 20, fontSize: 12 }}>
            😌 情绪：{data.emotion}
          </span>
        )}
        {data.dreamTime && (
          <span style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 20, fontSize: 12 }}>
            ⏰ {data.dreamTime}
          </span>
        )}
        {data.symbolType && (
          <span style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 20, fontSize: 12 }}>
            🔮 主题：{data.symbolType}类
          </span>
        )}
      </div>

      <div className="share-buttons" style={{ marginTop: 20 }}>
        <button className="btn-share" onClick={onShare}>
          <span>📋</span> 复制分享
        </button>
      </div>

      <div className="usage-info">剩余免费次数: {data.remaining > 0 ? data.remaining : 'VIP无限'}</div>
    </div>
  );
}

function NameResult({ data, onShare }) {
  const { config } = data;
  const nameDisclaimer =
    data.disclaimer ||
    '本推荐仅供文化参考与起名灵感，不构成户籍或命理判决；最终用字请以登记机关与家庭共识为准。';

  return (
    <div className="result-section">
      {/* 取名配置说明 */}
      {config && (
        <div className="result-card" style={{ marginBottom: 20 }}>
          <div className="result-card-title">📋 取名配置</div>
          <div className="result-card-content">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {config.nameLength === '4' && config.maSurname && (
                <span className="suggestion-tag">四字名（{data.surname}{config.maSurname}）</span>
              )}
              {config.useBaifen && config.baifenChar && (
                <span className="suggestion-tag">辈分字：{config.baifenChar}</span>
              )}
              {config.fixedType !== 'none' && config.fixedChar && (
                <span className="suggestion-tag">
                  定字：{config.fixedType === 'first' ? '首' : config.fixedType === 'middle' ? '中' : '末'}字固定为"{config.fixedChar}"
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="ai-report">
        <div className="ai-report-header"><span>✨ AI取名分析</span></div>
        <div className="ai-report-content">
          {data.analysis?.split('\n').map((line, i) => line.trim() ? <p key={i}>{line}</p> : <br key={i} />)}
        </div>
      </div>
      
      {data.names && (
        <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
          {data.names.map((name, i) => (
            <div key={i} className="result-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px', gap: 16 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 22, fontWeight: 700 }}>{name.fullName || name.surname + name.name}</span>
                  {name.source === 'llm' && (
                    <span style={{ fontSize: 10, padding: '2px 8px', background: 'rgba(46,204,113,0.15)', borderRadius: 10, color: 'var(--success)' }}>AI方案</span>
                  )}
                  {name.source === 'fallback' && (
                    <span style={{ fontSize: 10, padding: '2px 8px', background: 'rgba(255,255,255,0.06)', borderRadius: 10, color: 'var(--text-tertiary)' }}>系统备选</span>
                  )}
                  {name.features?.map((feature, fi) => (
                    <span key={fi} style={{ fontSize: 11, padding: '2px 8px', background: 'rgba(108,93,211,0.2)', borderRadius: 10, color: 'var(--primary-light)' }}>
                      {feature}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{name.meaning}</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8, lineHeight: 1.55 }}>
                  <div>五行（民俗字库）：<span style={{ color: 'var(--primary-light)' }}>{name.wuxingSummary || name.wuxing || '—'}</span></div>
                  <div style={{ marginTop: 4 }}>喜用对照：<span style={{ color: 'var(--text-secondary)' }}>{name.baziAlignment || '—'}</span></div>
                  {name.phoneticNoteText && (
                    <div style={{ marginTop: 6, color: '#E67E22', fontSize: 11 }}>⚠️ {name.phoneticNoteText}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="result-card" style={{ marginTop: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>📌 {nameDisclaimer}</div>
      </div>
      
      <div className="share-buttons">
        <button className="btn-share" onClick={onShare}>
          <span>📋</span> 复制结果
        </button>
      </div>
      
      <div className="usage-info">剩余免费次数: {data.remaining > 0 ? data.remaining : 'VIP无限'}</div>
    </div>
  );
}

function DateResult({ data }) {
  const dateDisclaimer =
    data.disclaimer ||
    '本结果依据通书类数据作民俗参考，非法律文书；冲合为简化说法，请以实际安排为准。';

  return (
    <div className="result-section">
      <div className="ai-report">
        <div className="ai-report-header"><span>📅 专业择日分析</span></div>
        <div className="ai-report-content">
          {data.analysis?.split('\n').map((line, i) => line.trim() ? <p key={i}>{line}</p> : <br key={i} />)}
        </div>
      </div>

      {data.ruleSummary && (
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 12, lineHeight: 1.5, padding: '0 4px' }}>
          📐 {data.ruleSummary}
        </div>
      )}

      {data.dates && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
          {data.dates.map((date, i) => (
            <div key={i} className="result-card" style={{
              padding: 16,
              border: i === 0 ? '2px solid var(--accent)' : '1px solid var(--card-border)',
              borderRadius: 12,
              background: i === 0 ? 'linear-gradient(135deg, rgba(255,215,0,0.08) 0%, rgba(255,165,0,0.05) 100%)' : 'var(--card-bg)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                <div style={{
                  width: 60, height: 60, borderRadius: 12,
                  background: i === 0 ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' : 'var(--card-bg)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  border: i === 0 ? '2px solid var(--accent)' : '1px solid var(--card-border)'
                }}>
                  <span style={{ fontSize: 12, opacity: 0.8 }}>{date.month}月</span>
                  <span style={{ fontSize: 20, fontWeight: 700 }}>{date.day}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                    {date.date} {i === 0 && <span style={{ color: 'var(--accent)', fontSize: 12 }}>★ 排序优先</span>}
                    {date.isGood && <span style={{ color: 'var(--success)', fontSize: 11, marginLeft: 8 }}>☀️ 较宜</span>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {date.lunar} · {date.ganZhi}日 · 日支{date.zodiac}
                    {date.zhiXing && <span style={{ marginLeft: 8 }}>· 建除「{date.zhiXing}」</span>}
                  </div>
                  {date.dayChongDesc && (
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                      日冲：{date.dayChongDesc}
                    </div>
                  )}
                  {date.suitable && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{date.suitable}</div>
                  )}
                  {date.clashInfo && (
                    <div style={{ fontSize: 11, color: '#E74C3C', marginTop: 4 }}>
                      ⚠️ {date.clashInfo}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: i === 0 ? 'var(--accent)' : 'var(--success)' }}>{date.score}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>参考分</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ color: 'var(--success)', marginRight: 4 }}>宜</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{date.yi}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ color: '#E74C3C', marginRight: 4 }}>忌</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{date.ji}</span>
                </div>
              </div>
              {(date.pengZu || date['彭祖百忌']) && (
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--card-border)' }}>
                  📜 彭祖百忌：{date.pengZu || date['彭祖百忌']}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="result-card" style={{ marginTop: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>📌 {dateDisclaimer}</div>
      </div>

      {data.totalCount > 0 && (
        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--text-tertiary)' }}>
          当前条件下共 {data.totalCount} 天
        </div>
      )}

      <div className="usage-info">剩余免费次数: {data.remaining > 0 ? data.remaining : 'VIP无限'}</div>
    </div>
  );
}

function FaceResult({ data, onShare }) {
  const [viewMode, setViewMode] = useState('simple');
  const prov = data.provenance;
  const hasPro = Boolean(data.palaceBrief?.length || data.features?.[0]?.ruleHint);

  const renderAnalysisBody = () => {
    const t = data.analysis || '';
    if (viewMode === 'pro' && /^###\s/m.test(t)) {
      const blocks = t.split(/\n(?=###\s)/).filter(Boolean);
      return blocks.map((block, bi) => {
        const m = block.match(/^###\s+(.+)(?:\n|$)([\s\S]*)$/);
        if (m) {
          const body = (m[2] || '').trim();
          return (
            <div key={bi} className="bazi-pro-card" style={{ marginBottom: 12 }}>
              <div className="bazi-pro-card-kicker">{m[1].trim()}</div>
              <div className="bazi-pro-card-body">
                {body.split('\n').map((line, li) => (line.trim() ? <p key={li}>{line}</p> : <br key={li} />))}
              </div>
            </div>
          );
        }
        return (
          <div key={bi} className="ai-report-content">
            {block.split('\n').map((line, li) => (line.trim() ? <p key={li}>{line}</p> : <br key={li} />))}
          </div>
        );
      });
    }
    return t.split('\n').map((line, i) => (line.trim() ? <p key={i}>{line}</p> : <br key={i} />));
  };

  return (
    <div className="result-section">
      {hasPro && (
        <div className="bazi-view-toggle" role="tablist" aria-label="面相探微结果展示模式">
          <button
            type="button"
            className={`bazi-view-toggle-btn ${viewMode === 'simple' ? 'active' : ''}`}
            onClick={() => setViewMode('simple')}
          >
            简版
          </button>
          <button
            type="button"
            className={`bazi-view-toggle-btn ${viewMode === 'pro' ? 'active' : ''}`}
            onClick={() => setViewMode('pro')}
          >
            详版
          </button>
        </div>
      )}

      <div className="luck-overview" style={{ background: 'linear-gradient(135deg, rgba(108,93,211,0.1) 0%, rgba(74,63,191,0.05) 100%)' }}>
        <span className="luck-score">{data.score ?? '—'}</span>
        <span className="luck-label">面相探微综合倾向分</span>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 12, lineHeight: 1.6 }}>
        倾向分为问卷规则映射，便于对照；非精密测量。若已联动八字，命局以八字扶抑参考为主，面相探微作气质补充。
      </p>

      {data.baziLink && (
        <div className="result-card" style={{ marginTop: 16 }}>
          <div className="result-card-title">🔗 八字联动（参照）</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.65 }}>
            <p style={{ margin: '0 0 8px' }}>{data.baziLink.summary}</p>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>
              四柱：{data.baziLink.pillars.year} {data.baziLink.pillars.month} {data.baziLink.pillars.day} {data.baziLink.pillars.hour}
            </p>
          </div>
        </div>
      )}

      {data.features && (
        <div className="result-card" style={{ marginTop: 16 }}>
          <div className="result-card-title">📊 五官倾向分</div>
          <div className="result-card-content">
            {data.features.map((feature, i) => (
              <div key={i} style={{ marginBottom: viewMode === 'pro' && feature.ruleHint ? 16 : 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13 }}>{feature.name}：{feature.meaning}</span>
                  <span style={{ fontSize: 13, color: feature.score >= 85 ? 'var(--success)' : feature.score >= 75 ? 'var(--primary)' : '#E74C3C' }}>{feature.score}分</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${feature.score}%`, background: feature.score >= 85 ? 'var(--success)' : feature.score >= 75 ? 'var(--primary)' : '#E74C3C' }} />
                </div>
                {viewMode === 'pro' && feature.ruleHint && (
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6, lineHeight: 1.5 }}>相理提示：{feature.ruleHint}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.palaceBrief && data.palaceBrief.length > 0 && viewMode === 'pro' && (
        <div className="result-card" style={{ marginTop: 16 }}>
          <div className="result-card-title">📍 宫位简化提示（规则）</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.palaceBrief.map((p, i) => (
              <div key={i} style={{ padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8, fontSize: 12, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--primary-light)' }}>{p.tag}</strong> {p.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.sanTing && (
        <div className="result-card" style={{ marginTop: 16 }}>
          <div className="result-card-title">🔮 三停分析（规则 · 含自评加权）</div>
          {viewMode === 'pro' && data.sanTing.meta?.userBalance && (
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 10 }}>
              三停自评：{data.sanTing.meta.userBalance === 'balanced' ? '较均衡' : data.sanTing.meta.userBalance === 'upper' ? '上停相对突出' : data.sanTing.meta.userBalance === 'middle' ? '中停相对突出' : '下停相对突出'}
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
            <div style={{ padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>上停（额头）</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{data.sanTing.forehead}</div>
            </div>
            <div style={{ padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>中停（眉眼鼻）</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{data.sanTing.middle}</div>
            </div>
            <div style={{ padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>下停（口颌）</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{data.sanTing.lower}</div>
            </div>
          </div>
        </div>
      )}

      {data.analysis && (
        <div className="ai-report">
          <div className="ai-report-header">
            <span>👤 AI 解读（{viewMode === 'pro' ? '结构化' : '全文'}）</span>
          </div>
          <div className="ai-report-content">{renderAnalysisBody()}</div>
        </div>
      )}

      {prov?.disclaimer && (
        <div className="bazi-pro-card bazi-provenance-card" style={{ marginTop: 16 }}>
          <div className="bazi-pro-card-kicker">可信度说明</div>
          <p className="bazi-pro-card-body subtle" style={{ margin: 0 }}>{prov.disclaimer}</p>
          <div className="bazi-pro-meta">
            规则 {prov.ruleEngineVersion || '—'} · 八字联动：{prov.baziLinked ? '是' : '否'}
          </div>
        </div>
      )}

      {typeof onShare === 'function' && (
        <div className="share-buttons">
          <button className="btn-share" onClick={onShare}>
            <span>📋</span> 生成分享链接
          </button>
        </div>
      )}

      <div className="usage-info">剩余免费次数: {data.remaining > 0 ? data.remaining : 'VIP无限'}</div>
    </div>
  );
}

function MemeResult({ data, onShare }) {
  const { code, title, tagline, traits, mbtiHint, matchPct, hitCount, totalQ } = data;
  return (
    <div className="result-section">
      <div className="mbti-result-type">
        <h2 style={{ letterSpacing: '0.06em' }}>{code || '—'}</h2>
        <p>{title}</p>
        <p style={{ opacity: 0.85, marginTop: 8 }}>{tagline}</p>
      </div>
      <div className="result-card" style={{ marginTop: 16 }}>
        <div className="result-card-title">你的主梗型</div>
        <div className="result-card-content">
          <p style={{ marginBottom: 12 }}>
            匹配度 <strong>{matchPct ?? 0}%</strong> · 梗型命中 <strong>{hitCount ?? 0}</strong> / {totalQ ?? 12} 题
          </p>
          <p style={{ fontSize: 13, opacity: 0.8 }}>维度命中越高，当前结果越接近你的「第一梗型画像」（娱乐算法）。</p>
        </div>
      </div>
      <div className="result-card">
        <div className="result-card-title">标签</div>
        <div className="result-card-content">
          <div className="suggestions-row">
            {(traits || []).map((t, i) => (
              <span key={i} className="suggestion-tag">
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
      {mbtiHint ? (
        <div className="ai-report">
          <div className="ai-report-header">
            <span>🎭 玩梗对应参考</span>
          </div>
          <div className="ai-report-content">
            <p>{mbtiHint}</p>
            <p style={{ fontSize: 13, opacity: 0.75, marginTop: 8 }}>
              仅为娱乐对照，不代表你是某一 MBTI 类型。
            </p>
          </div>
        </div>
      ) : null}
      {typeof onShare === 'function' && (
        <div className="share-buttons">
          <button type="button" className="btn-share" onClick={onShare}>
            <span>📋</span> 复制分享文案
          </button>
        </div>
      )}
    </div>
  );
}

function MBTIResult({ data, onShare }) {
  const { mbtiType, scores, analysis, careers } = data;
  
  // 计算各维度百分比
  const E_pct = scores?.E ? Math.round((scores.E / 15) * 100) : 50;
  const I_pct = scores?.I ? Math.round((scores.I / 15) * 100) : 50;
  const S_pct = scores?.S ? Math.round((scores.S / 15) * 100) : 50;
  const N_pct = scores?.N ? Math.round((scores.N / 15) * 100) : 50;
  const T_pct = scores?.T ? Math.round((scores.T / 15) * 100) : 50;
  const F_pct = scores?.F ? Math.round((scores.F / 15) * 100) : 50;
  const J_pct = scores?.J ? Math.round((scores.J / 15) * 100) : 50;
  const P_pct = scores?.P ? Math.round((scores.P / 15) * 100) : 50;
  
  return (
    <div className="result-section">
      {/* 性格类型展示 */}
      <div className="mbti-result-type">
        <h2>{mbtiType || 'INTJ'}</h2>
        <p>您的命理性格类型</p>
      </div>
      
      {/* 四维得分 */}
      <div className="mbti-dimensions">
        <div className="mbti-dimension">
          <div className="mbti-dimension-title">能量来源 (E/I)</div>
          <div className="mbti-dimension-bar">
            <div className="mbti-dimension-fill-left" style={{width: `${E_pct}%`}} />
            <div className="mbti-dimension-fill-right" style={{width: `${I_pct}%`}} />
          </div>
          <div className="mbti-dimension-labels">
            <span>外向 E {E_pct}%</span>
            <span>内向 I {I_pct}%</span>
          </div>
        </div>
        
        <div className="mbti-dimension">
          <div className="mbti-dimension-title">认知方式 (S/N)</div>
          <div className="mbti-dimension-bar">
            <div className="mbti-dimension-fill-left" style={{width: `${S_pct}%`}} />
            <div className="mbti-dimension-fill-right" style={{width: `${N_pct}%`}} />
          </div>
          <div className="mbti-dimension-labels">
            <span>实感 S {S_pct}%</span>
            <span>直觉 N {N_pct}%</span>
          </div>
        </div>
        
        <div className="mbti-dimension">
          <div className="mbti-dimension-title">决策方式 (T/F)</div>
          <div className="mbti-dimension-bar">
            <div className="mbti-dimension-fill-left" style={{width: `${T_pct}%`}} />
            <div className="mbti-dimension-fill-right" style={{width: `${F_pct}%`}} />
          </div>
          <div className="mbti-dimension-labels">
            <span>思考 T {T_pct}%</span>
            <span>情感 F {F_pct}%</span>
          </div>
        </div>
        
        <div className="mbti-dimension">
          <div className="mbti-dimension-title">生活态度 (J/P)</div>
          <div className="mbti-dimension-bar">
            <div className="mbti-dimension-fill-left" style={{width: `${J_pct}%`}} />
            <div className="mbti-dimension-fill-right" style={{width: `${P_pct}%`}} />
          </div>
          <div className="mbti-dimension-labels">
            <span>判断 J {J_pct}%</span>
            <span>知觉 P {P_pct}%</span>
          </div>
        </div>
      </div>
      
      {/* AI分析 */}
      <div className="ai-report">
        <div className="ai-report-header"><span>🎯 性格深度分析</span></div>
        <div className="ai-report-content">
          {analysis?.split('\n').map((line, i) => line.trim() ? <p key={i}>{line}</p> : <br key={i} />) || (
            <>
              <p>基于您完成的60道专业测试题目分析，您的性格类型展现出独特的心理特征。</p>
              <p>在人际交往中，您倾向于{mbtiType?.[0] === 'E' ? '主动社交，从外部世界获取能量' : '独处充电，从内心世界汲取力量'}。</p>
              <p>认知方式上，您更习惯{mbtiType?.[1] === 'S' ? '关注具体细节和实际经验' : '探索抽象概念和潜在可能'}。</p>
              <p>决策时，您更看重{mbtiType?.[2] === 'T' ? '逻辑分析和客观标准' : '个人价值和他人感受'}。</p>
              <p>生活态度上，您倾向于{mbtiType?.[3] === 'J' ? '有计划、有条理地安排生活' : '保持灵活、随机应变'}。</p>
            </>
          )}
        </div>
      </div>
      
      {/* 适合职业 */}
      <div className="result-card">
        <div className="result-card-title">💼 适合职业方向</div>
        <div className="result-card-content">
          <div className="suggestions-row">
            {careers?.map((career, i) => <span key={i} className="suggestion-tag">{career}</span>) || (
              <>
                <span className="suggestion-tag">战略规划</span>
                <span className="suggestion-tag">数据分析</span>
                <span className="suggestion-tag">项目管理</span>
                <span className="suggestion-tag">咨询顾问</span>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* 发展建议 */}
      <div className="result-card">
        <div className="result-card-title">🌟 发展建议</div>
        <div className="result-card-content">
          <p>• 发挥您的{mbtiType?.[2] === 'T' ? '理性分析' : '共情理解'}优势，同时注意平衡{mbtiType?.[2] === 'T' ? '情感表达' : '逻辑思考'}</p>
          <p>• 在{mbtiType?.[3] === 'J' ? '执行计划' : '适应变化'}的同时，保持适当的灵活性</p>
          <p>• 寻找与您性格互补的伙伴，形成良性互补</p>
          <p>• 定期回顾和调整，持续优化个人发展路径</p>
        </div>
      </div>

      <div className="share-buttons" style={{ marginTop: 18 }}>
        <button className="btn-share" onClick={onShare}>
          <span>🔗</span> 生成分享链接
        </button>
      </div>
      
      <div className="usage-info">剩余免费次数: {data.remaining > 0 ? data.remaining : 'VIP无限'}</div>
    </div>
  );
}

// ==================== 弹窗组件 ====================

/** 解析支付宝 pageExec 返回的 HTML，取出 form 并直接 POST（避免 blob/iframe/异步 document.write 失效） */
function submitAlipayFormHtml(html) {
  if (!html || typeof html !== 'string') return false;
  try {
    const cleaned = html.replace(/<script\b[\s\S]*?<\/script>/gi, '');
    const tpl = document.createElement('template');
    tpl.innerHTML = cleaned.trim();
    const form = tpl.content.querySelector('form');
    if (!form) return false;
    form.target = '_self';
    if (!form.getAttribute('method')) form.setAttribute('method', 'post');
    form.style.display = 'none';
    document.body.appendChild(form);
    form.submit();
    return true;
  } catch (e) {
    console.error('[alipay] submitAlipayFormHtml', e);
    return false;
  }
}

function PaywallModal({ onClose, onSubscribe, subscribePayLoading = false }) {
  const [selectedTier, setSelectedTier] = useState('xiuxing');
  const [billing, setBilling] = useState('annual');
  const [paymentMethod, setPaymentMethod] = useState('wechat');

  // 三档会员配置
  const tiers = {
    wendao: {
      id: 'wendao',
      name: '🥉 问道者',
      color: '#cd7f32',
      monthlyPrice: 19,
      annualPrice: 188,
      features: ['每日运势无限', '周公解梦无限', '八字批解 1次/月', '灵兽传音 3次/日（每发一条算1次）', '赛博灵符无水印'],
      notIncluded: ['风水顾问', '面相探微', '宝宝取名', '良辰吉日']
    },
    xiuxing: {
      id: 'xiuxing',
      name: '🥈 修行者',
      color: '#c0c0c0',
      monthlyPrice: 48,
      annualPrice: 468,
      badge: '⭐主推',
      features: ['八字/风水/面相探微 各3次/月', '灵兽传音无限（随便唠）', '多种灵兽音色', '宝宝取名/吉日 各1次/月', '历史记录永久保存'],
      notIncluded: []
    },
    tianji: {
      id: 'tianji',
      name: '🥇 天机阁',
      color: '#ffd700',
      monthlyPrice: 128,
      annualPrice: 998,
      features: ['全部功能无限次', '暖愈心愈情绪陪伴', '气场扫描等全站深度体验', '优先体验新功能与活动', '会员专属标识'],
      notIncluded: []
    }
  };

  const currentTier = tiers[selectedTier];
  const planId = `${selectedTier}_${billing}`;

  const handlePayClick = async () => {
    if (paymentMethod === 'wechat') {
      // 与支付宝一致：始终走服务端 Native / JSAPI / H5 下单；勿因 capabilities 为 false 落到个人静态收款码
      onSubscribe({
        plan: planId,
        paymentMethod,
        tier: selectedTier,
        billing,
        useWechatGateway: true
      });
      return;
    }
    // 支付宝：始终先走官方下单跳转；capabilities 易误判（env/代理不一致），不再用它决定是否出收款码
    onSubscribe({
      plan: planId,
      paymentMethod,
      tier: selectedTier,
      billing,
      useAlipayGateway: true
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header"><span>⚠️ 免费体验次数已用完</span></div>
        <div className="modal-body">
            <>
              <p style={{ marginBottom: 16, fontSize: 14, color: 'var(--text-secondary)' }}>
                选择适合您的会员等级，解锁更多功能：
              </p>
              
              {/* 三档会员选择 */}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <div className="choice-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                  {Object.values(tiers).map((tier) => (
                    <button
                      key={tier.id}
                      className={`choice-item ${selectedTier === tier.id ? 'active' : ''}`}
                      onClick={() => setSelectedTier(tier.id)}
                      style={{ 
                        flexDirection: 'column',
                        padding: '12px 8px',
                        borderColor: selectedTier === tier.id ? tier.color : undefined,
                        background: selectedTier === tier.id ? `${tier.color}15` : undefined
                      }}
                    >
                      <span style={{ fontSize: 12, marginBottom: 4 }}>{tier.name}</span>
                      <strong style={{ fontSize: 16 }}>¥{billing === 'annual' ? tier.annualPrice : tier.monthlyPrice}</strong>
                      <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                        {billing === 'annual' ? '/年' : '/月'}
                      </span>
                      {tier.badge && <em style={{ fontSize: 10 }}>{tier.badge}</em>}
                    </button>
                  ))}
                </div>
              </div>

              {/* 月付/年付切换 */}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">计费周期</label>
                <div className="choice-grid choice-grid-2">
                  <button 
                    className={`choice-item ${billing === 'monthly' ? 'active' : ''}`} 
                    onClick={() => setBilling('monthly')}
                  >
                    <span>月付</span>
                    <strong>¥{currentTier.monthlyPrice}/月</strong>
                  </button>
                  <button 
                    className={`choice-item ${billing === 'annual' ? 'active' : ''}`} 
                    onClick={() => setBilling('annual')}
                  >
                    <span>年付</span>
                    <strong>¥{currentTier.annualPrice}/年</strong>
                    <em style={{ fontSize: 10 }}>省¥{currentTier.monthlyPrice * 12 - currentTier.annualPrice}</em>
                  </button>
                </div>
              </div>

              {/* 功能列表 */}
              <div className="vip-features" style={{ 
                marginBottom: 16, 
                padding: 12, 
                background: 'var(--bg-tertiary)', 
                borderRadius: 8,
                fontSize: 12
              }}>
                <p style={{ fontWeight: 'bold', marginBottom: 8, color: currentTier.color }}>
                  {currentTier.name} 包含功能：
                </p>
                {currentTier.features.map((f, i) => (
                  <span key={i} style={{ display: 'block', marginBottom: 4 }}>✅ {f}</span>
                ))}
                {currentTier.notIncluded.length > 0 && (
                  <>
                    <p style={{ fontWeight: 'bold', margin: '8px 0 4px', color: 'var(--text-tertiary)' }}>
                      不包含：
                    </p>
                    {currentTier.notIncluded.map((f, i) => (
                      <span key={i} style={{ display: 'block', marginBottom: 4, color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>
                        ❌ {f}
                      </span>
                    ))}
                  </>
                )}
              </div>

              <button
                className="btn-primary"
                onClick={handlePayClick}
                disabled={subscribePayLoading}
              >
                {subscribePayLoading ? '正在拉起支付…' : `立即开通 ${currentTier.name}`}
              </button>
              <button className="btn-secondary" onClick={onClose} style={{ width: '100%', marginTop: 12 }}>稍后再说</button>
            </>
        </div>
      </div>
    </div>
  );
}

function VipDetailModal({ onClose, onSubscribe, userInfo, subscribePayLoading = false }) {
  const [selectedTier, setSelectedTier] = useState('xiuxing');
  const [billing, setBilling] = useState('annual');
  const [paymentMethod, setPaymentMethod] = useState('wechat');

  // 三档会员配置
  const tiers = {
    wendao: {
      id: 'wendao',
      name: '🥉 问道者',
      color: '#cd7f32',
      monthlyPrice: 19,
      annualPrice: 188,
      features: ['每日运势无限', '周公解梦无限', '八字批解 1次/月', '灵兽传音 3次/日（每发一条算1次）', '赛博灵符无水印'],
      notIncluded: ['风水顾问', '面相探微', '宝宝取名', '良辰吉日']
    },
    xiuxing: {
      id: 'xiuxing',
      name: '🥈 修行者',
      color: '#c0c0c0',
      monthlyPrice: 48,
      annualPrice: 468,
      badge: '⭐主推',
      features: ['八字/风水/面相探微 各3次/月', '灵兽传音无限（随便唠）', '多种灵兽音色', '宝宝取名/吉日 各1次/月', '历史记录永久保存'],
      notIncluded: []
    },
    tianji: {
      id: 'tianji',
      name: '🥇 天机阁',
      color: '#ffd700',
      monthlyPrice: 128,
      annualPrice: 998,
      features: ['全部功能无限次', '暖愈心愈情绪陪伴', '气场扫描等全站深度体验', '优先体验新功能与活动', '会员专属标识'],
      notIncluded: []
    }
  };
  
  const currentTier = tiers[selectedTier];
  const planId = `${selectedTier}_${billing}`;

  const handlePayClick = async () => {
    if (paymentMethod === 'wechat') {
      onSubscribe({
        plan: planId,
        paymentMethod,
        tier: selectedTier,
        billing,
        useWechatGateway: true
      });
      return;
    }
    onSubscribe({
      plan: planId,
      paymentMethod,
      tier: selectedTier,
      billing,
      useAlipayGateway: true
    });
  };

  // 获取当前用户等级信息
  const currentLevel = userInfo?.memberLevel || 'normal';
  const levelNames = {
    normal: '普通用户',
    wendao: '🥉 问道者',
    xiuxing: '🥈 修行者',
    tianji: '🥇 天机阁'
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span>👑 会员权益</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
            <>
              {userInfo?.isVip && (
                <div style={{ textAlign: 'center', padding: '12px 0 20px' }}>
                  <div style={{ fontSize: 42, marginBottom: 12 }}>{currentLevel === 'tianji' ? '🥇' : currentLevel === 'xiuxing' ? '🥈' : '🥉'}</div>
                  <h3 style={{ marginBottom: 8 }}>您已是{levelNames[currentLevel]}</h3>
                  <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                    到期时间：{new Date(userInfo.subscription?.expiresAt).toLocaleDateString('zh-CN')}
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 6 }}>
                    可继续升级、续费或更换套餐
                  </p>
                </div>
              )}

              {/* 三档会员对比 */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 12 }}>选择会员等级：</p>
                <div className="choice-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                  {Object.values(tiers).map((tier) => {
                    // 已是VIP时，不允许选择低于当前等级的套餐（避免降级困惑）
                    const isDowngrade = userInfo?.isVip && (
                      (currentLevel === 'tianji' && tier.id !== 'tianji') ||
                      (currentLevel === 'xiuxing' && tier.id === 'wendao')
                    );
                    return (
                    <button
                      key={tier.id}
                      className={`choice-item ${selectedTier === tier.id ? 'active' : ''}`}
                      onClick={() => !isDowngrade && setSelectedTier(tier.id)}
                      disabled={isDowngrade}
                      style={{ 
                        flexDirection: 'column',
                        padding: '12px 8px',
                        borderColor: selectedTier === tier.id ? tier.color : undefined,
                        background: selectedTier === tier.id ? `${tier.color}15` : undefined,
                        opacity: isDowngrade ? 0.4 : 1,
                        cursor: isDowngrade ? 'not-allowed' : undefined,
                        position: 'relative'
                      }}
                    >
                      <span style={{ fontSize: 12, marginBottom: 4 }}>{tier.name}</span>
                      <strong style={{ fontSize: 14 }}>¥{billing === 'annual' ? tier.annualPrice : tier.monthlyPrice}</strong>
                      <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                        {billing === 'annual' ? '/年' : '/月'}
                      </span>
                      {tier.badge && <em style={{ fontSize: 10 }}>{tier.badge}</em>}
                      {isDowngrade && (
                        <span style={{ 
                          fontSize: 9, 
                          position: 'absolute', 
                          top: 4, 
                          right: 6, 
                          color: 'var(--text-tertiary)',
                          background: 'var(--bg-secondary)',
                          padding: '1px 4px',
                          borderRadius: 4
                        }}>当前</span>
                      )}
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* 月付/年付切换 */}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">计费周期</label>
                <div className="choice-grid choice-grid-2">
                  <button 
                    className={`choice-item ${billing === 'monthly' ? 'active' : ''}`} 
                    onClick={() => setBilling('monthly')}
                  >
                    <span>月付</span>
                    <strong>¥{currentTier.monthlyPrice}/月</strong>
                  </button>
                  <button 
                    className={`choice-item ${billing === 'annual' ? 'active' : ''}`} 
                    onClick={() => setBilling('annual')}
                  >
                    <span>年付</span>
                    <strong>¥{currentTier.annualPrice}/年</strong>
                    <em style={{ fontSize: 10 }}>省¥{currentTier.monthlyPrice * 12 - currentTier.annualPrice}</em>
                  </button>
                </div>
              </div>

              {/* 当前选中等级权益 */}
              <div className="subscription-features" style={{ 
                marginBottom: 20, 
                padding: 16, 
                background: 'var(--bg-tertiary)', 
                borderRadius: 12,
                fontSize: 13
              }}>
                <p style={{ fontWeight: 'bold', marginBottom: 12, color: currentTier.color, fontSize: 14 }}>
                  {currentTier.name} 权益：
                </p>
                {currentTier.features.map((f, i) => (
                  <span key={i} style={{ display: 'block', marginBottom: 6 }}>✅ {f}</span>
                ))}
                {currentTier.notIncluded.length > 0 && (
                  <>
                    <p style={{ fontWeight: 'bold', margin: '12px 0 8px', color: 'var(--text-tertiary)' }}>
                      不包含：
                    </p>
                    {currentTier.notIncluded.map((f, i) => (
                      <span key={i} style={{ display: 'block', marginBottom: 6, color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>
                        ❌ {f}
                      </span>
                    ))}
                  </>
                )}
              </div>
              
              <button
                className="btn-subscribe"
                onClick={handlePayClick}
                disabled={subscribePayLoading}
              >
                {subscribePayLoading
                  ? '正在拉起支付…'
                  : !userInfo?.isVip
                    ? `开通 ${currentTier.name}`
                    : (() => {
                        // 判断是升级、降级还是续费
                        const tierOrder = { wendao: 1, xiuxing: 2, tianji: 3 };
                        const currentOrder = tierOrder[currentLevel] || 0;
                        const selectedOrder = tierOrder[selectedTier] || 0;
                        if (selectedOrder > currentOrder) return `升级至 ${currentTier.name}`;
                        if (selectedOrder < currentOrder) return `切换至 ${currentTier.name}`;
                        return `续费 ${currentTier.name}`;
                      })()}
              </button>
            </>
        </div>
      </div>
    </div>
  );
}

function AdminModal({ onClose }) {
  const [code, setCode] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    const r = await requestApi('/admin', { method: 'POST', data: { code, action: 'stats' } });
    if (r.error) setError(r.error); 
    else setData(r);
  };

  return (
    <div className="modal-overlay">
      <div className="modal admin-modal">
        <div className="modal-header">
          <span>🔧 后台管理</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {!data ? (
            <>
              <p style={{ marginBottom: 12 }}>请输入管理员密码：</p>
              <input type="password" className="form-input" value={code} onChange={e => setCode(e.target.value)} placeholder="管理员密码" />
              <button className="btn-primary" onClick={handleLogin} style={{ marginTop: 16 }}>登录</button>
              {error && <p className="error-text">{error}</p>}
            </>
          ) : (
            <>
              <h3 style={{ marginBottom: 16 }}>📊 数据统计</h3>
              <div className="stats-grid">
                <div className="stat-card"><span className="stat-num">{data.totalUsers || 0}</span><span>总用户</span></div>
                <div className="stat-card"><span className="stat-num">{data.vipCount || 0}</span><span>VIP会员</span></div>
                <div className="stat-card"><span className="stat-num">{data.totalCalls || 0}</span><span>总调用</span></div>
                <div className="stat-card"><span className="stat-num">¥{data.platformRevenue || data.totalRevenue || 0}</span><span>平台总收入(历史)</span></div>
                <div className="stat-card"><span className="stat-num">¥{data.paymentRevenue || 0}</span><span>支付流水收入</span></div>
              </div>
              
              <h4 style={{ marginTop: 24, marginBottom: 12 }}>最近用户</h4>
              <div className="user-list">
                {data.recentUsers?.map((u, i) => (
                  <div key={i} className="user-item">
                    <span>{u.id?.substring(0, 20)}...</span>
                    <span className={u.isVip ? 'vip-badge' : 'free-badge'}>{u.isVip ? 'VIP' : '免费'}</span>
                  </div>
                )) || <p style={{ color: 'var(--text-tertiary)' }}>暂无用户数据</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== 辅助函数 ====================

// ==================== MBTI 60道专业题目 ====================
const MBTI_QUESTIONS = [
  // E/I 维度 (1-15题)
  { question: "在参加聚会或社交活动后，您通常感到：", optionA: "充满活力，还想继续社交", optionB: "有些疲惫，需要独处恢复", type: "EI" },
  { question: "您更喜欢哪种工作环境？", optionA: "开放协作的团队空间", optionB: "安静独立的个人空间", type: "EI" },
  { question: "面对陌生人群，您通常会：", optionA: "主动打招呼，结识新朋友", optionB: "等待别人先来找我说话", type: "EI" },
  { question: "周末您更倾向于：", optionA: "和朋友外出聚会活动", optionB: "在家看书、追剧或独处", type: "EI" },
  { question: "在社交场合中，您通常是：", optionA: "话题的发起者和活跃者", optionB: "安静的倾听者和观察者", type: "EI" },
  { question: "思考问题的方式，您更习惯：", optionA: "边说边想，通过交流理清思路", optionB: "先想清楚，再开口表达", type: "EI" },
  { question: "您更喜欢通过电话还是文字沟通？", optionA: "电话或视频，直接交流", optionB: "文字消息，慢慢组织语言", type: "EI" },
  { question: "独处一整天后，您会感到：", optionA: "寂寞无聊，渴望见人", optionB: "舒适放松，享受宁静", type: "EI" },
  { question: "在团队讨论中，您通常：", optionA: "积极发言，分享想法", optionB: "先听别人说，再选择性发言", type: "EI" },
  { question: "您更享受哪种状态？", optionA: "被朋友围绕的热闹时光", optionB: "一个人独处的安静时光", type: "EI" },
  { question: "遇到问题时，您更可能：", optionA: "找朋友聊聊，寻求建议", optionB: "自己思考，独立解决", type: "EI" },
  { question: "您更喜欢哪种类型的活动？", optionA: "群体活动，如派对、聚餐", optionB: "个人活动，如阅读、运动", type: "EI" },
  { question: "在人群中，您通常感觉：", optionA: "自在舒适，如鱼得水", optionB: "有些紧张，需要适应", type: "EI" },
  { question: "您表达情感的方式更倾向于：", optionA: "外向表达，直接说出来", optionB: "内敛含蓄，用行动表示", type: "EI" },
  { question: "对您的能量来源描述更准确的是：", optionA: "与他人的互动和交流", optionB: "独处时的思考和反思", type: "EI" },

  // S/N 维度 (16-30题)
  { question: "您更关注事物的：", optionA: "具体细节和实际应用", optionB: "整体意义和未来可能", type: "SN" },
  { question: "学习新技能时，您更喜欢：", optionA: "按步骤实操，边做边学", optionB: "先理解原理，建立概念框架", type: "SN" },
  { question: "您更信任：", optionA: "过去的经验和已知事实", optionB: "直觉和第六感", type: "SN" },
  { question: "描述一件事情时，您会强调：", optionA: "具体的时间、地点、人物", optionB: "背后的含义和启示", type: "SN" },
  { question: "您更欣赏哪种人？", optionA: "脚踏实地、注重实际的", optionB: "富有想象力、有远见的", type: "SN" },
  { question: "做决策时，您更依赖：", optionA: "具体数据和现实依据", optionB: "直觉感受和潜在可能", type: "SN" },
  { question: "您更擅长：", optionA: "记住具体的事实和细节", optionB: "理解抽象的概念和模式", type: "SN" },
  { question: "阅读时，您更关注：", optionA: "字面意思和具体内容", optionB: "隐喻深意和作者意图", type: "SN" },
  { question: "您更喜欢的工作是：", optionA: "有明确流程和标准的", optionB: "需要创新想象的", type: "SN" },
  { question: "回忆往事时，您通常记得：", optionA: "当时具体发生了什么", optionB: "当时的感受和意义", type: "SN" },
  { question: "您更偏好哪种类型的书籍？", optionA: "纪实类、传记、说明书", optionB: "科幻、哲学、心理学", type: "SN" },
  { question: "解决问题时，您会：", optionA: "参考过往成功案例", optionB: "尝试全新的方法", type: "SN" },
  { question: "您更容易注意到：", optionA: "环境中的细节变化", optionB: "事物间的潜在联系", type: "SN" },
  { question: "您更喜欢聊什么话题？", optionA: "具体的生活琐事和经历", optionB: "抽象的理论和未来设想", type: "SN" },
  { question: "您看待生活的态度更接近：", optionA: "活在当下，享受眼前", optionB: "着眼未来，追求意义", type: "SN" },

  // T/F 维度 (31-45题)
  { question: "做重要决定时，您更看重：", optionA: "逻辑分析和客观标准", optionB: "个人价值和对他人的影响", type: "TF" },
  { question: "面对批评，您更在意：", optionA: "批评是否有道理和依据", optionB: "批评的方式和语气", type: "TF" },
  { question: "您认为什么更重要？", optionA: "事情的公正和合理", optionB: "人际关系的和谐", type: "TF" },
  { question: "安慰朋友时，您会：", optionA: "提供解决问题的建议", optionB: "给予情感支持和理解", type: "TF" },
  { question: "您更倾向于：", optionA: "理性分析，不轻易流露情感", optionB: "感同身受，容易被情绪影响", type: "TF" },
  { question: "评价一个人时，您更看重：", optionA: "能力和成就", optionB: "品德和为人", type: "TF" },
  { question: "在争论中，您更关心：", optionA: "谁的观点更符合逻辑", optionB: "如何不伤感情地达成共识", type: "TF" },
  { question: "您认为好人应该：", optionA: "坚持原则和正义", optionB: "体恤他人、富有同情心", type: "TF" },
  { question: "看电影时，您更容易被什么打动？", optionA: "精巧的剧情设计和逻辑", optionB: "角色的情感变化", type: "TF" },
  { question: "您更认同哪种说法？", optionA: "感情用事会影响判断", optionB: "过于理性会缺少人情味", type: "TF" },
  { question: "指出别人的错误时，您通常会：", optionA: "直接指出，帮助对方改进", optionB: "委婉表达，照顾对方感受", type: "TF" },
  { question: "您更重视：", optionA: "事物的正确与否", optionB: "做事情时的感受", type: "TF" },
  { question: "面对冲突，您倾向于：", optionA: "理性分析对错", optionB: "考虑各方感受", type: "TF" },
  { question: "您更容易因为什么而感动？", optionA: "伟大的成就和壮举", optionB: "温暖的人情和善意", type: "TF" },
  { question: "您认为好的领导应该：", optionA: "公正严明、赏罚分明", optionB: "关心下属、体贴入微", type: "TF" },

  // J/P 维度 (46-60题)
  { question: "您的生活状态更接近：", optionA: "有规律、按计划行事", optionB: "随性而为、灵活应变", type: "JP" },
  { question: "面对截止日期，您通常：", optionA: "提前完成，避免拖延", optionB: "在最后期限前冲刺完成", type: "JP" },
  { question: "您更喜欢：", optionA: "确定好的计划和安排", optionB: "保持开放的选择权", type: "JP" },
  { question: "整理物品时，您倾向于：", optionA: "分门别类，井然有序", optionB: "随手放置，用时再找", type: "JP" },
  { question: "旅行前，您会：", optionA: "详细规划行程和路线", optionB: "到了再说，边走边看", type: "JP" },
  { question: "面对突发变化，您感到：", optionA: "焦虑不安，喜欢稳定", optionB: "兴奋期待，喜欢新鲜感", type: "JP" },
  { question: "您的工作桌通常是：", optionA: "整洁有序，物品归位", optionB: "错落有致，随手可取", type: "JP" },
  { question: "做决定后，您会：", optionA: "不再纠结，坚定执行", optionB: "反复思考，留有退路", type: "JP" },
  { question: "您更享受：", optionA: "完成任务后的成就感", optionB: "探索过程中的乐趣", type: "JP" },
  { question: "对待规则，您倾向于：", optionA: "严格遵守，按部就班", optionB: "灵活处理，特事特办", type: "JP" },
  { question: "您的待办事项通常是：", optionA: "列出清单，逐一完成", optionB: "记在脑中，灵活处理", type: "JP" },
  { question: "新项目开始时，您会先：", optionA: "制定详细的计划和时间表", optionB: "先做起来，边做边调整", type: "JP" },
  { question: "您更喜欢哪种假期？", optionA: "有计划的充实之旅", optionB: "随性而为的自由行", type: "JP" },
  { question: "面对多个选择时，您会：", optionA: "尽快做出决定", optionB: "多留些时间考虑", type: "JP" },
  { question: "您的人生信条更接近：", optionA: "凡事预则立，不预则废", optionB: "船到桥头自然直", type: "JP" },
];

// —— 趣味梗型（娱乐向，纯前端计分，不调 API）——
const MEME_ARCHETYPE_KEYS = ['ctrl', 'give', 'spark', 'deep', 'chill', 'real', 'soft', 'luck'];
const MEME_ARCHETYPES = {
  ctrl: {
    code: 'CTRL',
    title: '拿捏者',
    tagline: '控场感在线，节奏跟你走',
    traits: ['大局观', '底线清晰', '讨厌失控'],
    mbtiHint: '常见对应：NTJ、强势外向型',
  },
  give: {
    code: 'GIVE',
    title: '暖心付出型',
    tagline: '先掏心掏肺，再谈回报',
    traits: ['抢着买单', '共情拉满', '容易心软'],
    mbtiHint: '常见对应：SFJ、高共情型',
  },
  spark: {
    code: 'SPARK',
    title: '气氛担当',
    tagline: '冷场？不存在的',
    traits: ['社交充电', '梗多话密', '情绪感染力'],
    mbtiHint: '常见对应：EFP、外向感性型',
  },
  deep: {
    code: 'DEEP',
    title: '深夜哲学家',
    tagline: '想太多不是你的错',
    traits: ['复盘狂魔', '内耗预备', '爱问意义'],
    mbtiHint: '常见对应：INP、内向直觉型',
  },
  chill: {
    code: 'CHILL',
    title: '随缘躺平派',
    tagline: '问题不大，先歇会儿',
    traits: ['低控制欲', '反卷', '舒适区钉子户'],
    mbtiHint: '常见对应：ISP、感知型',
  },
  real: {
    code: 'REAL',
    title: '人间清醒',
    tagline: '实话难听但我真敢说',
    traits: ['就事论事', '直球预警', '讨厌假嗨'],
    mbtiHint: '常见对应：ST、理性直球型',
  },
  soft: {
    code: 'SOFT',
    title: '温柔乡居民',
    tagline: '硬刚不如抱抱',
    traits: ['以和为贵', '照顾情绪', '回避硬刚'],
    mbtiHint: '常见对应：IFJ、高宜人性',
  },
  luck: {
    code: 'LUCK',
    title: '盲目乐观派',
    tagline: '船到桥头自然直（大概）',
    traits: ['先乐再说', '风险盲区', '正能量溢出'],
    mbtiHint: '常见对应：ENP、乐天行动派',
  },
};

const MEME_QUESTIONS = [
  { question: '聚会散场后你更想？', optionA: '再约一局，我来带节奏', optionB: '回家躺平充电', weightA: 'ctrl', weightB: 'chill' },
  { question: '朋友临时爽约，你更可能？', optionA: '算了下次我请', optionB: '直接问清楚原因', weightA: 'give', weightB: 'real' },
  { question: '当众被怼，你更可能？', optionA: '当场把场子热回来', optionB: '夜里反复复盘', weightA: 'spark', weightB: 'deep' },
  { question: '小组撕起来了，你更想？', optionA: '先劝和降温', optionB: '先把道理讲明白', weightA: 'soft', weightB: 'real' },
  { question: '大考前夜你更可能？', optionA: '转发锦鲤再说', optionB: '焦虑已经三天了', weightA: 'luck', weightB: 'deep' },
  { question: '项目要组局推进，你更想？', optionA: '我来分工盯进度', optionB: '我先请客攒人品', weightA: 'ctrl', weightB: 'give' },
  { question: '周末你更想？', optionA: '必须出门浪一下', optionB: '被窝才是故乡', weightA: 'spark', weightB: 'chill' },
  { question: '同事甩锅式加班，你更可能？', optionA: '顺手帮一把', optionB: '到点我就溜', weightA: 'give', weightB: 'chill' },
  { question: '失恋后你更可能？', optionA: '下一个更乖', optionB: '小作文已在草稿箱', weightA: 'luck', weightB: 'deep' },
  { question: '朋友穿搭翻车问你意见？', optionA: '委婉夸细节', optionB: '直说哪里不对', weightA: 'soft', weightB: 'real' },
  { question: '饭局突然冷场，你更可能？', optionA: '我讲个段子救场', optionB: '我来定话题拉回主线', weightA: 'spark', weightB: 'ctrl' },
  { question: '刷到「稳赚」投资机会，你更可能？', optionA: '先冲为敬', optionB: '先看数据和风险', weightA: 'luck', weightB: 'real' },
];

function computeMemeResult(answers) {
  const scores = {};
  MEME_ARCHETYPE_KEYS.forEach((k) => {
    scores[k] = 0;
  });
  for (let i = 1; i <= MEME_QUESTIONS.length; i += 1) {
    const ans = answers[i];
    const q = MEME_QUESTIONS[i - 1];
    if (!q || !ans) continue;
    const w = ans === 'A' ? q.weightA : q.weightB;
    if (scores[w] !== undefined) scores[w] += 1;
  }
  let best = MEME_ARCHETYPE_KEYS[0];
  for (let j = 1; j < MEME_ARCHETYPE_KEYS.length; j += 1) {
    const k = MEME_ARCHETYPE_KEYS[j];
    if (scores[k] > scores[best]) best = k;
  }
  const arch = MEME_ARCHETYPES[best];
  const hitCount = scores[best];
  const totalQ = MEME_QUESTIONS.length;
  const matchPct = Math.max(8, Math.min(98, Math.round((hitCount / totalQ) * 100)));
  return {
    ...arch,
    scores,
    hitCount,
    totalQ,
    matchPct,
  };
}

function getShiChen(hour) {
  const shiChen = ['子', '丑', '丑', '寅', '寅', '卯', '卯', '辰', '辰', '巳', '巳', '午', 
                   '午', '未', '未', '申', '申', '酉', '酉', '戌', '戌', '亥', '亥', '子'];
  return shiChen[hour] + '时';
}

function getTypeName(type) {
  const names = {
    bazi: '八字批解',
    chat: '暖愈心愈',
    fengshui: '风水顾问',
    aura: '气场扫描',
    daily: '每日运势',
    dream: '周公解梦',
    name: '宝宝取名',
    date: '良辰吉日',
    face: '面相探微',
    mbti: '性格测试',
    spirit: '灵兽传音',
    talisman: '赛博灵符',
    yunxiang: '云香祈福',
    matchmaking: '缘分合盘'
  };
  return names[type] || type;
}

export default App;
